import {
  AlertTriangle,
  Archive,
  ArrowLeft,
  Ban,
  Check,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  Import,
  FileText,
  Globe,
  Info,
  Layers,
  Link2,
  Loader2,
  MessageSquare,
  Pencil,
  PlayCircle,
  Plus,
  Save,
  Search,
  Send,
  ShieldCheck,
  Trash2,
  Upload,
  X,
  XCircle
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import {
  abortImportSession,
  type AvailableControl,
  type AvailableParameter,
  type CatalogResponse,
  commitImportSession,
  type ControlResponse,
  createProfile,
  deleteProfile,
  exportCatalogDoc,
  getCatalog,
  getControl,
  getExportDownloadUrl,
  getExportJob,
  getImportSession,
  getProfile,
  getProfileAvailableControls,
  getProfileAvailableParameters,
  getProfileMergeAnalysis,
  getResolvedCatalog,
  type GroupResponse,
  importCatalog,
  type ImportJobResponse,
  type ImportSessionResponse,
  listCatalogs,
  listControls,
  listProfiles,
  listResolvedProfiles,
  type MergeAnalysisResponse,
  type ParameterConstraint,
  type ParameterSelection,
  type ParamResponse,
  type ProfileControlDetail,
  type ProfileDetailResponse,
  type ProfileResponse,
  renameDocument,
  type ResolvedControl,
  resolveProfile,
  transitionDocument,
  updateProfile,
  validateImport,
  type ValidateResponse,
} from "../../api/frameworksApi";
import { getAPI, downloadFile } from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import { formatVersion } from "../../utils/formatters";
import { ControlNode, ControlTreeView } from "../components/ControlTreeView";
import { Column, DataTable } from "../components/DataTable";
import {
  EntityCardGrid,
  StatTile,
  StatTiles,
  MetaDates,
  VersionChip,
  CardActions,
  TitleTooltip,
} from "../components/EntityCardGrid";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { Dropdown } from "../components/Dropdown";
import {
  FormField,
  FormInput,
  FormSelect,
  Modal,
  ModalFooterButtons
} from "../components/Modal";
import { ResizablePanels } from "../components/ResizablePanels";
import {
  SkeletonCard,
  SkeletonList
} from "../components/Skeleton";
import { StatusBadge } from "../components/StatusBadge";
import { useToastStore } from "../../stores/toastStore";
import { Switch } from "../components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Tip,
} from "../components/ui/tooltip";
import { WorkflowStepper } from "../components/WorkflowStepper";
import { RBACButton, RBACAction } from "../components/RBACButton";
import { useRBACPermission } from "../../hooks/useRBACPermission";

interface ProfileRow {
  [key: string]: unknown;
  id: string;
  documentId: string;
  name: string;
  title: string;
  oscalVersion: string;
  importCount: number;
  controlCount: number;
  groupCount: number;
  status: "active" | "draft" | "deprecated";
  lifecycleStatus: string;
  resolved: boolean;
  version: number;
  updated: string;
  created: string;
}

function mapLifecycleStatus(s: string): "active" | "draft" | "deprecated" {
  switch (s.toLowerCase()) {
    case "active":
      return "active";
    case "deprecated":
      return "deprecated";
    default:
      return "draft";
  }
}

function isEditableLifecycleStatus(status?: string): boolean {
  const normalized = status?.toLowerCase();
  return normalized === "draft" || normalized === "rejected";
}

/** Supplement missing `clazz` on AvailableParameter entries using rawControls already in memory */
function supplementParamClazz(
  params: AvailableParameter[],
  rows: { rawControls: ControlResponse[] }[],
): AvailableParameter[] {
  const clazzMap = new Map<string, string>();
  for (const row of rows) {
    for (const ctrl of row.rawControls) {
      for (const p of ctrl.params ?? []) {
        if (p.clazz && !clazzMap.has(p.paramId.toLowerCase())) {
          clazzMap.set(p.paramId.toLowerCase(), p.clazz);
        }
      }
    }
  }
  return params.map((p) =>
    p.clazz ? p : { ...p, clazz: clazzMap.get(p.paramId.toLowerCase()) ?? null },
  );
}

/**
 * Render prose, badging ONLY the real parameter positions taken from the
 * template `prose` (the `{{ insert: param, ID }}` tokens) — never by scanning
 * the resolved text for value strings, which can match coincidental numbers.
 *  - grey badge  → param with no value set (unfilled placeholder)
 *  - green pill  → param with a value set (overridden / resolved)
 */
function renderProseWithParamHighlights(
  templateProse: string,
  resolvedProse: string,
  params?: { param_id: string; label?: string; values?: string[] }[],
): React.ReactNode {
  const tokenRe =
    /\{\{\s*insert:\s*param\s*,\s*(?:id-ref\s*=\s*")?([\w.-]+)"?\s*\}\}/gi;

  // No template tokens to anchor on → just show the resolved text plainly.
  if (!templateProse || !tokenRe.test(templateProse)) {
    return resolvedProse || templateProse || null;
  }
  tokenRe.lastIndex = 0;

  const paramMap = new Map(
    (params ?? []).map((p) => [p.param_id.toLowerCase(), p]),
  );

  const GREY =
    "inline-flex items-center px-1.5 py-0.5 mx-0.5 rounded text-xs font-medium bg-slate-200/60 text-slate-600 dark:bg-white/[0.08] dark:text-slate-300";
  const GREEN =
    "inline-flex items-center px-2 py-0.5 mx-0.5 rounded-full text-xs font-semibold text-emerald-700 dark:text-emerald-300 bg-gradient-to-r from-emerald-500/15 to-emerald-400/10 dark:from-emerald-500/25 dark:to-emerald-400/15 ring-1 ring-emerald-500/30 dark:ring-emerald-400/30";

  const nodes: React.ReactNode[] = [];
  let cursor = 0;
  let i = 0;
  let m: RegExpExecArray | null;
  while ((m = tokenRe.exec(templateProse)) !== null) {
    if (m.index > cursor) nodes.push(templateProse.slice(cursor, m.index));
    const param = paramMap.get(m[1].toLowerCase());
    const value = (param?.values ?? [])
      .filter((v) => v && v.trim().length > 0)
      .join(", ");
    if (value) {
      nodes.push(
        <span key={`o-${i}`} className={GREEN}>
          {value}
        </span>,
      );
    } else {
      nodes.push(
        <span key={`p-${i}`} className={GREY}>
          {param?.label ? `[${param.label}]` : `[${m[1]}]`}
        </span>,
      );
    }
    cursor = m.index + m[0].length;
    i++;
  }
  if (cursor < templateProse.length) nodes.push(templateProse.slice(cursor));

  return nodes;
}

function extractParamIdsFromInlineProse(prose: string): string[] {
  const ids: string[] = [];
  const patterns = [
    /\{\{\s*insert:\s*param\s*,\s*([\w.-]+)\s*\}\}/gi,
    /\{\{\s*insert:\s*param\s*,\s*id-ref\s*=\s*"([^"]+)"\s*\}\}/gi,
  ];

  for (const pattern of patterns) {
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(prose)) !== null) {
      if (!ids.includes(match[1])) ids.push(match[1]);
    }
  }

  return ids;
}

/* ─────────────────────────────────────────────────────────
   Resolved-catalog method rendering
   The resolved-catalog API returns a structuring `method`:
     • as-is  → controls shown grouped by their group
     • flat   → flat list of controls, no grouping
     • custom → grouped like as-is, ordered ascending/descending
                per the user's selection
───────────────────────────────────────────────────────── */

const RESOLVED_METHOD_META: Record<string, { label: string; hint: string }> = {
  "as-is": { label: "As-is", hint: "Controls grouped by their group" },
  flat: { label: "Flat", hint: "Controls only — no grouping" },
  custom: { label: "Custom", hint: "Grouped, ordered" },
};

function ResolvedMethodBadge({
  method,
  order,
}: {
  method?: string | null;
  order?: string | null;
}) {
  if (!method) return null;
  const m = method.toLowerCase();
  const meta = RESOLVED_METHOD_META[m] ?? { label: method, hint: "" };
  const orderLabel =
    m === "custom" && (order === "ascending" || order === "descending")
      ? ` · ${order}`
      : "";
  return (
    <Tip label={meta.hint}>
      <span className="inline-flex items-center text-[11px] font-semibold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300 border border-indigo-200/60 dark:border-indigo-500/20">
        {meta.label}
        {orderLabel}
      </span>
    </Tip>
  );
}

function ResolvedControlRow({ ctrl }: { ctrl: ResolvedControl }) {
  const statementPart = (ctrl.parts ?? []).find((p) => p.name === "statement");
  return (
    <div className="px-4 py-3 space-y-1">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] font-bold px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 shrink-0">
          {ctrl.control_id.toUpperCase()}
        </span>
        <span className="text-sm font-medium">{ctrl.title}</span>
        {ctrl.params && ctrl.params.length > 0 && (
          <span className="ml-auto shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
            {ctrl.params.length} params
          </span>
        )}
      </div>
      {statementPart?.resolved_prose && (
        <p className="text-xs text-muted-foreground leading-relaxed pl-1">
          {renderProseWithParamHighlights(
            statementPart.prose,
            statementPart.resolved_prose,
            ctrl.params,
          )}
        </p>
      )}
    </div>
  );
}

// Group ids (in first-seen order) for the grouped resolved view; empty when flat.
function getResolvedGroupIds(
  controls: ResolvedControl[],
  method?: string | null,
): string[] {
  const m = (method ?? "").toLowerCase();
  const isFlat =
    m === "flat" ||
    (m !== "as-is" && m !== "custom" && controls.every((c) => !c.group_id));
  if (isFlat) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const c of controls) {
    const key = c.group_id ?? "__ungrouped__";
    if (!seen.has(key)) {
      seen.add(key);
      ids.push(key);
    }
  }
  return ids;
}

// Filter resolved controls by a free-text query (id, title or statement prose)
// and, optionally, to only controls that carry parameters.
function filterResolvedControls(
  controls: ResolvedControl[],
  query: string,
  paramsOnly = false,
): ResolvedControl[] {
  const q = query.trim().toLowerCase();
  return controls.filter((c) => {
    if (paramsOnly && !(c.params && c.params.length > 0)) return false;
    if (!q) return true;
    if (c.control_id.toLowerCase().includes(q)) return true;
    if ((c.title ?? "").toLowerCase().includes(q)) return true;
    const statement = (c.parts ?? []).find((p) => p.name === "statement");
    const prose = statement?.resolved_prose ?? statement?.prose ?? "";
    return prose.toLowerCase().includes(q);
  });
}

function ResolvedGroupToolbar({
  groupIds,
  collapsedGroups,
  onExpandAll,
  onCollapseAll,
}: {
  groupIds: string[];
  collapsedGroups: Set<string>;
  onExpandAll: () => void;
  onCollapseAll: (groupIds: string[]) => void;
}) {
  if (groupIds.length <= 1) return null;
  const allExpanded = groupIds.every((id) => !collapsedGroups.has(id));
  const allCollapsed = groupIds.every((id) => collapsedGroups.has(id));
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onExpandAll}
        disabled={allExpanded}
        className={`flex items-center gap-1 px-2 py-0.5 rounded text-[12px] font-medium text-foreground transition-all ${
          allExpanded
            ? "opacity-30 cursor-not-allowed pointer-events-none"
            : "hover:bg-accent"
        }`}
      >
        <ChevronDown className="size-3" /> Expand All
      </button>
      <button
        type="button"
        onClick={() => onCollapseAll(groupIds)}
        disabled={allCollapsed}
        className={`flex items-center gap-1 px-2 py-0.5 rounded text-[12px] font-medium text-foreground transition-all ${
          allCollapsed
            ? "opacity-30 cursor-not-allowed pointer-events-none"
            : "hover:bg-accent"
        }`}
      >
        <ChevronRight className="size-3" /> Collapse All
      </button>
    </div>
  );
}

function ResolvedCatalogControls({
  controls,
  method,
  collapsedGroups,
  onToggleGroup,
}: {
  controls: ResolvedControl[];
  method?: string | null;
  collapsedGroups: Set<string>;
  onToggleGroup: (groupId: string) => void;
}) {
  const m = (method ?? "").toLowerCase();
  // Flat when method says so, or — when method is unknown — when controls carry
  // no group at all. The resolved-catalog API already returns controls in their
  // resolved order (including any custom asc/desc sort), so we preserve that
  // order as-is and only use `method` to decide grouped vs flat.
  const isFlat =
    m === "flat" ||
    (m !== "as-is" && m !== "custom" && controls.every((c) => !c.group_id));

  if (isFlat) {
    return (
      <div className="rounded-lg border border-border overflow-hidden divide-y divide-border max-h-[600px] overflow-y-auto">
        {controls.map((ctrl) => (
          <ResolvedControlRow key={ctrl.control_id} ctrl={ctrl} />
        ))}
      </div>
    );
  }

  const groupMap = new Map<
    string,
    { groupId: string; groupTitle: string; controls: ResolvedControl[] }
  >();
  for (const ctrl of controls) {
    const key = ctrl.group_id ?? "__ungrouped__";
    const title =
      ctrl.group_title ?? (ctrl.group_id ? ctrl.group_id.toUpperCase() : "Ungrouped");
    if (!groupMap.has(key))
      groupMap.set(key, { groupId: key, groupTitle: title, controls: [] });
    groupMap.get(key)!.controls.push(ctrl);
  }
  const groups = Array.from(groupMap.values());

  return (
    <div className="space-y-2 max-h-[600px] overflow-y-auto">
      {groups.map((g) => {
        const isOpen = !collapsedGroups.has(g.groupId);
        const paramCount = g.controls.filter(
          (c) => c.params && c.params.length > 0,
        ).length;
        return (
          <div
            key={g.groupId}
            className="rounded-lg border border-border overflow-hidden"
          >
            <button
              onClick={() => onToggleGroup(g.groupId)}
              className="w-full flex items-center gap-3 px-4 py-3 bg-muted/40 hover:bg-muted/60 transition-colors text-left"
            >
              {isOpen ? (
                <ChevronDown className="size-4 text-muted-foreground shrink-0" />
              ) : (
                <ChevronRight className="size-4 text-muted-foreground shrink-0" />
              )}
              <span className="font-mono text-xs font-bold px-2 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 shrink-0">
                {g.groupId === "__ungrouped__" ? "—" : g.groupId.toUpperCase()}
              </span>
              <span className="text-sm font-semibold truncate">
                {g.groupTitle}
              </span>
              <div className="ml-auto flex items-center gap-2 shrink-0">
                {paramCount > 0 && (
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
                    {paramCount} params
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  {g.controls.length} controls
                </span>
              </div>
            </button>
            {isOpen && (
              <div className="divide-y divide-border">
                {g.controls.map((ctrl) => (
                  <ResolvedControlRow key={ctrl.control_id} ctrl={ctrl} />
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ── Overview stat card (matches the Catalog detail header) ── */
const profStatColors: Record<
  string,
  { icon: string; iconBg: string; bg: string; ring: string; border: string }
> = {
  indigo: {
    icon: "text-indigo-500 dark:text-indigo-400",
    iconBg: "bg-indigo-100/80 dark:bg-indigo-500/15",
    bg: "bg-indigo-50/40 dark:bg-indigo-500/[0.06]",
    ring: "ring-indigo-200/60 dark:ring-indigo-500/20",
    border: "border-l-indigo-400 dark:border-l-indigo-500",
  },
  emerald: {
    icon: "text-emerald-500 dark:text-emerald-400",
    iconBg: "bg-emerald-100/80 dark:bg-emerald-500/15",
    bg: "bg-emerald-50/40 dark:bg-emerald-500/[0.06]",
    ring: "ring-emerald-200/60 dark:ring-emerald-500/20",
    border: "border-l-emerald-400 dark:border-l-emerald-500",
  },
  sky: {
    icon: "text-sky-500 dark:text-sky-400",
    iconBg: "bg-sky-100/80 dark:bg-sky-500/15",
    bg: "bg-sky-50/40 dark:bg-sky-500/[0.06]",
    ring: "ring-sky-200/60 dark:ring-sky-500/20",
    border: "border-l-sky-400 dark:border-l-sky-500",
  },
  violet: {
    icon: "text-violet-500 dark:text-violet-400",
    iconBg: "bg-violet-100/80 dark:bg-violet-500/15",
    bg: "bg-violet-50/40 dark:bg-violet-500/[0.06]",
    ring: "ring-violet-200/60 dark:ring-violet-500/20",
    border: "border-l-violet-400 dark:border-l-violet-500",
  },
  amber: {
    icon: "text-amber-500 dark:text-amber-400",
    iconBg: "bg-amber-100/80 dark:bg-amber-500/15",
    bg: "bg-amber-50/40 dark:bg-amber-500/[0.06]",
    ring: "ring-amber-200/60 dark:ring-amber-500/20",
    border: "border-l-amber-400 dark:border-l-amber-500",
  },
  rose: {
    icon: "text-rose-500 dark:text-rose-400",
    iconBg: "bg-rose-100/80 dark:bg-rose-500/15",
    bg: "bg-rose-50/40 dark:bg-rose-500/[0.06]",
    ring: "ring-rose-200/60 dark:ring-rose-500/20",
    border: "border-l-rose-400 dark:border-l-rose-500",
  },
  slate: {
    icon: "text-slate-400 dark:text-white/40",
    iconBg: "bg-slate-100/80 dark:bg-white/[0.06]",
    bg: "bg-slate-50/60 dark:bg-white/[0.03]",
    ring: "ring-slate-200/50 dark:ring-white/[0.08]",
    border: "border-l-slate-300 dark:border-l-white/20",
  },
};

function ProfStatCard({
  icon,
  label,
  value,
  color = "slate",
  valueClassName = "",
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color?: string;
  valueClassName?: string;
}) {
  const c = profStatColors[color] ?? profStatColors.slate;
  return (
    <div
      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg ${c.bg} ring-1 ${c.ring} border-l-2 ${c.border} transition-colors`}
    >
      <div
        className={`flex-shrink-0 size-7 rounded-lg ${c.iconBg} flex items-center justify-center`}
      >
        <span className={c.icon}>{icon}</span>
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="font-[Montserrat,_sans-serif] text-[12px] font-bold text-foreground">
          {label}
        </span>
        <span className={`leading-tight truncate text-sm font-semibold text-foreground ${valueClassName}`}>
          {value}
        </span>
      </div>
    </div>
  );
}

// Map a profile lifecycle status to a meaningful stat-card color.
function profStatusColor(status: string): string {
  switch (status) {
    case "published":
    case "approved":
      return "emerald";
    case "in_review":
      return "sky";
    case "rejected":
      return "rose";
    case "superseded":
    case "withdrawn":
      return "amber";
    default: // draft
      return "slate";
  }
}

function toProfileRow(p: ProfileResponse): ProfileRow {
  return {
    id: p.id,
    documentId: p.documentId,
    name: p.title,
    title: p.title,
    oscalVersion: p.oscalVersion,
    importCount: p.importCount,
    controlCount: p.controlCount ?? 0,
    groupCount: p.groupCount ?? 0,
    status: mapLifecycleStatus(p.lifecycleStatus),
    lifecycleStatus: p.lifecycleStatus,
    resolved: p.resolved ?? false,
    version: p.versionNumber,
    updated: p.updatedAt
      ? p.updatedAt.split("T")[0].split("-").reverse().join("/")
      : "-",
    created: p.createdAt
      ? p.createdAt.split("T")[0].split("-").reverse().join("/")
      : "-",
  };
}

/** Extract all controls (with params) from nested groups */
function extractControls(groups?: GroupResponse[]): ControlResponse[] {
  if (!groups) return [];
  const result: ControlResponse[] = [];
  for (const g of groups) {
    for (const c of g.controls ?? []) {
      result.push(c);
      if (c.enhancements) result.push(...c.enhancements);
    }
    result.push(...extractControls(g.groups));
  }
  return result;
}

/** Convert API control to ControlNode */
function controlToNode(ctrl: ControlResponse): ControlNode {
  const children = (ctrl.enhancements ?? []).map(controlToNode);
  return {
    id: ctrl.controlId.toUpperCase(),
    title: ctrl.title,
    children: children.length ? children : undefined,
    hasParams: (ctrl.params?.length ?? 0) > 0,
  };
}

/** Convert API group to ControlNode (group as parent, controls as children) */
function groupToNode(group: GroupResponse): ControlNode {
  const controlChildren = (group.controls ?? []).map(controlToNode);
  const subGroupChildren = (group.groups ?? []).map(groupToNode);
  return {
    id: group.groupId.toUpperCase(),
    title: group.title,
    children: [...subGroupChildren, ...controlChildren],
  };
}

/** Filter tree nodes based on filter mode */
function filterTree(
  nodes: ControlNode[],
  mode: "all" | "groups" | "controls" | "params",
): ControlNode[] {
  if (mode === "all") return nodes;
  return nodes.reduce<ControlNode[]>((acc, node) => {
    const isGroup = node.children && node.children.length > 0;
    if (mode === "groups") {
      if (isGroup)
        acc.push({ ...node, children: filterTree(node.children!, mode) });
    } else if (mode === "controls") {
      if (isGroup) {
        const filtered = filterTree(node.children!, mode);
        if (filtered.length > 0) acc.push({ ...node, children: filtered });
      } else {
        acc.push(node);
      }
    } else if (mode === "params") {
      if (node.hasParams) {
        acc.push(node);
      } else if (isGroup) {
        const filtered = filterTree(node.children!, mode);
        if (filtered.length > 0) acc.push({ ...node, children: filtered });
      }
    }
    return acc;
  }, []);
}

/** Search/filter tree nodes by query string (matches id or title) */
function searchTree(nodes: ControlNode[], query: string): ControlNode[] {
  if (!query) return nodes;
  const q = query.toLowerCase();
  return nodes.reduce<ControlNode[]>((acc, node) => {
    const matches =
      node.id.toLowerCase().includes(q) || node.title.toLowerCase().includes(q);
    if (node.children && node.children.length > 0) {
      const filteredChildren = searchTree(node.children, query);
      if (matches || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children:
            filteredChildren.length > 0 ? filteredChildren : node.children,
        });
      }
    } else if (matches) {
      acc.push(node);
    }
    return acc;
  }, []);
}

/** Collect all node IDs from the tree (for Select All) */
function collectAllIds(nodes: ControlNode[]): string[] {
  const ids: string[] = [];
  for (const n of nodes) {
    ids.push(n.id);
    if (n.children) ids.push(...collectAllIds(n.children));
  }
  return ids;
}

/** Count only leaf (control) nodes that are in checkedIds, excluding group IDs */
function countLeafChecked(
  nodes: ControlNode[],
  checkedIds: Set<string>,
): number {
  let count = 0;
  for (const n of nodes) {
    if (n.children && n.children.length > 0) {
      count += countLeafChecked(n.children, checkedIds);
    } else if (checkedIds.has(n.id)) {
      count++;
    }
  }
  return count;
}

/** Find a node by ID and return all its descendant IDs (excluding itself) */
function collectDescendantIds(
  nodes: ControlNode[],
  targetId: string,
): string[] {
  for (const n of nodes) {
    if (n.id === targetId) return n.children ? collectAllIds(n.children) : [];
    if (n.children) {
      const found = collectDescendantIds(n.children, targetId);
      if (found.length > 0) return found;
    }
  }
  return [];
}

/** A single import row in the multi-source builder */
interface ImportRow {
  key: string;
  sourceType: "CATALOG" | "PROFILE";
  sourceId: string | null;
  sourceHref?: string;
  sourceTitle?: string;
  /** Preserves catalog selection when user temporarily switches to Profile type */
  savedCatalogId: string | null;
  savedCatalogTitle?: string;
  /** Preserves profile selection when user temporarily switches to Catalog type */
  savedProfileId: string | null;
  savedProfileTitle?: string;
  controls: ControlNode[];
  rawControls: ControlResponse[];
  checkedIds: Set<string>;
  loading: boolean;
}

let importRowCounter = 0;
function nextImportRowKey(): string {
  return `ir-${++importRowCounter}`;
}

const builderSteps = [
  {
    id: "s1",
    label: "Select Sources",
    description: "Choose source catalogs or profiles",
  },
  {
    id: "s2",
    label: "Select Controls",
    description: "Include/exclude controls",
  },
  {
    id: "s3",
    label: "Merge Configuration",
    description: "Combine method & output structure",
  },
  {
    id: "s4",
    label: "Alters",
    description: "Add or remove parts from controls",
  },
  { id: "s5", label: "Parameters", description: "Override parameter values" },
  { id: "s6", label: "Review", description: "Preview and save baseline" },
];

const editWizardSteps = [
  {
    id: "e1",
    label: "Select Controls",
    description: "Update included/excluded controls",
  },
  {
    id: "e2",
    label: "Merge Configuration",
    description: "Combine method & output structure",
  },
  {
    id: "e3",
    label: "Alters",
    description: "Add or remove parts from controls",
  },
  { id: "e4", label: "Parameters", description: "Override parameter values" },
];

/**
 * Map a control from the resolved-catalog API (snake_case, merged output) into
 * the ControlResponse shape the detail panel renders. Used as a fallback when a
 * source catalog can't be fetched directly (e.g. it was deleted / returns 404)
 * but the control still exists in the profile's resolved catalog.
 */
function resolvedToControlDetail(rc: ResolvedControl): ControlResponse {
  return {
    id: rc.control_id,
    controlId: rc.control_id,
    title: rc.title,
    clazz: "",
    sortOrder: 0,
    parts: (rc.parts ?? []).map((p, i) => ({
      id: p.part_id || `${rc.control_id}-part-${i}`,
      partId: p.part_id || `${rc.control_id}-part-${i}`,
      name: p.name,
      prose: p.resolved_prose || p.prose || "",
      sortOrder: i,
      parts: [],
    })),
    params: (rc.params ?? []).map((p, i) => ({
      id: p.param_id || `${rc.control_id}-param-${i}`,
      paramId: p.param_id || `${rc.control_id}-param-${i}`,
      label: p.label ?? "",
      values: p.values ?? [],
      guidelines: p.guidelines ?? [],
      sortOrder: i,
    })),
    enhancements: [],
  };
}

export function Profiles() {
  const navigate = useNavigate();
  const { id: profileIdFromUrl } = useParams<{ id: string }>();

  const [view, setView] = useState<"list" | "builder">("list");
  const [builderStep, setBuilderStep] = useState(0);
  const [profileTitle, setProfileTitle] = useState("");
  const [createdProfileId, setCreatedProfileId] = useState<string | null>(null);

  /* ── Multi-source import rows (builder) ── */
  const [importRows, setImportRows] = useState<ImportRow[]>([]);
  const [activeImportIdx, setActiveImportIdx] = useState(0);
  const [resolvedProfilesList, setResolvedProfilesList] = useState<
    ProfileResponse[]
  >([]);
  const [resolvedProfilesLoading, setResolvedProfilesLoading] = useState(false);

  const [profiles, setProfiles] = useState<ProfileRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profSearch, setProfSearch] = useState("");
  const [profStatusFilter, setProfStatusFilter] = useState("");
  const [profResolvedFilter, setProfResolvedFilter] = useState("");
  const [profPage, setProfPage] = useState(0);
  const [profPageSize, setProfPageSize] = useState(12);
  const [profSortKey, setProfSortKey] = useState<string | null>(null);
  const [profSortDir, setProfSortDir] = useState<"asc" | "desc">("asc");
  // 3-state cycle: asc → desc → unsorted (back to the default original order).
  const handleProfSort = (key: string) => {
    if (profSortKey !== key) {
      setProfSortKey(key);
      setProfSortDir("asc");
    } else if (profSortDir === "asc") {
      setProfSortDir("desc");
    } else {
      setProfSortKey(null);
      setProfSortDir("asc");
    }
  };

  const [catalogs, setCatalogs] = useState<CatalogResponse[]>([]);
  const [catalogsLoading, setCatalogsLoading] = useState(false);
  const [paramOverrides, setParamOverrides] = useState<Record<string, string>>(
    {},
  );
  const [saving, setSaving] = useState(false);
  const savingRef = React.useRef(false);
  const [builderExpandAll, setBuilderExpandAll] = useState<boolean | undefined>(
    undefined,
  );
  const [builderExpandTrigger, setBuilderExpandTrigger] = useState(0);

  /* ── Merge configuration state ── */
  const [mergeAnalysis, setMergeAnalysis] =
    useState<MergeAnalysisResponse | null>(null);
  const [mergeAnalysisLoading, setMergeAnalysisLoading] = useState(false);
  const [combineMethod, setCombineMethod] = useState<"use-first" | "keep">(
    "keep",
  );
  const [structuringDirective, setStructuringDirective] = useState<
    "flat" | "as-is" | "custom"
  >("as-is");
  const [mergeSaved, setMergeSaved] = useState(false);

  /* ── Available parameters state (wizard step 3) ── */
  const [availableParams, setAvailableParams] = useState<AvailableParameter[]>(
    [],
  );
  const [availableParamsLoading, setAvailableParamsLoading] = useState(false);

  /* ── Available controls / alter directives state (wizard step 4) ── */
  const [availableControls, setAvailableControls] = useState<
    AvailableControl[]
  >([]);
  const [availableControlsLoading, setAvailableControlsLoading] =
    useState(false);
  type AlterParamDraft = {
    id: string;
    paramId: string;
    label: string;
    clazz: string;
    usage: string;
    values: string;
    selections: string[];
    constraints: string[];
    guidelines: string[];
  };
  type AlterAdd = {
    id: string;
    position: string;
    byId: string;
    name: string;
    title: string;
    prose: string;
    params: AlterParamDraft[];
  };
  type AlterRemove = { id: string; byId: string; name: string };
  const [alterDirectives, setAlterDirectives] = useState<
    Record<string, { adds: AlterAdd[]; removes: AlterRemove[] }>
  >({});
  const [showAddForm, setShowAddForm] = useState<Record<string, boolean>>({});
  const [addPartDraft, setAddPartDraft] = useState<
    Record<
      string,
      {
        position: string;
        byId: string;
        name: string;
        title: string;
        prose: string;
        params: AlterParamDraft[];
      }
    >
  >({});

  /* ── Custom ordering state (wizard + edit mode) ── */
  const [customOrder, setCustomOrder] = useState<
    "keep" | "ascending" | "descending"
  >("keep");

  /* ── Edit-mode merge config state ── */
  const [editCombineMethod, setEditCombineMethod] = useState<
    "use-first" | "keep"
  >("keep");
  const [editStructuringDirective, setEditStructuringDirective] = useState<
    "flat" | "as-is" | "custom"
  >("flat");
  const [editCustomOrder, setEditCustomOrder] = useState<
    "keep" | "ascending" | "descending"
  >("keep");

  /* ── Inline rename state ── */
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [savingName, setSavingName] = useState(false);

  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState("");

  const [selectedProfile, setSelectedProfile] =
    useState<ProfileDetailResponse | null>(null);
  const [selectedProfileRow, setSelectedProfileRow] = useState(
    null as ProfileRow | null,
  );
  const [detailLoading, setDetailLoading] = useState(false);

  /* ── Resolved catalog state ── */
  const [resolvedControls, setResolvedControls] = useState<ResolvedControl[]>(
    [],
  );
  const [resolvedAt, setResolvedAt] = useState<string | null>(null);
  // Structuring method from the resolved-catalog API: "as-is" | "flat" | "custom"
  const [resolvedMethod, setResolvedMethod] = useState<string | null>(null);
  const [resolvedLoading, setResolvedLoading] = useState(false);
  const [resolvedSelectedControl, setResolvedSelectedControl] =
    useState<ResolvedControl | null>(null);
  // Collapsed resolved-catalog groups (empty = all expanded by default).
  const [resolvedCollapsedGroups, setResolvedCollapsedGroups] = useState<
    Set<string>
  >(new Set());

  // Free-text search over the resolved controls (matches id, title or prose).
  const [resolvedSearch, setResolvedSearch] = useState("");
  // When on, only show resolved controls that have parameters.
  const [resolvedParamsOnly, setResolvedParamsOnly] = useState(false);

  const toggleResolvedGroup = useCallback((groupId: string) => {
    setResolvedCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const filteredResolvedControls = useMemo(
    () =>
      filterResolvedControls(
        resolvedControls,
        resolvedSearch,
        resolvedParamsOnly,
      ),
    [resolvedControls, resolvedSearch, resolvedParamsOnly],
  );

  // Number of resolved controls that carry parameters (for the toggle badge).
  const resolvedParamCount = useMemo(
    () =>
      resolvedControls.filter((c) => c.params && c.params.length > 0).length,
    [resolvedControls],
  );

  /* ── Profile view-mode tree state ── */
  const [viewControlTree, setViewControlTree] = useState<ControlNode[]>([]);
  const [viewRawControls, setViewRawControls] = useState<ControlResponse[]>([]);
  const [viewTreeLoading, setViewTreeLoading] = useState(false);
  const [viewSelectedControl, setViewSelectedControl] =
    useState<ControlResponse | null>(null);
  const [viewSelectedGroup, setViewSelectedGroup] = useState<{
    groupId: string;
    groupName: string;
    controlCount: number;
  } | null>(null);
  const [viewTreeFilter, setViewTreeFilter] = useState<
    "all" | "groups" | "controls" | "params" | "selected" | "selected-params"
  >("all");
  // Expand/collapse model for the profile "Included Controls" tree — mirrors the
  // Catalog page's ControlTreeView (an expandAll flag + a trigger to re-apply it).
  const [viewTreeExpandAll, setViewTreeExpandAll] = useState<
    boolean | undefined
  >(undefined);
  const [viewTreeExpandTrigger, setViewTreeExpandTrigger] = useState(0);
  const [viewParamControls, setViewParamControls] = useState<
    { controlId: string; title: string }[]
  >([]);
  const [viewParamsMode, setViewParamsMode] = useState(false);
  const [viewParamsLoading, setViewParamsLoading] = useState(false);
  // Maps controlId (lowercase) → source_catalog_id for profile-source controls
  const [resolvedControlSourceMap, setResolvedControlSourceMap] = useState<
    Map<string, string>
  >(new Map());
  // Catalog tree metadata for CATALOG-source profiles whose controlDetails is
  // empty (e.g. manually created profiles). Built from getCatalog so the detail
  // view can show control titles, group structure and parts/params.
  const [viewCatalogControlMap, setViewCatalogControlMap] = useState<
    Map<string, { control: ControlResponse; groupId: string; groupName: string }>
  >(new Map());
  const [viewCatalogGroupIds, setViewCatalogGroupIds] = useState<Set<string>>(
    new Set(),
  );
  const [viewControlDetail, setViewControlDetail] =
    useState<ControlResponse | null>(null);
  const [viewControlLoading, setViewControlLoading] = useState(false);
  const [viewTreeSearch, setViewTreeSearch] = useState("");
  const [viewIncludedImportIdx, setViewIncludedImportIdx] = useState(0);
  const [editExpandAll, setEditExpandAll] = useState<boolean | undefined>(
    undefined,
  );
  const [editExpandTrigger, setEditExpandTrigger] = useState(0);

  /* ── Profile edit mode state ── */
  const [editMode, setEditMode] = useState(false);
  const [editWizardStep, setEditWizardStep] = useState(0);
  const [editSelectedControl, setEditSelectedControl] =
    useState<ControlResponse | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editTitleBackup, setEditTitleBackup] = useState("");
  const [editCheckedIds, setEditCheckedIds] = useState<Set<string>>(new Set());
  const [editCheckedIdsBackup, setEditCheckedIdsBackup] = useState<Set<string>>(
    new Set(),
  );
  const [editCatalogControls, setEditCatalogControls] = useState<ControlNode[]>(
    [],
  );
  const [editRawControls, setEditRawControls] = useState<ControlResponse[]>([]);
  const [editCatalogControlsLoading, setEditCatalogControlsLoading] =
    useState(false);
  const [editSourceCatalogId, setEditSourceCatalogId] = useState<string | null>(
    null,
  );
  const [profileParamOverrides, setProfileParamOverrides] = useState<
    Record<string, string>
  >({});
  const [profileParamOverridesBackup, setProfileParamOverridesBackup] =
    useState<Record<string, string>>({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [paramError, setParamError] = useState<string | null>(null);
  const [errorParamId, setErrorParamId] = useState<string | null>(null);
  const [expandedControls, setExpandedControls] = useState<Set<string>>(
    new Set(),
  );
  /* ── Multi-source edit state ── */
  const [editImportRows, setEditImportRows] = useState<ImportRow[]>([]);
  const [editImportRowsBackup, setEditImportRowsBackup] = useState<ImportRow[]>(
    [],
  );
  const [editActiveImportIdx, setEditActiveImportIdx] = useState(0);

  /* ── Export state ── */
  const [exportingId, setExportingId] = useState<string | null>(null);
  const [exportModalDocId, setExportModalDocId] = useState<string | null>(null);
  const [exportModalName, setExportModalName] = useState("");
  const [exportFormat, setExportFormat] = useState<string>("");

  /* ── Lifecycle transition state ── */
  const [resolving, setResolving] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState<string>("");
  const [transitionLabel, setTransitionLabel] = useState("");
  const [transitionComments, setTransitionComments] = useState("");
  const [transitioning, setTransitioning] = useState(false);
  const [transitionRequireComments, setTransitionRequireComments] =
    useState(false);

  const [showImport, setShowImport] = useState(false);
  const [importStep, setImportStep] = useState<
    "select" | "url" | "validating" | "validated" | "importing" | "findings"
  >("select");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importUrl, setImportUrl] = useState("");
  const [importMode, setImportMode] = useState<"full" | "delta">("full");
  const [importError, setImportError] = useState<string | null>(null);
  const [importErrorTitle, setImportErrorTitle] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<ImportJobResponse | null>(
    null,
  );
  const [validateResult, setValidateResult] = useState<ValidateResponse | null>(
    null,
  );
  const [validating, setValidating] = useState(false);

  /* ── Import Session modal state ── */
  const [showSession, setShowSession] = useState(false);
  const [sessionData, setSessionData] = useState<ImportSessionResponse | null>(
    null,
  );
  const [sessionLoading, setSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [committing, setCommitting] = useState(false);
  const [aborting, setAborting] = useState(false);

  /* ── Organization state ── */
  const [accessibleOrgs, setAccessibleOrgs] = useState<any[]>([]);
  const [accessibleOrgsLoading, setAccessibleOrgsLoading] = useState(false);
  const [importOrgId, setImportOrgId] = useState("");

  const fetchProfileDetail = useCallback(
    async (
      id: string,
      options?: { resolved?: boolean; lifecycleStatus?: string },
    ) => {
      const isResolved = options?.resolved ?? false;
      const isEditable = isEditableLifecycleStatus(options?.lifecycleStatus);
      setEditMode(false);
      setEditSelectedControl(null);
      setEditImportRows([]);
      setDetailLoading(true);
      setResolvedControls([]);
      setResolvedAt(null);
      setResolvedMethod(null);
      setSelectedProfile(null);
      setViewSelectedControl(null);
      setViewControlDetail(null);
      setViewParamsMode(false);
      setViewParamControls([]);
      setResolvedControlSourceMap(new Map());
      setViewCatalogControlMap(new Map());
      setViewCatalogGroupIds(new Set());
      setResolvedSelectedControl(null);
      setViewTreeExpandAll(undefined);
      setViewIncludedImportIdx(0);

      if (isResolved && !isEditable) {
        // Resolved profiles - only call resolved-catalog API
        try {
          const rRes = await getResolvedCatalog(id);
          setResolvedControls(rRes.data.controls ?? []);
          setResolvedAt(rRes.data.resolvedAt ?? null);
          setResolvedMethod(rRes.data.method ?? null);
          // Default to collapsed groups when opening a resolved profile.
          setResolvedCollapsedGroups(
            new Set(
              getResolvedGroupIds(
                rRes.data.controls ?? [],
                rRes.data.method ?? null,
              ),
            ),
          );
        } catch {
          setResolvedControls([]);
        } finally {
          setDetailLoading(false);
        }
      } else {
        // Editable profiles should continue to use profile detail API,
        // even when a resolved catalog exists.
        try {
          let profileData;
          try {
            const res = await getProfile(id);
            profileData = res.data;
          } catch (error) {
            // Fallback for view-only users: use resolved catalog if getProfile fails
            const rRes = await getResolvedCatalog(id);
            setResolvedControls(rRes.data.controls ?? []);
            setResolvedAt(rRes.data.resolvedAt ?? null);
            setResolvedMethod(rRes.data.method ?? null);
            setResolvedCollapsedGroups(
              new Set(
                getResolvedGroupIds(
                  rRes.data.controls ?? [],
                  rRes.data.method ?? null,
                ),
              ),
            );
            // Set a minimal profile object so the resolved view can render
            setSelectedProfile({
              id,
              title: "",
              imports: [],
              merge: undefined,
              modify: undefined,
            } as any);
            setDetailLoading(false);
            return;
          }

          setSelectedProfile(profileData);

          // For CATALOG sources whose controlDetails is empty (e.g. manually
          // created profiles), fetch the catalog tree so the detail view can
          // resolve control titles, group structure and parts/params.
          (async () => {
            const ctrlMap = new Map<
              string,
              { control: ControlResponse; groupId: string; groupName: string }
            >();
            const grpIds = new Set<string>();
            const seenCatalogIds = new Set<string>();
            const walkGroup = (g: GroupResponse) => {
              grpIds.add(g.groupId.toLowerCase());
              const register = (
                c: ControlResponse,
                groupId: string,
                groupName: string,
              ) => {
                ctrlMap.set(c.controlId.toLowerCase(), {
                  control: c,
                  groupId,
                  groupName,
                });
                for (const e of c.enhancements ?? [])
                  register(e, groupId, groupName);
              };
              for (const c of g.controls ?? []) register(c, g.groupId, g.title);
              for (const sub of g.groups ?? []) walkGroup(sub);
            };
            for (const imp of profileData.imports ?? []) {
              if ((imp.sourceType ?? "CATALOG") !== "CATALOG") continue;
              // Fetch when the backend did not embed group metadata — either no
              // controlDetails at all, or controlDetails entries that carry a
              // title but no groupId (so the accordion can't be built from them).
              const hasGroupInfo = (imp.controlDetails ?? []).some(
                (cd) => ((cd as any).groupId ?? "").trim() !== "",
              );
              if (hasGroupInfo) continue;
              const catalogId =
                imp.catalogId ??
                imp.sourceId ??
                (imp.href?.startsWith("#") ? imp.href.slice(1) : imp.href);
              if (!catalogId || seenCatalogIds.has(catalogId)) continue;
              seenCatalogIds.add(catalogId);
              try {
                const cRes = await getCatalog(catalogId);
                for (const g of cRes.data.groups ?? []) walkGroup(g);
                // Top-level (ungrouped) catalog controls.
                const registerTop = (c: ControlResponse) => {
                  ctrlMap.set(c.controlId.toLowerCase(), {
                    control: c,
                    groupId: "",
                    groupName: "",
                  });
                  for (const e of c.enhancements ?? []) registerTop(e);
                };
                for (const c of cRes.data.controls ?? []) registerTop(c);
              } catch {
                /* ignore — falls back to flat rendering */
              }
            }
            if (ctrlMap.size > 0) setViewCatalogControlMap(ctrlMap);
            if (grpIds.size > 0) setViewCatalogGroupIds(grpIds);
          })();

          // Build param controls badge:
          // - CATALOG sources: use controlDetails.hasParameters (already in GET /profiles/{id} response)
          // - PROFILE sources: backend does not populate hasParameters in controlDetails,
          //   so we still need getResolvedCatalog to identify controls that carry params
          setViewParamsLoading(true);
          const seenIds = new Set<string>();
          const results: { controlId: string; title: string }[] = [];

          // Pass 1: catalog sources (no extra API call)
          for (const imp of profileData.imports ?? []) {
            if (imp.sourceType === "PROFILE") continue;
            for (const cd of imp.controlDetails ?? []) {
              if (cd.hasParameters) {
                const key = cd.controlId.toUpperCase();
                if (!seenIds.has(key)) {
                  seenIds.add(key);
                  results.push({ controlId: key, title: cd.controlName });
                }
              }
            }
          }
          setViewParamControls([...results]);
          if (isResolved) {
            try {
              const resolvedRes = await getResolvedCatalog(id);
              setResolvedControls(resolvedRes.data.controls ?? []);
              setResolvedAt(resolvedRes.data.resolvedAt ?? null);
              setResolvedMethod(resolvedRes.data.method ?? null);
              setResolvedCollapsedGroups(
                new Set(
                  getResolvedGroupIds(
                    resolvedRes.data.controls ?? [],
                    resolvedRes.data.method ?? null,
                  ),
                ),
              );
            } catch {
              setResolvedControls([]);
              setResolvedAt(null);
              setResolvedMethod(null);
            }
          }

          // Pass 2: profile sources — async, resolved-catalog needed for param detection
          // Also builds resolvedControlSourceMap: controlId → source_catalog_id
          (async () => {
            const seenProfileIds = new Set<string>();
            const srcCatalogMap = new Map<string, string>();
            for (const imp of profileData.imports ?? []) {
              if (imp.sourceType !== "PROFILE") continue;
              const sourceId =
                imp.sourceId ??
                (imp.href.startsWith("#") ? imp.href.slice(1) : imp.href);
              if (!sourceId || seenProfileIds.has(sourceId)) continue;
              seenProfileIds.add(sourceId);
              try {
                const rRes = await getResolvedCatalog(sourceId);
                for (const ctrl of rRes.data.controls ?? []) {
                  // Map controlId → source_catalog_id for use in fetchControlDetail
                  if (ctrl.source_catalog_id) {
                    srcCatalogMap.set(
                      ctrl.control_id.toLowerCase(),
                      ctrl.source_catalog_id,
                    );
                  }
                  if (ctrl.params && ctrl.params.length > 0) {
                    const key = ctrl.control_id.toUpperCase();
                    if (!seenIds.has(key)) {
                      seenIds.add(key);
                      results.push({ controlId: key, title: ctrl.title });
                    }
                  }
                }
              } catch {
                /* ignore */
              }
            }
            setResolvedControlSourceMap(srcCatalogMap);
            setViewParamControls([...results]);
            setViewParamsLoading(false);
          })();
        } catch {
          setSelectedProfile(null);
        } finally {
          setDetailLoading(false);
        }
      }
    },
    [],
  );

  /** Resolve an import href to a catalog UUID - fast path only (# or UUID) */
  const resolveCatalogIdFast = (href: string): string | null => {
    if (href.startsWith("#")) return href.slice(1);
    if (/^[0-9a-f-]{36}$/i.test(href)) return href;
    return null;
  };

  const buildImportHref = (row: ImportRow): string => {
    if (row.sourceId) return `#${row.sourceId}`;
    return row.sourceHref ?? "";
  };

  /** Collect all params from a list of controls with full metadata */
  interface CollectedParam {
    paramId: string;
    controlId: string;
    label: string;
    catalogValues: string[];
    clazz: string | null;
    constraints: ParameterConstraint[];
    guidelines: { prose: string }[];
    selectConfig: ParameterSelection | null;
    usage: string | null;
  }

  const collectParams = (
    controls: ControlResponse[],
    includedIds: string[] | null, // null = include all
  ): CollectedParam[] => {
    const params: CollectedParam[] = [];
    const mapParam = (p: ParamResponse, ctrlId: string) => ({
      paramId: p.paramId,
      controlId: ctrlId,
      label: p.label,
      catalogValues: p.values ?? [],
      clazz: (p.clazz as string | null) ?? null,
      constraints: (p.constraints as ParameterConstraint[] | undefined) ?? [],
      guidelines: (p.guidelines as { prose: string }[] | undefined) ?? [],
      selectConfig:
        (p.selectConfig as ParameterSelection | null | undefined) ?? null,
      usage: (p.usage as string | null) ?? null,
    });
    for (const ctrl of controls) {
      const ctrlId = ctrl.controlId.toUpperCase();
      if (includedIds && !includedIds.some((id) => id.toUpperCase() === ctrlId))
        continue;
      for (const p of ctrl.params ?? []) params.push(mapParam(p, ctrlId));
      for (const enh of ctrl.enhancements ?? []) {
        const enhId = enh.controlId.toUpperCase();
        if (
          includedIds &&
          !includedIds.some((id) => id.toUpperCase() === enhId)
        )
          continue;
        for (const p of enh.params ?? []) params.push(mapParam(p, enhId));
      }
    }
    return params;
  };

  /** Fetch controls from a catalog by ID, returns flat ControlResponse[] */
  const fetchControlsFromCatalog = async (
    catalogId: string,
  ): Promise<ControlResponse[]> => {
    try {
      const res = await listControls(catalogId, { flat: true });
      return res.data ?? [];
    } catch {
      try {
        const catRes = await getCatalog(catalogId);
        return extractControls(catRes.data.groups);
      } catch {
        return [];
      }
    }
  };

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listProfiles();
      setProfiles(res.data.map(toProfileRow));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to load profiles");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCatalogs = useCallback(async () => {
    setCatalogsLoading(true);
    try {
      const res = await listCatalogs();
      setCatalogs(res.data);
    } catch {
      // catalogs fail silently - user sees empty list
    } finally {
      setCatalogsLoading(false);
    }
  }, []);

  /* ── Fetch accessible organizations ── */
  const fetchAccessibleOrgs = async (module: string, action: string) => {
    setAccessibleOrgsLoading(true);
    try {
      const res = await getAPI(`${API_URL.ACCESSIBLE_ORGS}?module=${module}&action=${action}&org_type=ORGANIZATION&org_type=GROUP`);
      const orgs = res?.data?.organizations ?? [];
      setAccessibleOrgs(orgs);
      if (orgs.length === 1) {
        setImportOrgId(orgs[0].id);
      }
    } catch {
      setAccessibleOrgs([]);
    } finally {
      setAccessibleOrgsLoading(false);
    }
  };

  const fetchResolvedProfilesForBuilder = useCallback(async () => {
    setResolvedProfilesLoading(true);
    try {
      const res = await listResolvedProfiles();
      const list = Array.isArray(res.data) ? res.data : [];
      if (list.length > 0) {
        setResolvedProfilesList(list);
      } else {
        // Fallback: filter resolved profiles from the full list
        const allRes = await listProfiles();
        setResolvedProfilesList((allRes.data ?? []).filter((p) => p.resolved));
      }
    } catch {
      // Fallback: filter resolved profiles from the full list
      try {
        const allRes = await listProfiles();
        setResolvedProfilesList((allRes.data ?? []).filter((p) => p.resolved));
      } catch {
        setResolvedProfilesList([]);
      }
    } finally {
      setResolvedProfilesLoading(false);
    }
  }, []);

  /** Load controls for a specific import row by index */
  const loadImportRowControls = useCallback(
    async (
      sourceId: string,
      sourceType: "CATALOG" | "PROFILE",
      rowIdx: number,
    ) => {
      setImportRows((prev) =>
        prev.map((r, i) =>
          i === rowIdx
            ? {
              ...r,
              loading: true,
              controls: [],
              rawControls: [],
              checkedIds: new Set<string>(),
            }
            : r,
        ),
      );
      try {
        if (sourceType === "CATALOG") {
          const res = await getCatalog(sourceId);
          const catalog = res.data;
          let controls: ControlNode[] = [];
          let raw: ControlResponse[] = [];
          if (catalog.groups && catalog.groups.length > 0) {
            controls = catalog.groups.map(groupToNode);
            raw = extractControls(catalog.groups);
          } else {
            const ctrls =
              (catalog as unknown as { controls?: ControlResponse[] })
                .controls ?? [];
            controls = ctrls.map(controlToNode);
            raw = ctrls;
          }
          setImportRows((prev) =>
            prev.map((r, i) =>
              i === rowIdx
                ? { ...r, controls, rawControls: raw, loading: false }
                : r,
            ),
          );
        } else {
          // Profile source - load resolved controls from the profile
          const res = await getResolvedCatalog(sourceId);
          const resolvedControls = res.data.controls ?? [];
          // Group resolved controls into tree nodes by source
          const groupMap = new Map<string, ControlNode>();
          for (const rc of resolvedControls) {
            const groupId = (
              rc.group_id ?? rc.control_id.replace(/-\d+$/, "")
            ).toUpperCase();
            if (!groupMap.has(groupId)) {
              groupMap.set(groupId, {
                id: groupId,
                title: rc.group_title ?? groupId,
                children: [],
              });
            }
            groupMap.get(groupId)!.children!.push({
              id: rc.control_id.toUpperCase(),
              title: rc.title,
            });
          }
          const controls = Array.from(groupMap.values());
          // Create minimal ControlResponse entries for param collection
          const raw: ControlResponse[] = resolvedControls.map(
            (rc) =>
              ({
                controlId: rc.control_id,
                title: rc.title,
                params: [],
                enhancements: [],
              }) as unknown as ControlResponse,
          );
          setImportRows((prev) =>
            prev.map((r, i) =>
              i === rowIdx
                ? { ...r, controls, rawControls: raw, loading: false }
                : r,
            ),
          );
        }
      } catch {
        setImportRows((prev) =>
          prev.map((r, i) =>
            i === rowIdx
              ? { ...r, controls: [], rawControls: [], loading: false }
              : r,
          ),
        );
      }
    },
    [],
  );

  /* ── Rename handler ── */
  const handleRenameProfile = async (id: string, documentId: string) => {
    const trimmed = editingNameValue.trim();
    if (!trimmed) return;
    setSavingName(true);
    try {
      await renameDocument(documentId, trimmed);
      setProfiles((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, name: trimmed, title: trimmed } : p,
        ),
      );
      if (selectedProfileRow?.id === id)
        setSelectedProfileRow((prev) =>
          prev ? { ...prev, name: trimmed, title: trimmed } : prev,
        );
      if (selectedProfile?.id === id)
        setSelectedProfile((prev) =>
          prev ? { ...prev, title: trimmed } : prev,
        );
      useToastStore.getState().showSuccess("Success", "Profile renamed");
    } catch (err: unknown) {
      useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Failed to rename profile",
      );
    } finally {
      setSavingName(false);
      setEditingNameId(null);
    }
  };

  const handleDeleteProfile = (id: string) => {
    setDeleteTargetId(id);
    setDeleteReason("");
    setDeleteModalOpen(true);
  };

  const confirmDeleteProfile = async (reason?: string) => {
    if (!deleteTargetId) return;
    setDeleteModalOpen(false);
    setDeleting(deleteTargetId);
    try {
      await deleteProfile(deleteTargetId, reason);
      fetchProfiles();
      if (
        selectedProfile?.id === deleteTargetId ||
        selectedProfileRow?.id === deleteTargetId
      ) {
        setSelectedProfile(null);
        setSelectedProfileRow(null);
      }
    } catch (err: unknown) {
      useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Failed to delete profile",
      );
    } finally {
      setDeleting(null);
      setDeleteTargetId(null);
      setDeleteReason("");
    }
  };

  /* ── Lifecycle ── */
  const lifecycleActions: Record<
    string,
    {
      label: string;
      target: string;
      color: string;
      icon: React.ReactNode;
      requireComments?: boolean;
      warning?: string;
    }[]
  > = {
    draft: [
      {
        label: "Submit for Review",
        target: "in_review",
        color:
          "bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20",
        icon: <Send className="size-4" />,
      },
    ],
    in_review: [
      {
        label: "Approve",
        target: "approved",
        color: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20",
        icon: <ShieldCheck className="size-4" />,
      },
      {
        label: "Reject",
        target: "rejected",
        color: "bg-red-600 hover:bg-red-700 shadow-red-500/20",
        icon: <XCircle className="size-4" />,
        requireComments: true,
      },
    ],
    approved: [
      {
        label: "Publish",
        target: "published",
        color: "bg-purple-600 hover:bg-purple-700 shadow-purple-500/20",
        icon: <Globe className="size-4" />,
        warning: "This action will freeze the content permanently.",
      },
    ],
    rejected: [
      {
        label: "Submit for Review",
        target: "in_review",
        color:
          "bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20",
        icon: <Send className="size-4" />,
      },
    ],
    published: [
      {
        label: "Supersede",
        target: "superseded",
        color: "bg-orange-600 hover:bg-orange-700 shadow-orange-500/20",
        icon: <Archive className="size-4" />,
      },
      {
        label: "Withdraw",
        target: "withdrawn",
        color: "bg-rose-700 hover:bg-rose-800 shadow-rose-500/20",
        icon: <Ban className="size-4" />,
      },
    ],
    superseded: [],
    withdrawn: [],
  };

  // Get lifecycle actions for current state (permission-based filtering is done at render time)
  const hasUpdatePermission = useRBACPermission("profile", "update");
  const hasApprovePermission = useRBACPermission("profile", "approve");
  const hasPublishPermission = useRBACPermission("profile", "publish");
  const currentActions = selectedProfileRow
    ? (lifecycleActions[selectedProfileRow.lifecycleStatus] ?? []).filter((a) => {
        // Show "Submit for Review" only if user has update permission
        if (a.label === "Submit for Review") return hasUpdatePermission;
        // Show "Approve" and "Reject" only if user has approve permission
        if (["Approve", "Reject"].includes(a.label)) return hasApprovePermission;
        // Show "Publish" only if user has publish permission
        if (a.label === "Publish") return hasPublishPermission;
        // Show "Supersede" and "Withdraw" only if user has update permission
        if (["Supersede", "Withdraw"].includes(a.label)) return hasUpdatePermission;
        return true;
      })
    : [];

  const isEditable = selectedProfileRow
    ? isEditableLifecycleStatus(selectedProfileRow.lifecycleStatus)
    : false;

  // A profile with no groups and no controls is empty — it must not be resolved
  // or advanced through the lifecycle until it actually contains controls.
  // The list-row counts (controlCount/groupCount) can lag or come back 0 from
  // the list API, so also look at the loaded detail: resolved controls, or the
  // controls included in the profile's imports.
  const detailControlCount =
    (resolvedControls?.length ?? 0) ||
    (selectedProfile?.imports?.reduce(
      (sum, imp) =>
        sum +
        (imp.includeControls?.reduce(
          (s, ic) => s + (ic["with-ids"]?.length ?? 0),
          0,
        ) ?? 0),
      0,
    ) ??
      0);
  const profileIsEmpty = selectedProfileRow
    ? (selectedProfileRow.controlCount ?? 0) === 0 &&
      (selectedProfileRow.groupCount ?? 0) === 0 &&
      detailControlCount === 0
    : false;
  const EMPTY_PROFILE_MSG =
    "This profile has no controls or groups. Add controls before resolving or advancing its stage.";

  /* ── Filtered & paginated profiles ── */
  const filteredProfiles = useMemo(() => {
    const q = profSearch.trim().toLowerCase();
    return profiles.filter((p) => {
      const matchSearch = !q || p.title.toLowerCase().includes(q);
      const matchStatus =
        !profStatusFilter || p.lifecycleStatus === profStatusFilter;
      const matchResolved =
        !profResolvedFilter ||
        (profResolvedFilter === "yes" ? p.resolved : !p.resolved);
      return matchSearch && matchStatus && matchResolved;
    });
  }, [profiles, profSearch, profStatusFilter, profResolvedFilter]);

  // Original serial number per row (position in the unsorted, filtered list) so
  // the "#" travels with its row — making it obvious when sorting reorders rows.
  const profOriginalIndexMap = useMemo(
    () => new Map(filteredProfiles.map((p, i) => [p.id, i])),
    [filteredProfiles],
  );

  const sortedProfiles = useMemo(() => {
    if (!profSortKey) return filteredProfiles;
    return [...filteredProfiles].sort((a, b) => {
      const aVal: any = a[profSortKey as keyof typeof a];
      const bVal: any = b[profSortKey as keyof typeof a];
      if (typeof aVal === "number")
        return profSortDir === "asc" ? aVal - bVal : bVal - aVal;
      return profSortDir === "asc"
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal));
    });
  }, [filteredProfiles, profSortKey, profSortDir]);

  const paginatedProfiles = useMemo(() => {
    const start = profPage * profPageSize;
    return sortedProfiles.slice(start, start + profPageSize);
  }, [sortedProfiles, profPage, profPageSize]);

  const openTransitionModal = (
    target: string,
    label: string,
    requireComments = false,
  ) => {
    if (profileIsEmpty) {
      useToastStore
        .getState()
        .showError("Cannot change stage", EMPTY_PROFILE_MSG);
      return;
    }
    setTransitionTarget(target);
    setTransitionLabel(label);
    setTransitionComments("");
    setTransitionRequireComments(requireComments);
    setShowTransition(true);
  };

  const handleTransition = async () => {
    if (!selectedProfileRow) return;
    if (transitionRequireComments && !transitionComments.trim()) return;
    setTransitioning(true);
    try {
      await transitionDocument(selectedProfileRow.documentId, {
        targetStatus: transitionTarget,
        comments: transitionComments.trim() || undefined,
      });
      // Auto-resolve after publishing
      if (transitionTarget === "published") {
        try {
          await resolveProfile(selectedProfileRow.id);
        } catch (resolveErr) {
          console.error("Auto-resolve failed:", resolveErr);
          // Don't fail the publish if resolve fails - log and continue
        }
      }

      // Refresh profiles list
      const res = await listProfiles();
      const rows = res.data.map(toProfileRow);
      setProfiles(rows);
      // Update selected row
      const updated = rows.find((r) => r.id === selectedProfileRow.id);
      if (updated) {
        setSelectedProfileRow(updated);
        // Refresh the loaded detail so the resolved/detail view reflects the
        // new lifecycle status instead of showing stale data.
        await fetchProfileDetail(updated.id, {
          resolved: updated.resolved,
          lifecycleStatus: updated.lifecycleStatus,
        });
      }
      setShowTransition(false);
      useToastStore.getState().showSuccess("Success", `Status changed to "${transitionTarget.replace(/_/g, " ")}"`,
      );
    } catch (err: unknown) {
      useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Transition failed",
      );
    } finally {
      setTransitioning(false);
    }
  };

  const handleResolveProfile = async () => {
    if (!selectedProfileRow) return;
    if (profileIsEmpty) {
      useToastStore.getState().showError("Cannot resolve", EMPTY_PROFILE_MSG);
      return;
    }
    setResolving(true);
    try {
      await resolveProfile(selectedProfileRow.id);
      useToastStore.getState().showSuccess("Success", "Profile resolved successfully");
      const res = await listProfiles();
      const rows = res.data.map(toProfileRow);
      setProfiles(rows);
      const updated = rows.find((r) => r.id === selectedProfileRow.id);
      if (updated) {
        setSelectedProfileRow(updated);
        await fetchProfileDetail(updated.id, {
          resolved: updated.resolved,
          lifecycleStatus: updated.lifecycleStatus,
        });
      }
    } catch (err: unknown) {
      useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Failed to resolve profile",
      );
    } finally {
      setResolving(false);
    }
  };

  /* ── Export handler ── */
  const handleExportProfile = async (
    documentId: string,
    format: "json" | "xml" | "yaml",
  ) => {
    setExportingId(documentId);
    setExportModalDocId(null);
    setExportFormat(format);
    try {
      const profileName = profiles.find((p) => p.id === documentId)?.name || "profile";
      const res = await exportCatalogDoc(documentId, format);
      let job = res.data;

      if (job.status === "pending") {
        for (let i = 0; i < 30; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          const poll = await getExportJob(job.jobId);
          job = poll.data;
          if (job.status !== "pending") break;
        }
      }

      if (job.status === "completed") {
        const downloadUrl = job.downloadUrl || getExportDownloadUrl(job.jobId);
        const downloadRes = await downloadFile(downloadUrl);
        if (!downloadRes) throw new Error("No response from download");

        // Extract documentTitle from response headers
        let documentTitle = profileName;
        const xDocumentTitle = downloadRes.headers?.["x-document-title"];
        if (xDocumentTitle) {
          documentTitle = xDocumentTitle;
        } else {
          // Try to extract from Content-Disposition header
          const contentDisposition = downloadRes.headers?.["content-disposition"] || "";
          const filenameMatch = contentDisposition.match(/filename\*?=(?:"([^"]*)"|([^;,\s]*))/);
          if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
            const filename = (filenameMatch[1] || filenameMatch[2]).split(".")[0];
            documentTitle = filename;
          }
        }

        // Get MIME type from API response Content-Type header first
        let mimeType = downloadRes.headers?.["content-type"] || "";
        if (!mimeType) {
          // Fallback: use selected format
          const mimeTypes: Record<string, string> = {
            json: "application/json",
            xml: "application/xml",
            yaml: "application/yaml",
          };
          mimeType = mimeTypes[format] || "application/octet-stream";
        }
        const blob = new Blob([downloadRes.data], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${documentTitle}.${format}`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(url);
        useToastStore.getState().showSuccess("Success", "Export ready - download started");
      } else if (job.status === "failed") {
        useToastStore.getState().showError("Error", "Export failed");
      } else {
        useToastStore.getState().showError("Error", "Export timed out - please try again");
      }
    } catch (err: unknown) {
      useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Export failed");
    } finally {
      setExportingId(null);
    }
  };

  /** Enter edit mode - load catalog tree(s), pre-check controls.
   *  If focusControl is provided, pre-select that control in the edit tree. */
  const enterEditMode = async (focusControl?: ControlResponse | null) => {
    if (!selectedProfile) return;

    setEditTitle(selectedProfile.title);
    setEditTitleBackup(selectedProfile.title);

    // Pre-fill merge config from existing profile merge settings
    setEditCombineMethod(
      (selectedProfile.merge?.combineMethod as "use-first" | "keep") ?? "keep",
    );
    setEditStructuringDirective(
      (selectedProfile.merge?.structuringDirective as
        | "flat"
        | "as-is"
        | "custom") ?? "flat",
    );
    const existingCustom = selectedProfile.merge?.custom as {
      "insert-controls"?: { order?: string }[];
    } | null;
    setEditCustomOrder(
      (existingCustom?.["insert-controls"]?.[0]?.order as
        | "keep"
        | "ascending"
        | "descending") ?? "keep",
    );

    // Pre-fill param overrides from existing modify.setParameters
    const existingModify = selectedProfile.modify as {
      setParameters?: { "param-id": string; values: string[] }[];
    } | null;
    const overrides: Record<string, string> = {};
    if (existingModify?.setParameters) {
      for (const sp of existingModify.setParameters) {
        overrides[sp["param-id"]] = sp.values.join(", ");
      }
    }
    setProfileParamOverrides(overrides);
    setProfileParamOverridesBackup({ ...overrides });

    const includedIds = selectedProfile.imports
      .flatMap(
        (imp) =>
          imp.includeControls?.flatMap((ic) => ic["with-ids"] ?? []) ?? [],
      )
      .map((id) => id.toUpperCase());
    setEditCheckedIds(new Set(includedIds));
    setEditCheckedIdsBackup(new Set(includedIds));
    setParamError(null);
    setErrorParamId(null);

    // If we already have edit import rows loaded from a previous edit, reuse
    if (
      editImportRows.length > 0 &&
      editImportRows.some((r) => r.controls.length > 0)
    ) {
      // Apply focus control even when reusing cached data
      if (focusControl) {
        const focusId = focusControl.controlId.toUpperCase();
        for (let i = 0; i < editImportRows.length; i++) {
          if (editImportRows[i].checkedIds.has(focusId)) {
            setEditActiveImportIdx(i);
            setEditCatalogControls(editImportRows[i].controls);
            setEditRawControls(editImportRows[i].rawControls);
            setEditCheckedIds(editImportRows[i].checkedIds);
            setEditSourceCatalogId(editImportRows[i].sourceId);
            break;
          }
        }
        const allRaw = editImportRows.flatMap((r) => r.rawControls);
        const ctrl = allRaw.find((c) => c.controlId.toUpperCase() === focusId);
        setEditSelectedControl(ctrl ?? focusControl);
        setViewTreeFilter("all");
      } else {
        setEditSelectedControl(null);
      }
      setEditMode(true);
      return;
    }

    // Build import rows from profile imports and load controls for each
    setEditCatalogControlsLoading(true);
    const rows: ImportRow[] = [];

    let cachedCatalogs: CatalogResponse[] | null = null;

    for (let i = 0; i < selectedProfile.imports.length; i++) {
      const imp = selectedProfile.imports[i];
      const sourceType = imp.sourceType ?? "CATALOG";
      const href = imp.href;
      let sourceId = imp.sourceId ?? resolveCatalogIdFast(href);

      const impCheckedIds = (imp.includeControls ?? [])
        .flatMap((ic) => ic["with-ids"] ?? [])
        .map((id) => id.toUpperCase());

      if (sourceType === "CATALOG") {
        try {
          if (!cachedCatalogs) {
            const catListRes = await listCatalogs();
            cachedCatalogs = catListRes.data ?? [];
          }

          const availableCatalogs = cachedCatalogs;
          const hasExactCatalogId =
            !!sourceId && availableCatalogs.some((cat) => cat.id === sourceId);

          if (!hasExactCatalogId) {
            const titleMatchedCatalog =
              availableCatalogs.find((cat) => cat.title === imp.sourceTitle) ??
              null;

            if (titleMatchedCatalog) {
              sourceId = titleMatchedCatalog.id;
            }
          }

          if (
            (!sourceId ||
              !availableCatalogs.some((cat) => cat.id === sourceId)) &&
            impCheckedIds.length > 0
          ) {
            for (const cat of availableCatalogs) {
              const controls = await fetchControlsFromCatalog(cat.id);
              const controlIds = new Set(
                controls.map((c) => c.controlId.toUpperCase()),
              );
              if (impCheckedIds.some((id) => controlIds.has(id))) {
                sourceId = cat.id;
                break;
              }
            }
          }
        } catch {
          /* skip */
        }

        if (
          sourceId &&
          cachedCatalogs &&
          !cachedCatalogs.some((cat) => cat.id === sourceId)
        ) {
          sourceId = null;
        }
      }

      if (!sourceId && imp.sourceId) {
        sourceId = imp.sourceId;
      }

      const row: ImportRow = {
        key: nextImportRowKey(),
        sourceType,
        sourceId: sourceId ?? null,
        sourceHref: imp.href,
        sourceTitle: imp.sourceTitle,
        savedCatalogId: null,
        savedProfileId: null,
        controls: [],
        rawControls: [],
        checkedIds: new Set(impCheckedIds),
        loading: false,
      };

      // Load controls from the source
      if (row.sourceId) {
        try {
          if (sourceType === "CATALOG") {
            const res = await getCatalog(row.sourceId);
            const catalog = res.data;
            if (catalog.groups && catalog.groups.length > 0) {
              row.controls = catalog.groups.map(groupToNode);
              row.rawControls = extractControls(catalog.groups);
            } else {
              const ctrls =
                (catalog as unknown as { controls?: ControlResponse[] })
                  .controls ?? [];
              row.controls = ctrls.map(controlToNode);
              row.rawControls = ctrls;
            }
          } else {
            const res = await getResolvedCatalog(row.sourceId);
            const resolvedCtrls = res.data.controls ?? [];
            const groupMap = new Map<string, ControlNode>();
            for (const rc of resolvedCtrls) {
              const groupId = (
                rc.group_id ?? rc.control_id.replace(/-\d+$/, "")
              ).toUpperCase();
              const groupTitle = rc.group_title ?? groupId;
              if (!groupMap.has(groupId))
                groupMap.set(groupId, {
                  id: groupId,
                  title: groupTitle,
                  children: [],
                });
              groupMap.get(groupId)!.children!.push({
                id: rc.control_id.toUpperCase(),
                title: rc.title,
              });
            }
            row.controls = Array.from(groupMap.values());
            row.rawControls = resolvedCtrls.map(
              (rc) =>
                ({
                  controlId: rc.control_id,
                  title: rc.title,
                  // Preserve params from resolved-catalog so Parameters filter works in edit mode
                  params: (rc.params ?? []).map((p) => ({
                    paramId: p.param_id,
                    label: p.label ?? p.param_id,
                    values: p.values ?? [],
                    clazz: p.class ?? null,
                    guidelines: p.guidelines ?? [],
                    constraints: [],
                    sortOrder: 0,
                  })),
                  enhancements: [],
                }) as unknown as ControlResponse,
            );
          }
        } catch {
          row.controls = [];
          row.rawControls = [];
        }
      } else if (sourceType === "CATALOG" && (imp.controlDetails?.length ?? 0) > 0) {
        const groupMap = new Map<string, ControlNode>();
        for (const cd of imp.controlDetails ?? []) {
          const groupId = (cd.groupId || "UNGROUPED").toUpperCase();
          const groupTitle = cd.groupName || groupId;
          if (!groupMap.has(groupId)) {
            groupMap.set(groupId, {
              id: groupId,
              title: groupTitle,
              children: [],
            });
          }
          groupMap.get(groupId)!.children!.push({
            id: cd.controlId.toUpperCase(),
            title: cd.controlName,
            hasParams: cd.hasParameters ?? false,
          });
        }
        row.controls = Array.from(groupMap.values());
        row.rawControls = (imp.controlDetails ?? []).map(
          (cd) =>
            ({
              controlId: cd.controlId,
              title: cd.controlName,
              params: [],
              enhancements: [],
            }) as unknown as ControlResponse,
        );
      }
      rows.push(row);
    }

    setEditImportRows(rows);
    setEditImportRowsBackup(
      rows.map((r) => ({ ...r, checkedIds: new Set(r.checkedIds) })),
    );
    setEditActiveImportIdx(0);

    // Also set legacy single-source state for backward compat with existing edit UI
    if (rows.length > 0) {
      setEditSourceCatalogId(rows[0].sourceId);
      setEditCatalogControls(rows[0].controls);
      setEditRawControls(rows[0].rawControls);
    }

    setEditCatalogControlsLoading(false);

    // Pre-select a control if entering edit from a specific control context
    if (focusControl) {
      // Find which import row contains this control and set it active
      const focusId = focusControl.controlId.toUpperCase();
      for (let i = 0; i < rows.length; i++) {
        if (rows[i].checkedIds.has(focusId)) {
          setEditActiveImportIdx(i);
          setEditCatalogControls(rows[i].controls);
          setEditRawControls(rows[i].rawControls);
          setEditCheckedIds(rows[i].checkedIds);
          setEditSourceCatalogId(rows[i].sourceId);
          break;
        }
      }
      // Find the full control from rawControls to pre-select
      const allRaw = rows.flatMap((r) => r.rawControls);
      const ctrl = allRaw.find((c) => c.controlId.toUpperCase() === focusId);
      setEditSelectedControl(ctrl ?? focusControl);
      setViewTreeFilter("all");
    } else {
      setEditSelectedControl(null);
    }

    setEditWizardStep(0);
    setEditMode(true);
  };

  /** Cancel edit mode */
  const cancelEditMode = () => {
    setEditMode(false);
    setEditWizardStep(0);
    setEditSelectedControl(null);
    setEditTitle(editTitleBackup);
    setEditCheckedIds(new Set(editCheckedIdsBackup));
    setEditImportRows(
      editImportRowsBackup.map((r) => ({
        ...r,
        checkedIds: new Set(r.checkedIds),
      })),
    );
    setProfileParamOverrides({ ...profileParamOverridesBackup });
    setParamError(null);
    setErrorParamId(null);
  };

  /** Handle check/uncheck in edit mode control tree */
  const handleEditCheck = (id: string, checked: boolean) => {
    const activeRow = editImportRows[editActiveImportIdx];
    const treeToUse = activeRow ? activeRow.controls : editCatalogControls;
    const descendantIds = collectDescendantIds(treeToUse, id);

    // Update the active edit import row
    if (activeRow) {
      setEditImportRows((prev) =>
        prev.map((r, i) => {
          if (i !== editActiveImportIdx) return r;
          const next = new Set(r.checkedIds);
          if (checked) {
            next.add(id);
            descendantIds.forEach((d) => next.add(d));
          } else {
            next.delete(id);
            descendantIds.forEach((d) => next.delete(d));
          }
          return { ...r, checkedIds: next };
        }),
      );
    }

    // Also keep legacy editCheckedIds in sync
    setEditCheckedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
        descendantIds.forEach((d) => next.add(d));
      } else {
        next.delete(id);
        descendantIds.forEach((d) => next.delete(d));
      }
      return next;
    });
  };

  /** Save all profile changes via PATCH */
  const handleSaveAll = async () => {
    if (!selectedProfile) return;
    setSavingProfile(true);
    setParamError(null);
    setErrorParamId(null);

    try {
      const payload: import("../../api/frameworksApi").UpdateProfilePayload =
        {};

      if (editTitle.trim() && editTitle !== selectedProfile.title) {
        payload.title = editTitle.trim();
      }

      // Build imports from editImportRows (multi-source)
      if (editImportRows.length > 0) {
        const newImports = editImportRows
          .filter(
            (row) =>
              row.checkedIds.size > 0 && (!!row.sourceId || !!row.sourceHref),
          )
          .map((row, idx) => ({
            href: buildImportHref(row),
            sourceType: row.sourceType,
            includeControls: [
              { "with-ids": [...row.checkedIds].map((id) => id.toLowerCase()) },
            ],
            excludeControls: [] as { "with-ids": string[] }[],
            sortOrder: idx,
          }));
        // Check if controls changed compared to original
        const oldControlIds = selectedProfile.imports.flatMap(
          (imp) =>
            imp.includeControls?.flatMap((ic) => ic["with-ids"] ?? []) ?? [],
        );
        const newControlIds = newImports.flatMap(
          (imp) => imp.includeControls[0]["with-ids"],
        );
        const controlsChanged =
          newControlIds.length !== oldControlIds.length ||
          !newControlIds.every((id) => oldControlIds.includes(id)) ||
          newImports.length !== selectedProfile.imports.length;
        if (controlsChanged) {
          payload.imports = newImports;
        }
      } else {
        // Fallback: legacy single-source
        const newControlIds = [...editCheckedIds].map((id) => id.toLowerCase());
        const oldControlIds = selectedProfile.imports.flatMap(
          (imp) =>
            imp.includeControls?.flatMap((ic) => ic["with-ids"] ?? []) ?? [],
        );
        const controlsChanged =
          newControlIds.length !== oldControlIds.length ||
          !newControlIds.every((id) => oldControlIds.includes(id));
        if (controlsChanged) {
          payload.imports = [
            {
              href:
                editImportRows[0]?.sourceHref ??
                (editSourceCatalogId ? `#${editSourceCatalogId}` : ""),
              includeControls: [{ "with-ids": newControlIds }],
              excludeControls: [],
              sortOrder: 0,
            },
          ];
        }
      }

      // Include merge config if changed
      const origCombine = (selectedProfile.merge?.combineMethod ?? "keep") as
        | "use-first"
        | "keep";
      const origStructuring = (selectedProfile.merge?.structuringDirective ??
        "flat") as "flat" | "as-is" | "custom";
      const origCustom = selectedProfile.merge?.custom as {
        "insert-controls"?: { order?: string }[];
      } | null;
      const origCustomOrder = (origCustom?.["insert-controls"]?.[0]?.order ??
        "keep") as "keep" | "ascending" | "descending";
      if (
        editCombineMethod !== origCombine ||
        editStructuringDirective !== origStructuring ||
        (editStructuringDirective === "custom" &&
          editCustomOrder !== origCustomOrder)
      ) {
        payload.merge = {
          combineMethod: editCombineMethod,
          structuringDirective: editStructuringDirective,
          custom:
            editStructuringDirective === "custom"
              ? {
                "insert-controls": [
                  { order: editCustomOrder, "include-all": {} },
                ],
              }
              : null,
        };
      }

      const setParameters = Object.entries(profileParamOverrides)
        .filter(([, v]) => v.trim())
        .map(([paramId, v]) => ({
          "param-id": paramId,
          values: v
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean),
        }));
      if (setParameters.length > 0) {
        payload.modify = { setParameters };
      }

      await updateProfile(selectedProfile.id, payload);
      useToastStore.getState().showSuccess("Success", "Profile updated successfully");
      setEditMode(false);
      setEditWizardStep(0);

      const res = await getProfile(selectedProfile.id);
      setSelectedProfile(res.data);

      const profilesRes = await listProfiles();
      const rows = profilesRes.data.map(toProfileRow);
      setProfiles(rows);
      const updatedRow = rows.find((r) => r.id === selectedProfile.id);
      if (updatedRow) {
        setSelectedProfileRow(updatedRow);
        await fetchProfileDetail(updatedRow.id, {
          resolved: updatedRow.resolved,
          lifecycleStatus: updatedRow.lifecycleStatus,
        });
      }

      const savedModify = res.data.modify as {
        setParameters?: { "param-id": string; values: string[] }[];
      } | null;
      if (savedModify?.setParameters) {
        const overrides: Record<string, string> = {};
        for (const sp of savedModify.setParameters) {
          overrides[sp["param-id"]] = sp.values.join(", ");
        }
        setProfileParamOverrides(overrides);
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to save profile";
      setParamError(message);
      const paramMatch = message.match(/set-parameter '([^']+)'/);
      if (paramMatch) {
        setErrorParamId(paramMatch[1]);
      }
    } finally {
      setSavingProfile(false);
    }
  };

  /** Save current edit wizard step as draft, then advance */
  const handleEditWizardNext = async () => {
    if (!selectedProfile) return;
    setSavingProfile(true);
    try {
      if (editWizardStep === 0) {
        // Step 0: save controls (imports)
        const newImports = editImportRows
          .filter(
            (row) =>
              row.checkedIds.size > 0 && (!!row.sourceId || !!row.sourceHref),
          )
          .map((row, idx) => ({
            href: buildImportHref(row),
            sourceType: row.sourceType,
            includeControls: [
              { "with-ids": [...row.checkedIds].map((id) => id.toLowerCase()) },
            ],
            excludeControls: [] as { "with-ids": string[] }[],
            sortOrder: idx,
          }));
        await updateProfile(selectedProfile.id, { imports: newImports });
        setEditWizardStep(1);
      } else if (editWizardStep === 1) {
        // Step 1: save merge config
        await updateProfile(selectedProfile.id, {
          merge: {
            combineMethod: editCombineMethod,
            structuringDirective: editStructuringDirective,
            custom:
              editStructuringDirective === "custom"
                ? {
                  "insert-controls": [
                    { order: editCustomOrder, "include-all": {} },
                  ],
                }
                : null,
          },
        });
        setEditWizardStep(2);
        // Load available controls + params for Alters step
        setAvailableControlsLoading(true);
        setAvailableParamsLoading(true);
        setAvailableControls([]);
        setAvailableParams([]);
        try {
          const [ctrlsRes, paramsRes] = await Promise.all([
            getProfileAvailableControls(selectedProfile.id),
            getProfileAvailableParameters(selectedProfile.id),
          ]);
          // use-first: only show controls from the first import source
          let editCtrlsData = ctrlsRes.data ?? [];
          if (
            editCombineMethod === "use-first" &&
            selectedProfile.imports.length > 1
          ) {
            const firstSourceIds = new Set(
              selectedProfile.imports[0].includeControls?.flatMap(
                (ic) => ic["with-ids"] ?? [],
              ) ?? [],
            );
            editCtrlsData = editCtrlsData.filter((c) =>
              firstSourceIds.has(c.controlId.toLowerCase()),
            );
          }
          setAvailableControls(editCtrlsData);
          setAvailableParams(supplementParamClazz(paramsRes.data ?? [], editImportRows));
        } catch {
          setAvailableControls([]);
          setAvailableParams([]);
        } finally {
          setAvailableControlsLoading(false);
          setAvailableParamsLoading(false);
        }
      } else if (editWizardStep === 2) {
        // Step 2: draft-save alters
        const altersDraft = Object.entries(alterDirectives)
          .filter(([, dir]) => dir.adds.length > 0 || dir.removes.length > 0)
          .map(([controlId, dir]) => ({
            "control-id": controlId,
            adds: dir.adds.map((a) => ({
              ...(a.position ? { position: a.position } : {}),
              ...(a.byId ? { "by-id": a.byId } : {}),
              name: a.name,
              ...(a.title ? { title: a.title } : {}),
              prose: a.prose,
              ...(a.params && a.params.length > 0
                ? {
                  params: a.params
                    .filter((p) => p.paramId.trim())
                    .map((p) => ({
                      "param-id": p.paramId.trim(),
                      ...(p.label ? { label: p.label } : {}),
                      ...(p.clazz ? { class: p.clazz } : {}),
                      ...(p.usage ? { usage: p.usage } : {}),
                      ...(p.values
                        ? {
                          values: p.values
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean),
                        }
                        : {}),
                      ...(p.selections.length > 0
                        ? { select: { choice: p.selections.filter(Boolean) } }
                        : {}),
                      ...(p.constraints.length > 0
                        ? {
                          constraints: p.constraints
                            .filter(Boolean)
                            .map((c) => ({ description: c })),
                        }
                        : {}),
                      ...(p.guidelines.length > 0
                        ? {
                          guidelines: p.guidelines
                            .filter(Boolean)
                            .map((g) => ({ prose: g })),
                        }
                        : {}),
                    })),
                }
                : {}),
            })),
            removes: dir.removes.map((r) => ({
              ...(r.byId ? { "by-id": r.byId } : {}),
              ...(r.name ? { name: r.name } : {}),
            })),
          }));
        await updateProfile(selectedProfile.id, {
          modify: { setParameters: [], alters: altersDraft },
        });
        setEditWizardStep(3);
        // Refresh params for Parameters step
        setAvailableParamsLoading(true);
        setAvailableParams([]);
        try {
          const paramsRes = await getProfileAvailableParameters(
            selectedProfile.id,
          );
          setAvailableParams(supplementParamClazz(paramsRes.data ?? [], editImportRows));
        } catch {
          setAvailableParams([]);
        } finally {
          setAvailableParamsLoading(false);
        }
      } else if (editWizardStep === 3) {
        // Step 3 (final): save complete modify (params + alters)
        await handleSaveAll();
        setEditWizardStep(0);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save";
      useToastStore.getState().showError("Error", msg);
    } finally {
      setSavingProfile(false);
    }
  };


  useEffect(() => {
    if (error) useToastStore.getState().showError("Error", error);
  }, [error]);
  useEffect(() => {
    if (importError) useToastStore.getState().showError(importErrorTitle || "Error", importError);
  }, [importError, importErrorTitle]);
  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  /* ── Load profile from URL parameter on mount, or clear when URL is removed ── */
  useEffect(() => {
    if (!profileIdFromUrl) {
      setSelectedProfile(null);
      setSelectedProfileRow(null);
      return;
    }

    let cancelled = false;

    const loadProfileFromUrl = async () => {
      const existingProfile = profiles.find((p) => p.id === profileIdFromUrl);
      if (existingProfile && !cancelled) {
        setSelectedProfileRow(existingProfile);
        fetchProfileDetail(profileIdFromUrl, {
          resolved: existingProfile.resolved,
          lifecycleStatus: existingProfile.lifecycleStatus,
        });
      } else if (!cancelled) {
        // Directly fetch if not in list yet
        setSelectedProfileRow({
          id: profileIdFromUrl,
          documentId: "",
          name: "Loading",
          title: "Loading",
          oscalVersion: "",
          importCount: 0,
          controlCount: 0,
          groupCount: 0,
          status: "draft",
          lifecycleStatus: "draft",
          resolved: false,
          version: 0,
          updated: "",
          created: "",
        } as ProfileRow);
        fetchProfileDetail(profileIdFromUrl, {
          resolved: false,
          lifecycleStatus: "draft",
        });
      }
    };

    loadProfileFromUrl();
    return () => {
      cancelled = true;
    };
  }, [profileIdFromUrl, profiles, fetchProfileDetail]);

  /** Convert a GitHub blob URL to its raw content URL */
  const toRawUrl = (url: string): string => {
    const ghMatch = url.match(
      /^https?:\/\/github\.com\/([^/]+\/[^/]+)\/blob\/(.+)$/,
    );
    if (ghMatch)
      return `https://raw.githubusercontent.com/${ghMatch[1]}/${ghMatch[2]}`;
    return url;
  };

  /** Check whether URL points to an allowed file type */
  const isAllowedUrl = (url: string): boolean =>
    /\.(json|yaml|yml|xml)(\?.*)?$/i.test(url.split("#")[0]);

  /** Fetch file from URL, then validate it */
  const fetchUrl = async () => {
    if (!importUrl.trim()) return;
    if (!isAllowedUrl(importUrl.trim())) {
      setImportError("URL must point to a .json, .yaml, or .xml file");
      return;
    }
    setValidating(true);
    setImportError(null);
    setValidateResult(null);
    setImportFile(null);

    try {
      const rawUrl = toRawUrl(importUrl.trim());
      const response = await fetch(rawUrl);
      if (!response.ok)
        throw new Error(
          `Failed to fetch: ${response.status} ${response.statusText}`,
        );
      const blob = await response.blob();
      const filename = importUrl.split("/").pop() || "document";
      const file = new File([blob], filename, {
        type: blob.type || "application/octet-stream",
      });
      setImportFile(file);

      const res = await validateImport(file);
      if (res.data.documentType.toLowerCase() !== "profile") {
        setImportError(
          `Expected a profile document but got "${res.data.documentType}"`,
        );
      } else {
        setValidateResult(res.data);
        // Findings are shown inline in the validation results, not as toasts
      }
    } catch (err: unknown) {
      setImportErrorTitle(err instanceof Error && err.name !== "Error" ? err.name : "Import Failed");
      setImportError(err instanceof Error ? err.message : "Failed to fetch");
    } finally {
      setValidating(false);
    }
  };

  /** Step 2: Actual import after validation passes */
  const runImport = async () => {
    if (!importFile) return;
    setImportStep("importing");
    setImportError(null);
    try {
      const res = await importCatalog(importFile, importMode, importOrgId || undefined);
      setImportResult(res.data);
      setShowImport(false);
      openSessionModal(res.data.id);
    } catch (err: unknown) {
      useToastStore.getState().showError(
        err instanceof Error && err.name !== "Error" ? err.name : "Import Failed",
        err instanceof Error ? err.message : "An unexpected error occurred",
      );
      setImportStep("validated");
    }
  };

  /** Check session findings for ERROR severity and show alert if found */
  const checkSessionForErrors = (session: ImportSessionResponse) => {
    const errorFinding = session.findings?.find((f) => f.severity === "error");
    if (errorFinding) {
      setShowSession(false);
      setSessionData(null);
      setImportErrorTitle(errorFinding.title || "Validation Error");
      setImportError(errorFinding.detail || errorFinding.title || "Import validation failed");
      setShowImport(true);
      setImportStep("findings");
      return true;
    }
    return false;
  };

  /** Open session modal, fetch session, and start polling */
  const openSessionModal = async (sessionId: string) => {
    setShowSession(true);
    setSessionData(null);
    setSessionError(null);
    setSessionLoading(true);
    setCommitting(false);
    setAborting(false);
    try {
      const res = await getImportSession(sessionId);
      if (checkSessionForErrors(res.data)) return;
      setSessionData(res.data);
    } catch (err: unknown) {
      setSessionError(
        err instanceof Error ? err.message : "Failed to fetch session",
      );
    } finally {
      setSessionLoading(false);
    }
  };

  /** Poll session every 10s until status is validated/failed/committed */
  useEffect(() => {
    if (!showSession || !sessionData) return;
    const status = sessionData.jobStatus.toLowerCase();
    if (status === "validated" || status === "failed" || status === "committed")
      return;
    const timer = setInterval(async () => {
      try {
        const res = await getImportSession(sessionData.id);
        if (checkSessionForErrors(res.data)) return;
        setSessionData(res.data);
      } catch {
        // silently ignore polling errors
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [showSession, sessionData]);

  /** Commit the import session */
  const handleCommitSession = async () => {
    if (!sessionData) return;
    setCommitting(true);
    setSessionError(null);
    try {
      await commitImportSession(sessionData.id, importOrgId || undefined);
      fetchProfiles();
      useToastStore.getState().showSuccess("Success", "Profile Imported Successfully");
      setShowSession(false);
      setSessionData(null);
      resetImport();
    } catch (err: unknown) {
      setSessionError(err instanceof Error ? err.message : "Commit failed");
    } finally {
      setCommitting(false);
    }
  };

  /** Abort the import session */
  const handleAbortSession = async () => {
    if (!sessionData) return;
    setAborting(true);
    setSessionError(null);
    try {
      await abortImportSession(sessionData.id);
      setShowSession(false);
      setSessionData(null);
      resetImport();
      fetchProfiles();
    } catch (err: unknown) {
      setSessionError(err instanceof Error ? err.message : "Abort failed");
    } finally {
      setAborting(false);
    }
  };

  const resetImport = () => {
    setImportStep("select");
    setImportFile(null);
    setImportUrl("");
    setImportMode("full");
    setImportError(null);
    setImportErrorTitle(null);
    setImportResult(null);
    setValidateResult(null);
    setValidating(false);
    setImportOrgId("");
  };

  /** Handle check in builder (multi-source) - updates the active import row */
  const handleCheck = (id: string, checked: boolean) => {
    const row = importRows[activeImportIdx];
    if (!row) return;
    const descendantIds = collectDescendantIds(row.controls, id);
    setImportRows((prev) =>
      prev.map((r, i) => {
        if (i !== activeImportIdx) return r;
        const next = new Set(r.checkedIds);
        if (checked) {
          next.add(id);
          descendantIds.forEach((d) => next.add(d));
        } else {
          next.delete(id);
          descendantIds.forEach((d) => next.delete(d));
        }
        return { ...r, checkedIds: next };
      }),
    );
  };

  /* ════════════════════════════════════════════════════════
     Profile Detail View
  ════════════════════════════════════════════════════════ */
  if (detailLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 animate-pulse">
          <div className="h-3.5 w-16 rounded bg-muted" />
          <div className="h-3.5 w-3 rounded bg-muted" />
          <div className="h-3.5 w-32 rounded bg-muted" />
        </div>
        <SkeletonCard lines={4} />
        <SkeletonCard lines={3} />
      </div>
    );
  }

  /* ── Shared lifecycle transition confirmation modal ──
     Rendered in every profile view branch (edit/non-resolved and resolved)
     so the confirmation popup shows regardless of which view is active. */
  const transitionModal = selectedProfileRow && (
    <Modal
      open={showTransition}
      onClose={() => setShowTransition(false)}
      title={`${transitionLabel}`}
      size="sm"
      footer={
        <ModalFooterButtons
          onCancel={() => setShowTransition(false)}
          onSubmit={handleTransition}
          submitLabel={
            transitioning
              ? "Processing…"
              : transitionLabel === "Submit for Review"
                ? "Submit for Review"
                : `Submit for ${transitionLabel}`
          }
          submitDisabled={
            transitioning ||
            (transitionRequireComments && !transitionComments.trim())
          }
        />
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-foreground">
          You are about to change the status of{" "}
          <span className="font-semibold text-foreground">
            "{selectedProfile?.title ?? selectedProfileRow.name}"
          </span>{" "}
          to{" "}
          <span className="font-semibold text-foreground capitalize">
            {transitionTarget.replace(/_/g, " ")}
          </span>
          .
        </p>

        {/* Warning for destructive transitions */}
        {lifecycleActions[selectedProfileRow.lifecycleStatus]?.find(
          (a) => a.target === transitionTarget,
        )?.warning && (
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-sm text-amber-700 dark:text-amber-400">
            <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
            <span className="text-xs">
              {
                lifecycleActions[selectedProfileRow.lifecycleStatus]?.find(
                  (a) => a.target === transitionTarget,
                )?.warning
              }
            </span>
          </div>
        )}

        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
            <MessageSquare className="size-3.5" />
            Comments{" "}
            {transitionRequireComments ? (
              <span className="text-red-500">*</span>
            ) : (
              <span className="text-muted-foreground text-xs">(optional)</span>
            )}
          </label>
          <textarea
            placeholder={
              transitionRequireComments
                ? "Please provide a reason…"
                : "Add a comment…"
            }
            value={transitionComments}
            onChange={(e: { target: { value: string } }) =>
              setTransitionComments(e.target.value)
            }
            rows={3}
            className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 resize-none leading-relaxed text-[#0f172a] dark:text-white"
          />
        </div>
      </div>
    </Modal>
  );

  /* ── Helper: highlight resolved param values inside prose ── */
  /* ── Resolved Profile loading state (resolved-catalog API only) ── */
  if (selectedProfileRow?.resolved && !selectedProfile && detailLoading) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              navigate("/profiles");
              setSelectedProfile(null);
              setSelectedProfileRow(null);
              setResolvedControls([]);
              setResolvedAt(null);
            }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Profiles
          </button>
          <ChevronRight className="size-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">{selectedProfileRow.name}</span>
        </div>

        {/* Profile metadata from row */}
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="flex items-start justify-between">
            <div>
              {editingNameId === selectedProfileRow.id ? (
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    value={editingNameValue}
                    onChange={(e) => setEditingNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleRenameProfile(
                          selectedProfileRow.id,
                          selectedProfileRow.documentId,
                        );
                      if (e.key === "Escape") setEditingNameId(null);
                    }}
                    style={{
                      width: `${Math.max(editingNameValue.length + 2, 20)}ch`,
                    }}
                    className="text-lg font-semibold px-2 py-1 rounded border border-indigo-400 bg-card outline-none focus:ring-1 focus:ring-indigo-500"
                    disabled={savingName}
                  />
                  <Tip label="Save">
                    <button
                      onClick={() =>
                        handleRenameProfile(
                          selectedProfileRow.id,
                          selectedProfileRow.documentId,
                        )
                      }
                      disabled={savingName || !editingNameValue.trim()}
                      className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-500/10 text-green-600 dark:text-green-400 transition-colors disabled:opacity-40"
                    >
                      {savingName ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Check className="size-4" />
                      )}
                    </button>
                  </Tip>
                  <Tip label="Cancel">
                    <button
                      onClick={() => setEditingNameId(null)}
                      disabled={savingName}
                      className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600 dark:text-red-400 transition-colors disabled:opacity-40"
                    >
                      <X className="size-4" />
                    </button>
                  </Tip>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">
                    {selectedProfileRow.name}
                  </h2>
                  <Tip label="Rename profile">
                    <button
                      onClick={() => {
                        setEditingNameId(selectedProfileRow.id);
                        setEditingNameValue(selectedProfileRow.name);
                      }}
                      className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Pencil className="size-3.5" />
                    </button>
                  </Tip>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-muted-foreground">
                <span>
                  OSCAL Version:{" "}
                  <span className="font-mono font-medium text-foreground">
                    {selectedProfileRow.oscalVersion}
                  </span>
                </span>
                <span>
                  Version:{" "}
                  <span className="font-mono font-medium text-foreground">
                    {formatVersion(selectedProfileRow.version)}
                  </span>
                </span>
                <span>
                  Updated:{" "}
                  <span className="font-medium text-foreground">
                    {selectedProfileRow.updated}
                  </span>
                </span>
                <span>
                  Status:{" "}
                  <StatusBadge
                    status={
                      selectedProfileRow.lifecycleStatus as Parameters<
                        typeof StatusBadge
                      >[0]["status"]
                    }
                  />
                </span>
                <span>
                  Resolved: <StatusBadge status="pass" label="Yes" />
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Resolved Catalog */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <ShieldCheck className="size-4 text-indigo-500" />
                Resolved Profile
                {resolvedControls.length > 0 && (
                  <span className="text-xs font-normal text-muted-foreground">
                    ({resolvedControls.length} controls)
                  </span>
                )}
                <ResolvedMethodBadge method={resolvedMethod} order={null} />
              </h3>
              <ResolvedGroupToolbar
                groupIds={getResolvedGroupIds(resolvedControls, resolvedMethod)}
                collapsedGroups={resolvedCollapsedGroups}
                onExpandAll={() => setResolvedCollapsedGroups(new Set())}
                onCollapseAll={(ids) =>
                  setResolvedCollapsedGroups(new Set(ids))
                }
              />
            </div>
            <div className="flex items-center gap-3">
              {resolvedControls.length > 0 && (
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    value={resolvedSearch}
                    onChange={(e) => setResolvedSearch(e.target.value)}
                    placeholder="Search controls…"
                    className="h-8 w-56 rounded-md border border-border bg-background pl-8 pr-7 text-xs outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                  />
                  {resolvedSearch && (
                    <button
                      type="button"
                      onClick={() => setResolvedSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>
              )}
              {resolvedAt && (
                <span className="text-xs text-muted-foreground">
                  Resolved at:{" "}
                  <span className="font-medium text-foreground">
                    {resolvedAt.split("T")[0].split("-").reverse().join("/")}
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Show-controls-with-parameters toggle (matches Catalog/Profile) */}
          {resolvedParamCount > 0 && (
            <label className="flex items-center gap-2 cursor-pointer w-fit">
              <Switch
                checked={resolvedParamsOnly}
                onCheckedChange={(checked) => setResolvedParamsOnly(checked)}
              />
              <span className="text-[11px] font-medium text-muted-foreground select-none">
                Show Controls with Parameters ({resolvedParamCount})
              </span>
            </label>
          )}

          {resolvedLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="size-4 animate-spin" /> Loading resolved
              catalog…
            </div>
          ) : resolvedControls.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No resolved controls found.
            </p>
          ) : filteredResolvedControls.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {resolvedSearch
                ? `No controls match “${resolvedSearch}”.`
                : "No controls with parameters found."}
            </p>
          ) : (
            <ResolvedCatalogControls
              controls={filteredResolvedControls}
              method={resolvedMethod}
              collapsedGroups={resolvedCollapsedGroups}
              onToggleGroup={toggleResolvedGroup}
            />
          )}
        </div>
      </div>
    );
  }

  if (selectedProfile && (!selectedProfileRow?.resolved || editMode)) {
    const allIncludedIds = selectedProfile.imports.flatMap(
      (imp) => imp.includeControls?.flatMap((ic) => ic["with-ids"] ?? []) ?? [],
    );
    const allExcludedIds = selectedProfile.imports.flatMap(
      (imp) => imp.excludeControls?.flatMap((ec) => ec["with-ids"] ?? []) ?? [],
    );

    // Build a lookup map from controlDetails (nested inside each import)
    // controlCatalogMap: CATALOG sources only (href → catalog UUID)
    // resolvedControlSourceMap (state): PROFILE sources, built async via getResolvedCatalog
    const controlDetailsMap = new Map<string, ProfileControlDetail>();
    const controlCatalogMap = new Map<string, string>();
    for (const imp of selectedProfile.imports ?? []) {
      // Populate controlDetailsMap for all sources (for controlName display)
      for (const cd of imp.controlDetails ?? []) {
        if (!controlDetailsMap.has(cd.controlId.toLowerCase())) {
          controlDetailsMap.set(cd.controlId.toLowerCase(), cd);
        }
      }
      if (imp.sourceType === "PROFILE") continue; // catalog ID for profile controls comes from resolvedControlSourceMap
      const catalogId = imp.href.startsWith("#") ? imp.href.slice(1) : imp.href;
      for (const cd of imp.controlDetails ?? []) {
        controlCatalogMap.set(cd.controlId.toLowerCase(), catalogId);
      }
      for (const ic of imp.includeControls ?? []) {
        for (const cid of ic["with-ids"] ?? []) {
          if (!controlCatalogMap.has(cid.toLowerCase())) {
            controlCatalogMap.set(cid.toLowerCase(), catalogId);
          }
        }
      }
    }

    // Seed control metadata from the fetched catalog tree for profiles whose
    // controlDetails was empty (manually created profiles). This supplies the
    // control title, group id/name and parameter flag the same way backend-
    // provided controlDetails would.
    for (const [cid, meta] of viewCatalogControlMap) {
      const existing = controlDetailsMap.get(cid);
      if (!existing) {
        controlDetailsMap.set(cid, {
          controlId: meta.control.controlId,
          controlName: meta.control.title,
          groupId: meta.groupId,
          groupName: meta.groupName,
          hasParameters: (meta.control.params?.length ?? 0) > 0,
        });
      } else if (!(existing.groupId ?? "").trim()) {
        // controlDetails came back with a title but no group — enrich it with
        // the catalog's real group so the accordion can be built (e.g. CIS).
        controlDetailsMap.set(cid, {
          ...existing,
          controlName: existing.controlName || meta.control.title,
          groupId: meta.groupId,
          groupName: meta.groupName,
        });
      }
    }

    // Build param ID set:
    // - CATALOG sources: controlDetails.hasParameters is populated → use it directly
    // - PROFILE sources: backend does not populate hasParameters in controlDetails,
    //   so fall back to viewParamControls (populated via getResolvedCatalog in fetchProfileDetail)
    const viewParamIdSet = new Set<string>();
    for (const [cid, detail] of controlDetailsMap) {
      if (detail.hasParameters) viewParamIdSet.add(cid.toUpperCase());
    }
    for (const vpc of viewParamControls) {
      viewParamIdSet.add(vpc.controlId.toUpperCase());
    }

    type IncludedGroup = {
      groupId: string;
      groupName: string;
      controls: { controlId: string; controlName: string }[];
    };

    /**
     * Build the grouped accordion structure for a list of included control IDs.
     */
    // True only when this profile has NO group metadata anywhere (neither in
    // controlDetails nor in the fetched catalog). Catalogs like CIS encode the
    // hierarchy in the control IDs themselves (CISC-1 → CISC-1.1) rather than in
    // OSCAL groups, so for those we derive the accordion from the IDs. This is
    // gated so backend-grouped profiles (whose stray group-level IDs must stay
    // in the hidden "other" bucket) are never affected.
    const allowHierarchyDerive = ![...controlDetailsMap.values()].some(
      (d) => (d.groupId ?? "").trim() !== "",
    );

    const buildIncludedGroups = (tabIncludedIds: string[]): IncludedGroup[] => {
      // Families that have dotted sub-controls in this set (CISC-1 has CISC-1.1).
      const familiesWithChildren = new Set<string>();
      if (allowHierarchyDerive) {
        for (const cid of tabIncludedIds) {
          const family = cid.split(".")[0];
          if (family.toLowerCase() !== cid.toLowerCase()) {
            familiesWithChildren.add(family.toLowerCase());
          }
        }
      }

      const grouped = new Map<string, IncludedGroup>();
      const ensureGroup = (groupId: string, groupName: string) => {
        const key = groupId.toLowerCase();
        if (!grouped.has(key)) {
          grouped.set(key, { groupId, groupName, controls: [] });
        }
        return grouped.get(key)!;
      };
      for (const cid of tabIncludedIds) {
        const lower = cid.toLowerCase();
        // Skip included IDs that reference a catalog group, not a control —
        // the group already appears as an accordion header, so listing it as a
        // member would duplicate the group title inside its own section.
        if (viewCatalogGroupIds.has(lower)) continue;

        const detail = controlDetailsMap.get(lower);
        const backendGroupId = detail?.groupId?.trim();
        if (backendGroupId) {
          ensureGroup(
            backendGroupId,
            detail?.groupName?.trim() || backendGroupId,
          ).controls.push({
            controlId: cid,
            controlName: detail?.controlName ?? cid.toUpperCase(),
          });
        } else if (allowHierarchyDerive && familiesWithChildren.size > 0) {
          // Derive group from the control-ID hierarchy (e.g. CIS).
          const family = cid.split(".")[0];
          const familyLower = family.toLowerCase();
          const isFamilyHead = familyLower === lower;
          const familyDetail = controlDetailsMap.get(familyLower);
          const familyName =
            familyDetail?.controlName?.trim() || family.toUpperCase();
          if (isFamilyHead && familiesWithChildren.has(familyLower)) {
            // Section parent — use it as the group header only, not also a member.
            ensureGroup(family, familyName);
          } else {
            ensureGroup(family, familyName).controls.push({
              controlId: cid,
              controlName: detail?.controlName ?? cid.toUpperCase(),
            });
          }
        } else {
          ensureGroup("other", "Other").controls.push({
            controlId: cid,
            controlName: detail?.controlName ?? cid.toUpperCase(),
          });
        }
      }

      const groupedValues = Array.from(grouped.values());
      let groups = groupedValues.filter(
        (g) => g.groupId.toLowerCase() !== "other",
      );
      if (groups.length === 0) {
        groups = groupedValues;
      }
      return groups;
    };

    // Expand/collapse availability follows the ControlTreeView model: "Expand
    // All" is disabled once everything is expanded, "Collapse All" until it is.
    const profileExpandAllDisabled = viewTreeExpandAll === true;
    const profileCollapseAllDisabled = viewTreeExpandAll !== true;

    /** Fetch full control detail from catalog API.
     *  - Catalog source: controlCatalogMap (href → catalog UUID)
     *  - Profile source: resolvedControlSourceMap (source_catalog_id from resolved-catalog API)
     */
    const fetchControlDetail = async (controlId: string) => {
      const key = controlId.toLowerCase();
      // Fast path: the control was already loaded from the catalog tree (manual
      // profiles with empty controlDetails) — its parts/params are in hand, so
      // render directly without another round trip.
      const cached = viewCatalogControlMap.get(key);
      if (cached) {
        setViewControlLoading(false);
        setViewControlDetail(cached.control);
        return;
      }
      // For catalog tab with multiple imports: resolve the active import's
      // catalog id. Prefer the explicit catalogId / sourceId over href (which
      // may be an OSCAL document ref that differs from the catalog UUID) — same
      // precedence used when fetching the catalog tree above.
      const activeImpAny =
        selectedProfile.imports.length > 1
          ? (selectedProfile.imports as any[])[viewIncludedImportIdx]
          : null;
      const activeCatalogId =
        activeImpAny && (activeImpAny.sourceType ?? "CATALOG") === "CATALOG"
          ? activeImpAny.catalogId ??
            activeImpAny.sourceId ??
            (activeImpAny.href?.startsWith("#")
              ? activeImpAny.href.slice(1)
              : activeImpAny.href)
          : null;
      const catalogId =
        activeCatalogId ??
        controlCatalogMap.get(key) ??
        resolvedControlSourceMap.get(key);

      // Fallback: when the source catalog can't be fetched directly (deleted /
      // 404) the control still lives in the profile's resolved catalog, which
      // carries the merged parts/params. Fetch it on demand if not already held.
      const loadFromResolved = async (): Promise<boolean> => {
        try {
          let pool = resolvedControls;
          if (!pool || pool.length === 0) {
            const rRes = await getResolvedCatalog(selectedProfile.id);
            pool = rRes.data.controls ?? [];
            setResolvedControls(pool);
          }
          const rc = pool.find((c) => c.control_id?.toLowerCase() === key);
          if (!rc) return false;
          setViewControlDetail(resolvedToControlDetail(rc));
          return true;
        } catch {
          return false;
        }
      };

      setViewControlLoading(true);
      setViewControlDetail(null);
      try {
        if (!catalogId) {
          if (!(await loadFromResolved())) setViewControlDetail(null);
          return;
        }
        try {
          const res = await getControl(catalogId, controlId.toLowerCase());
          setViewControlDetail(res.data);
        } catch {
          if (!(await loadFromResolved())) setViewControlDetail(null);
        }
      } finally {
        setViewControlLoading(false);
      }
    };

    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              navigate("/profiles");
              setSelectedProfile(null);
              setSelectedProfileRow(null);
            }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Profiles
          </button>
          <ChevronRight className="size-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">{selectedProfile.title}</span>
          {(() => {
            const lifecycle =
              selectedProfileRow?.lifecycleStatus ??
              selectedProfile.lifecycleStatus;
            const steps = [
              "draft",
              "in_review",
              "approved",
              "published",
            ] as const;
            const stepLabels: Record<string, string> = {
              draft: "Draft",
              in_review: "In Review",
              approved: "Approved",
              published: "Published",
            };
            const terminalStates: Record<
              string,
              { label: string; color: string }
            > = {
              rejected: { label: "Rejected", color: "bg-red-500" },
              superseded: { label: "Superseded", color: "bg-orange-500" },
              withdrawn: { label: "Withdrawn", color: "bg-rose-700" },
            };
            const terminal = terminalStates[lifecycle];
            const activeIdx = steps.indexOf(
              lifecycle as (typeof steps)[number],
            );
            return (
              <div className="ml-3 flex items-center gap-1">
                {terminal ? (
                  <>
                    {steps.map((s, i) => {
                      const isRejectedFromReview = lifecycle === "rejected";
                      const filledUpTo = isRejectedFromReview
                        ? 1
                        : steps.length;
                      const filled = i < filledUpTo;
                      return (
                        <React.Fragment key={s}>
                          <div
                            className={`flex items-center gap-1 ${filled ? "text-muted-foreground" : "text-muted-foreground/40"}`}
                          >
                            <div
                              className={`size-2 rounded-full ${filled ? "bg-muted-foreground/50" : "bg-muted-foreground/20"}`}
                            />
                            <span className="text-[10px] font-medium">
                              {stepLabels[s]}
                            </span>
                          </div>
                          {i < steps.length - 1 && (
                            <div
                              className={`w-4 h-px ${filled && i < filledUpTo - 1 ? "bg-muted-foreground/40" : "bg-muted-foreground/15"}`}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                    <div className="w-4 h-px bg-muted-foreground/15" />
                    <div className="flex items-center gap-1">
                      <div
                        className={`size-2 rounded-full ${terminal.color}`}
                      />
                      <span
                        className={`text-[10px] font-bold ${lifecycle === "rejected" ? "text-red-600 dark:text-red-400" : lifecycle === "superseded" ? "text-orange-600 dark:text-orange-400" : "text-rose-700 dark:text-rose-400"}`}
                      >
                        {terminal.label}
                      </span>
                    </div>
                  </>
                ) : (
                  steps.map((s, i) => {
                    const isCurrent = s === lifecycle;
                    const isPast = activeIdx >= 0 && i < activeIdx;
                    return (
                      <React.Fragment key={s}>
                        <div
                          className={`flex items-center gap-1 ${isCurrent && s === "published" ? "text-emerald-600 dark:text-emerald-400" : isCurrent ? "text-foreground" : isPast ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/60 dark:text-white"}`}
                        >
                          <div
                            className={`size-2 rounded-full ${isCurrent && s === "published" ? "bg-emerald-500 ring-2 ring-emerald-500/30" : isCurrent ? "bg-blue-500 ring-2 ring-blue-500/30" : isPast ? "bg-emerald-500" : "bg-muted-foreground/30 dark:bg-white/20"}`}
                          />
                          <span
                            className={`text-[10px] ${isCurrent ? "font-bold" : "font-medium"}`}
                          >
                            {stepLabels[s]}
                          </span>
                        </div>
                        {i < steps.length - 1 && (
                          <div
                            className={`w-4 h-px ${isPast ? "bg-emerald-500/50" : "bg-muted-foreground/20 dark:bg-white/15"}`}
                          />
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </div>
            );
          })()}
        </div>

        {/* Profile overview */}
        <div className="rounded-2xl border border-border bg-gradient-to-br from-card via-card to-indigo-50/30 dark:to-indigo-500/[0.03] overflow-hidden shadow-sm">
          <div className="p-4 space-y-3.5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              {editingNameId === selectedProfile.id ? (
                <div className="flex items-center gap-2">
                  <input
                    autoFocus
                    value={editingNameValue}
                    onChange={(e) => setEditingNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter")
                        handleRenameProfile(
                          selectedProfile.id,
                          selectedProfile.documentId,
                        );
                      if (e.key === "Escape") setEditingNameId(null);
                    }}
                    style={{
                      width: `${Math.max(editingNameValue.length + 2, 20)}ch`,
                    }}
                    className="text-lg font-semibold px-2 py-1 rounded border border-indigo-400 bg-card outline-none focus:ring-1 focus:ring-indigo-500"
                    disabled={savingName}
                  />
                  <Tip label="Save">
                    <button
                      onClick={() =>
                        handleRenameProfile(
                          selectedProfile.id,
                          selectedProfile.documentId,
                        )
                      }
                      disabled={savingName || !editingNameValue.trim()}
                      className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-500/10 text-green-600 dark:text-green-400 transition-colors disabled:opacity-40"
                    >
                      {savingName ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <Check className="size-4" />
                      )}
                    </button>
                  </Tip>
                  <Tip label="Cancel">
                    <button
                      onClick={() => setEditingNameId(null)}
                      disabled={savingName}
                      className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600 dark:text-red-400 transition-colors disabled:opacity-40"
                    >
                      <X className="size-4" />
                    </button>
                  </Tip>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-semibold">
                    {editMode ? editTitle : selectedProfile.title}
                  </h2>
                  {isEditable && !editMode && (
                    <Tip label="Rename profile">
                      <button
                        onClick={() => {
                          setEditingNameId(selectedProfile.id);
                          setEditingNameValue(selectedProfile.title);
                        }}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Pencil className="size-3.5" />
                      </button>
                    </Tip>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {/* Edit button */}
              {isEditable && !editMode && (
                <button
                  onClick={() => enterEditMode(viewSelectedControl)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors"
                >
                  <Pencil className="size-3.5" /> Edit
                </button>
              )}
              {/* Lifecycle transition buttons */}
              {currentActions.map((action) => {
                const button = (
                  <Tip
                    key={action.target}
                    label={profileIsEmpty ? EMPTY_PROFILE_MSG : ""}
                    contentClassName="max-w-xs break-words"
                  >
                    <button
                      onClick={() =>
                        openTransitionModal(
                          action.target,
                          action.label,
                          action.requireComments,
                        )
                      }
                      disabled={profileIsEmpty}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-sm font-medium transition-colors shadow-md whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${action.color}`}
                    >
                      {action.icon}
                      {action.label}
                    </button>
                  </Tip>
                );

                // Determine required permission based on action label
                if (["Approve", "Reject"].includes(action.label)) {
                  return (
                    <RBACAction key={action.target} module="profile" action="approve">
                      {button}
                    </RBACAction>
                  );
                }
                if (action.label === "Publish") {
                  return (
                    <RBACAction key={action.target} module="profile" action="publish">
                      {button}
                    </RBACAction>
                  );
                }
                if (action.label === "Submit for Review") {
                  return (
                    <RBACAction key={action.target} module="profile" action="update">
                      {button}
                    </RBACAction>
                  );
                }
                if (["Supersede", "Withdraw"].includes(action.label)) {
                  return (
                    <RBACAction key={action.target} module="profile" action="update">
                      {button}
                    </RBACAction>
                  );
                }
                return button;
              })}
              {/* Resolve button - available at any stage if not already resolved */}
              {!editMode && !selectedProfileRow?.resolved && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleResolveProfile}
                      disabled={resolving || profileIsEmpty}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-sm font-medium transition-colors shadow-md bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resolving ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <PlayCircle className="size-4" />
                      )}
                      {resolving ? "Resolving…" : "Resolve"}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={6}>
                    {profileIsEmpty
                      ? EMPTY_PROFILE_MSG
                      : "You can resolve this profile at any stage"}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>

          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <ProfStatCard
              icon={<Info className="size-4" />}
              label="OSCAL Version"
              value={selectedProfile.oscalVersion}
              color="sky"
            />
            <ProfStatCard
              icon={<FileText className="size-4" />}
              label="Version"
              value={formatVersion(selectedProfile.versionNumber)}
              color="violet"
            />
            <ProfStatCard
              icon={<Clock className="size-4" />}
              label="Created On"
              value={selectedProfile.createdAt
                .split("T")[0]
                .split("-")
                .reverse()
                .join("/")}
              color="emerald"
            />
            <ProfStatCard
              icon={<Clock className="size-4" />}
              label="Updated On"
              value={selectedProfile.updatedAt
                .split("T")[0]
                .split("-")
                .reverse()
                .join("/")}
              color="amber"
            />
            <ProfStatCard
              icon={<ShieldCheck className="size-4" />}
              label="Status"
              value={(
                selectedProfileRow?.lifecycleStatus ??
                selectedProfile.lifecycleStatus ??
                ""
              ).replace(/_/g, " ")}
              color={profStatusColor(
                selectedProfileRow?.lifecycleStatus ??
                  selectedProfile.lifecycleStatus,
              )}
              valueClassName="capitalize"
            />
          </div>

          {/* Import sources badges */}
          {selectedProfile.imports.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">Sources:</span>
              {selectedProfile.imports.map((imp, idx) => (
                <span
                  key={imp.id ?? idx}
                  className="inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border border-border bg-muted/50"
                >
                  <span
                    className={`inline-block size-1.5 rounded-full ${imp.sourceType === "PROFILE" ? "bg-purple-500" : "bg-indigo-500"}`}
                  />
                  <span className="font-medium">
                    {imp.sourceTitle ??
                      (imp.sourceType === "PROFILE" ? "Profile" : "Catalog")}
                  </span>
                  <span className="text-[10px] text-muted-foreground">
                    ({imp.sourceType === "PROFILE" ? "Profile" : "Catalog"})
                  </span>
                  <span className="font-mono text-[10px] px-1.5 py-px rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300">
                    {imp.controlDetails?.length ??
                      imp.includeControls?.reduce(
                        (sum, ic) => sum + (ic["with-ids"]?.length ?? 0),
                        0,
                      ) ??
                      0}{" "}
                    controls
                  </span>
                </span>
              ))}
            </div>
          )}

          {/* Rejection notice */}
          {(selectedProfileRow?.lifecycleStatus ??
            selectedProfile.lifecycleStatus) === "rejected" && (
              <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
                <XCircle className="size-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-semibold">This profile was rejected</p>
                  <p className="text-xs mt-0.5">
                    You can edit the profile and submit it for review again.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Edit Wizard Stepper — only visible in edit mode */}
        {editMode && (
          <div className="rounded-lg border border-border bg-card p-5">
            <WorkflowStepper
              steps={editWizardSteps}
              currentStep={editWizardStep}
            />
          </div>
        )}

        {/* Merge Configuration — visible when not editing, or in edit mode on step 1 */}
        {(selectedProfile.merge || isEditable) &&
          (!editMode || editWizardStep === 1) && (
            <div className="rounded-lg border border-border bg-card p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Merge Configuration</h3>
                {!editMode && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium tracking-wider">
                    {isEditable ? "Click Edit to change" : "Read-only"}
                  </span>
                )}
              </div>

              {editMode ? (
                <div className="space-y-5">
                  {/* Editable: Combine Method — only when multiple sources */}
                  {selectedProfile.imports.length >= 2 && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground capitalize">
                        Combine Method
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {(["use-first", "keep"] as const).map((opt) => (
                          <label
                            key={opt}
                            className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors flex-1 ${editCombineMethod === opt
                                ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10"
                                : "border-border hover:border-indigo-300 dark:hover:border-indigo-500/40"
                              }`}
                          >
                            <input
                              type="radio"
                              name="editCombineMethod"
                              value={opt}
                              checked={editCombineMethod === opt}
                              onChange={() => setEditCombineMethod(opt)}
                              className="mt-0.5 accent-indigo-600"
                            />
                            <div>
                              <p className="text-sm font-medium">
                                {opt === "use-first" ? "Use First" : "Keep All"}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {opt === "use-first"
                                  ? "When controls overlap, keep the first occurrence and discard duplicates"
                                  : "Retain all versions of duplicate controls"}
                              </p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Editable: Structuring Directive */}
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground capitalize">
                      Structuring Directive
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      {(["flat", "as-is", "custom"] as const).map((opt) => (
                        <label
                          key={opt}
                          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors flex-1 ${editStructuringDirective === opt
                              ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10"
                              : "border-border hover:border-indigo-300 dark:hover:border-indigo-500/40"
                            }`}
                        >
                          <input
                            type="radio"
                            name="editStructuringDirective"
                            value={opt}
                            checked={editStructuringDirective === opt}
                            onChange={() => setEditStructuringDirective(opt)}
                            className="mt-0.5 accent-indigo-600"
                          />
                          <div>
                            <p className="text-sm font-medium">
                              {opt === "as-is"
                                ? "As-Is"
                                : opt.charAt(0).toUpperCase() + opt.slice(1)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {opt === "flat" &&
                                "All controls as a flat list, no grouping"}
                              {opt === "as-is" &&
                                "Preserve original grouping from source catalogs"}
                              {opt === "custom" &&
                                "Configure how resolved controls are organized in the resulting profile."}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Editable: Custom Order */}
                  {editStructuringDirective === "custom" && (
                    <div className="space-y-2 pl-1">
                      <p className="text-xs font-medium text-muted-foreground capitalize">
                        Control Sort Order
                      </p>
                      <div className="flex flex-col sm:flex-row gap-2">
                        {(["keep", "ascending", "descending"] as const).map(
                          (opt) => (
                            <label
                              key={opt}
                              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors flex-1 ${editCustomOrder === opt
                                  ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10"
                                  : "border-border hover:border-indigo-300 dark:hover:border-indigo-500/40"
                                }`}
                            >
                              <input
                                type="radio"
                                name="editCustomOrder"
                                value={opt}
                                checked={editCustomOrder === opt}
                                onChange={() => setEditCustomOrder(opt)}
                                className="mt-0.5 accent-indigo-600"
                              />
                              <div>
                                <p className="text-sm font-medium">
                                  {opt === "keep"
                                    ? "Keep Original Order"
                                    : opt === "ascending"
                                      ? "Ascending (A → Z)"
                                      : "Descending (Z → A)"}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {opt === "keep" && "Preserve source order"}
                                  {opt === "ascending" &&
                                    "Sort by control ID ascending"}
                                  {opt === "descending" &&
                                    "Sort by control ID descending"}
                                </p>
                              </div>
                            </label>
                          ),
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* Read-only view */
                (() => {
                  const mergeData = selectedProfile.merge;
                  const combineMethod = mergeData?.combineMethod ?? "keep";
                  const structuringDirective =
                    mergeData?.structuringDirective ?? "flat";
                  const hasMultipleSources =
                    selectedProfile.imports.length >= 2;
                  return (
                    <div className="flex flex-wrap gap-4">
                      {hasMultipleSources && (
                        <div className="flex flex-col gap-1 min-w-[140px]">
                          <span className="text-sm font-normal text-foreground capitalize">
                            Combine Method
                          </span>
                          <span className="inline-flex items-center gap-1.5">
                            <span className="font-mono text-sm font-bold px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400">
                              {combineMethod}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {combineMethod === "use-first"
                                ? "— First occurrence wins"
                                : "— All copies retained"}
                            </span>
                          </span>
                        </div>
                      )}
                      <div className="flex flex-col gap-1 min-w-[140px]">
                        <span className="text-sm font-normal text-foreground capitalize">
                          Structuring Directive
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <span className="font-mono text-sm font-bold px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400">
                            {structuringDirective}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {structuringDirective === "flat" &&
                              "— Flat list, no grouping"}
                            {structuringDirective === "as-is" &&
                              "— Preserves source grouping"}
                            {structuringDirective === "custom" &&
                              "— Custom sort order"}
                          </span>
                        </span>
                      </div>
                      {structuringDirective === "custom" &&
                        (() => {
                          const customCfg = mergeData?.custom as {
                            "insert-controls"?: { order?: string }[];
                          } | null;
                          const order =
                            customCfg?.["insert-controls"]?.[0]?.order ??
                            "keep";
                          return (
                            <div className="flex flex-col gap-1 min-w-[140px]">
                              <span className="text-sm font-normal text-foreground capitalize">
                                Sort Order
                              </span>
                              <span className="inline-flex items-center gap-1.5">
                                <span className="font-mono text-sm font-bold px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400">
                                  {order}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  {order === "keep" &&
                                    "— Source order preserved"}
                                  {order === "ascending" && "— A → Z"}
                                  {order === "descending" && "— Z → A"}
                                </span>
                              </span>
                            </div>
                          );
                        })()}
                    </div>
                  );
                })()
              )}
            </div>
          )}

        {/* Controls section */}
        {editMode && editWizardStep === 2 && (
          /* ── Edit mode step 2: Alters ── */
          <div className="rounded-lg border border-border bg-card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Alter Directives</h3>
              <span className="text-sm text-muted-foreground">
                {availableControlsLoading
                  ? "Loading…"
                  : `${availableControls.length} controls`}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Add new parts to controls or mark existing parts for removal.
            </p>
            {availableControlsLoading ? (
              <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                <span className="text-sm">Loading controls…</span>
              </div>
            ) : availableControls.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No controls found for alter configuration.
              </p>
            ) : (
              <div className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
                {availableControls.map((ctrl) => {
                  const dir = alterDirectives[ctrl.controlId] ?? {
                    adds: [],
                    removes: [],
                  };
                  const draft = addPartDraft[ctrl.controlId] ?? {
                    position: "ending",
                    byId: "",
                    name: "",
                    title: "",
                    prose: "",
                    params: [],
                  };
                  const isShowingForm = !!showAddForm[ctrl.controlId];
                  const removedByIds = new Set(
                    dir.removes.map((r) => r.byId).filter(Boolean),
                  );
                  const removedByNames = new Set(
                    dir.removes.map((r) => r.name).filter(Boolean),
                  );
                  const hasChanges =
                    dir.adds.length > 0 || dir.removes.length > 0;
                  return (
                    <div
                      key={ctrl.controlId}
                      className="rounded-lg border border-border overflow-hidden"
                    >
                      <div className="bg-muted/40 px-4 py-3 flex items-center gap-2 border-b border-border">
                        <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400">
                          {ctrl.controlId?.toUpperCase()}
                        </span>
                        <span className="text-sm font-semibold">
                          {ctrl.controlTitle}
                        </span>
                        {hasChanges && (
                          <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 font-medium">
                            {dir.adds.length > 0 && `+${dir.adds.length}`}
                            {dir.adds.length > 0 &&
                              dir.removes.length > 0 &&
                              " "}
                            {dir.removes.length > 0 && `-${dir.removes.length}`}
                          </span>
                        )}
                      </div>
                      <div className="p-4 space-y-3">
                        {ctrl.parts.length > 0 && (
                          <div className="space-y-1">
                            <p className="text-[10px] font-bold text-muted-foreground tracking-wider">
                              Existing Parts
                            </p>
                            <div className="rounded border border-border divide-y divide-border">
                              {ctrl.parts.map((part) => {
                                const isRemoved =
                                  removedByIds.has(part.partId) ||
                                  removedByNames.has(part.name);
                                return (
                                  <div
                                    key={part.partId}
                                    className={`flex items-center justify-between px-3 py-2 ${isRemoved ? "opacity-40" : ""}`}
                                  >
                                    <div className="flex items-center gap-2">
                                      <span
                                        className={`text-xs font-medium ${isRemoved ? "line-through" : ""}`}
                                      >
                                        {part.name}
                                      </span>
                                      <span className="text-[10px] text-muted-foreground font-mono">
                                        {part.partId}
                                      </span>
                                    </div>
                                    {isRemoved ? (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setAlterDirectives((prev) => ({
                                            ...prev,
                                            [ctrl.controlId]: {
                                              ...(prev[ctrl.controlId] ?? {
                                                adds: [],
                                                removes: [],
                                              }),
                                              removes: (
                                                prev[ctrl.controlId]?.removes ??
                                                []
                                              ).filter(
                                                (r) =>
                                                  r.byId !== part.partId &&
                                                  r.name !== part.name,
                                              ),
                                            },
                                          }))
                                        }
                                        className="text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded border border-border"
                                      >
                                        Undo
                                      </button>
                                    ) : (
                                      <Tip label="Remove this part">
                                        <button
                                          type="button"
                                          onClick={() =>
                                            setAlterDirectives((prev) => ({
                                              ...prev,
                                              [ctrl.controlId]: {
                                                ...(prev[ctrl.controlId] ?? {
                                                  adds: [],
                                                  removes: [],
                                                }),
                                                removes: [
                                                  ...(prev[ctrl.controlId]
                                                    ?.removes ?? []),
                                                  {
                                                    id: `r-${Date.now()}-${part.partId}`,
                                                    byId: part.partId,
                                                    name: part.name,
                                                  },
                                                ],
                                              },
                                            }))
                                          }
                                          className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                        >
                                          <Trash2 className="size-3.5" />
                                        </button>
                                      </Tip>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {isShowingForm ? (
                          <div className="rounded border border-border p-3 space-y-3 bg-muted/20">
                            <p className="text-xs font-semibold">New Part</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="space-y-1">
                                <label className="text-[10px] font-bold text-muted-foreground ">
                                  Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                  type="text"
                                  placeholder="e.g. implementation"
                                  value={draft.name}
                                  onChange={(e) =>
                                    setAddPartDraft((prev) => ({
                                      ...prev,
                                      [ctrl.controlId]: {
                                        ...draft,
                                        name: e.target.value,
                                      },
                                    }))
                                  }
                                  className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-foreground transition-colors"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-[10px] font-bold text-muted-foreground ">
                                  Title
                                </label>
                                <input
                                  type="text"
                                  placeholder="Optional title"
                                  value={draft.title}
                                  onChange={(e) =>
                                    setAddPartDraft((prev) => ({
                                      ...prev,
                                      [ctrl.controlId]: {
                                        ...draft,
                                        title: e.target.value,
                                      },
                                    }))
                                  }
                                  className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-foreground transition-colors"
                                />
                              </div>
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold text-muted-foreground ">
                                Position
                              </label>
                              <Dropdown
                                size="sm"
                                variant="form"
                                value={draft.position}
                                onChange={(v) =>
                                  setAddPartDraft((prev) => ({
                                    ...prev,
                                    [ctrl.controlId]: {
                                      ...draft,
                                      position: v,
                                      byId:
                                        v === "before" || v === "after"
                                          ? draft.byId
                                          : "",
                                    },
                                  }))
                                }
                                options={[
                                  { value: "ending", label: "Ending" },
                                  { value: "starting", label: "Starting" },
                                  { value: "before", label: "Before" },
                                  { value: "after", label: "After" },
                                ]}
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[10px] font-bold text-muted-foreground ">
                                Prose <span className="text-red-500">*</span>
                              </label>
                              <textarea
                                placeholder="Part content…"
                                value={draft.prose}
                                onChange={(e) =>
                                  setAddPartDraft((prev) => ({
                                    ...prev,
                                    [ctrl.controlId]: {
                                      ...draft,
                                      prose: e.target.value,
                                    },
                                  }))
                                }
                                rows={3}
                                className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-foreground transition-colors resize-none"
                              />
                            </div>
                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <label className="text-[10px] font-bold text-muted-foreground ">
                                  Parameters
                                </label>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setAddPartDraft((prev) => ({
                                      ...prev,
                                      [ctrl.controlId]: {
                                        ...draft,
                                        params: [
                                          ...(draft.params ?? []),
                                          {
                                            id: `p-${Date.now()}`,
                                            paramId: "",
                                            label: "",
                                            clazz: "",
                                            usage: "",
                                            values: "",
                                            selections: [],
                                            constraints: [],
                                            guidelines: [],
                                          },
                                        ],
                                      },
                                    }))
                                  }
                                  className="flex items-center gap-1 text-[10px] text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 transition-colors"
                                >
                                  <Plus className="size-3" /> Add Parameter
                                </button>
                              </div>
                              {(draft.params ?? []).map((param, pIdx) => {
                                const isRef =
                                  !param.paramId ||
                                  draft.prose.includes(
                                    `{{ insert: param, ${param.paramId} }}`,
                                  );
                                const upd = (fields: Partial<typeof param>) =>
                                  setAddPartDraft((prev) => ({
                                    ...prev,
                                    [ctrl.controlId]: {
                                      ...draft,
                                      params: (draft.params ?? []).map(
                                        (p, i) =>
                                          i === pIdx ? { ...p, ...fields } : p,
                                      ),
                                    },
                                  }));
                                return (
                                  <div
                                    key={param.id}
                                    className={`rounded border p-3 space-y-3 ${!isRef ? "border-orange-500" : "border-border"}`}
                                  >
                                    {!isRef && (
                                      <p className="flex items-center gap-1.5 text-[10px] text-orange-500">
                                        <AlertTriangle className="size-3 shrink-0" />
                                        Parameter {param.paramId} is not
                                        referenced in the statement prose.
                                      </p>
                                    )}
                                    <div className="flex items-center justify-between">
                                      <span className="text-[10px] text-muted-foreground">
                                        Param {pIdx + 1}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setAddPartDraft((prev) => ({
                                            ...prev,
                                            [ctrl.controlId]: {
                                              ...draft,
                                              params: (
                                                draft.params ?? []
                                              ).filter((_, i) => i !== pIdx),
                                            },
                                          }))
                                        }
                                        className="text-muted-foreground hover:text-red-500 transition-colors"
                                      >
                                        <X className="size-3" />
                                      </button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                      <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground ">
                                          Param ID
                                        </label>
                                        <input
                                          type="text"
                                          value={param.paramId}
                                          onChange={(e) =>
                                            upd({ paramId: e.target.value })
                                          }
                                          className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-indigo-500 transition-colors"
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground ">
                                          Label
                                        </label>
                                        <input
                                          type="text"
                                          value={param.label}
                                          onChange={(e) =>
                                            upd({ label: e.target.value })
                                          }
                                          className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-indigo-500 transition-colors"
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground ">
                                          Class
                                        </label>
                                        <Dropdown
                                          size="sm"
                                          variant="form"
                                          placeholder="Select class"
                                          showPlaceholderWhenEmpty
                                          value={param.clazz}
                                          onChange={(v) => upd({ clazz: v })}
                                          options={[
                                            { value: "", label: "Select class" },
                                            { value: "sp800-53", label: "SP 800-53" },
                                            { value: "sp800-53a", label: "SP 800-53A" },
                                          ]}
                                        />
                                      </div>
                                    </div>
                                    <div className="space-y-1">
                                      <label className="text-[10px] font-bold text-muted-foreground ">
                                        Usage
                                      </label>
                                      <textarea
                                        placeholder="e.g. description of how this parameter is used"
                                        value={param.usage}
                                        onChange={(e) =>
                                          upd({ usage: e.target.value })
                                        }
                                        rows={2}
                                        className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-indigo-500 transition-colors resize-none"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <label className="text-[10px] font-bold text-muted-foreground ">
                                        Default Values
                                      </label>
                                      <input
                                        type="text"
                                        placeholder="e.g. 90, 180"
                                        value={param.values}
                                        onChange={(e) =>
                                          upd({ values: e.target.value })
                                        }
                                        className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-indigo-500 transition-colors"
                                      />
                                    </div>
                                    <div className="border-t border-border pt-1 space-y-1">
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-muted-foreground">
                                          Selection Choices
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            upd({
                                              selections: [
                                                ...param.selections,
                                                "",
                                              ],
                                            })
                                          }
                                          className="text-[10px] text-indigo-600 dark:text-indigo-400"
                                        >
                                          + Add Selection
                                        </button>
                                      </div>
                                      {param.selections.map((sel, sIdx) => (
                                        <div
                                          key={sIdx}
                                          className="flex items-center gap-2"
                                        >
                                          <input
                                            type="text"
                                            value={sel}
                                            onChange={(e) =>
                                              upd({
                                                selections:
                                                  param.selections.map(
                                                    (s, i) =>
                                                      i === sIdx
                                                        ? e.target.value
                                                        : s,
                                                  ),
                                              })
                                            }
                                            className="flex-1 text-xs bg-muted/50 rounded px-2 py-1 outline-none border border-border focus:border-indigo-500 transition-colors"
                                          />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              upd({
                                                selections:
                                                  param.selections.filter(
                                                    (_, i) => i !== sIdx,
                                                  ),
                                              })
                                            }
                                            className="text-muted-foreground hover:text-red-500"
                                          >
                                            <X className="size-3" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="border-t border-border pt-1 space-y-1">
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-muted-foreground">
                                          Constraints
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            upd({
                                              constraints: [
                                                ...param.constraints,
                                                "",
                                              ],
                                            })
                                          }
                                          className="text-[10px] text-indigo-600 dark:text-indigo-400"
                                        >
                                          + Add Constraint
                                        </button>
                                      </div>
                                      {param.constraints.map((con, cIdx) => (
                                        <div
                                          key={cIdx}
                                          className="flex items-center gap-2"
                                        >
                                          <input
                                            type="text"
                                            value={con}
                                            onChange={(e) =>
                                              upd({
                                                constraints:
                                                  param.constraints.map(
                                                    (c, i) =>
                                                      i === cIdx
                                                        ? e.target.value
                                                        : c,
                                                  ),
                                              })
                                            }
                                            className="flex-1 text-xs bg-muted/50 rounded px-2 py-1 outline-none border border-border focus:border-indigo-500 transition-colors"
                                          />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              upd({
                                                constraints:
                                                  param.constraints.filter(
                                                    (_, i) => i !== cIdx,
                                                  ),
                                              })
                                            }
                                            className="text-muted-foreground hover:text-red-500"
                                          >
                                            <X className="size-3" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="border-t border-border pt-1 space-y-1">
                                      <div className="flex items-center justify-between">
                                        <span className="text-[10px] text-muted-foreground">
                                          Guidelines
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            upd({
                                              guidelines: [
                                                ...param.guidelines,
                                                "",
                                              ],
                                            })
                                          }
                                          className="text-[10px] text-indigo-600 dark:text-indigo-400"
                                        >
                                          + Add Guideline
                                        </button>
                                      </div>
                                      {param.guidelines.map((gui, gIdx) => (
                                        <div
                                          key={gIdx}
                                          className="flex items-center gap-2"
                                        >
                                          <input
                                            type="text"
                                            value={gui}
                                            onChange={(e) =>
                                              upd({
                                                guidelines:
                                                  param.guidelines.map(
                                                    (g, i) =>
                                                      i === gIdx
                                                        ? e.target.value
                                                        : g,
                                                  ),
                                              })
                                            }
                                            className="flex-1 text-xs bg-muted/50 rounded px-2 py-1 outline-none border border-border focus:border-indigo-500 transition-colors"
                                          />
                                          <button
                                            type="button"
                                            onClick={() =>
                                              upd({
                                                guidelines:
                                                  param.guidelines.filter(
                                                    (_, i) => i !== gIdx,
                                                  ),
                                              })
                                            }
                                            className="text-muted-foreground hover:text-red-500"
                                          >
                                            <X className="size-3" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            <div className="flex items-center justify-end gap-2">
                              <button
                                type="button"
                                onClick={() =>
                                  setShowAddForm((prev) => ({
                                    ...prev,
                                    [ctrl.controlId]: false,
                                  }))
                                }
                                className="text-xs px-3 py-1.5 rounded border border-border text-muted-foreground hover:text-foreground transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                disabled={
                                  !draft.name.trim() || !draft.prose.trim()
                                }
                                onClick={() => {
                                  const newAdd: AlterAdd = {
                                    id: `a-${Date.now()}-${ctrl.controlId}`,
                                    position: draft.position,
                                    byId:
                                      draft.position === "before" ||
                                        draft.position === "after"
                                        ? draft.byId
                                        : "",
                                    name: draft.name.trim(),
                                    title: draft.title.trim(),
                                    prose: draft.prose.trim(),
                                    params: draft.params ?? [],
                                  };
                                  setAlterDirectives((prev) => ({
                                    ...prev,
                                    [ctrl.controlId]: {
                                      ...(prev[ctrl.controlId] ?? {
                                        adds: [],
                                        removes: [],
                                      }),
                                      adds: [
                                        ...(prev[ctrl.controlId]?.adds ?? []),
                                        newAdd,
                                      ],
                                    },
                                  }));
                                  setAddPartDraft((prev) => ({
                                    ...prev,
                                    [ctrl.controlId]: {
                                      position: "ending",
                                      byId: "",
                                      name: "",
                                      title: "",
                                      prose: "",
                                      params: [],
                                    },
                                  }));
                                  setShowAddForm((prev) => ({
                                    ...prev,
                                    [ctrl.controlId]: false,
                                  }));
                                }}
                                className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-40"
                              >
                                Add Part
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              setShowAddForm((prev) => ({
                                ...prev,
                                [ctrl.controlId]: true,
                              }))
                            }
                            className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                          >
                            <Plus className="size-3.5" />
                            Add Part
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {editMode && editWizardStep === 3 && (
          /* ── Edit mode step 3: Parameters ── */
          <div className="rounded-lg border border-border bg-card p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Parameter Overrides</h3>
              <span className="text-sm text-muted-foreground">
                {availableParamsLoading
                  ? "Loading…"
                  : `${availableParams.filter((p) => {
                    const ids = new Set(
                      editImportRows.flatMap((r) =>
                        [...r.checkedIds].map((id) => id.toUpperCase()),
                      ),
                    );
                    return (
                      ids.size === 0 || ids.has(p.controlId.toUpperCase())
                    );
                  }).length
                  } parameters`}
              </span>
            </div>
            {availableParamsLoading ? (
              <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                <span className="text-sm">Loading parameters…</span>
              </div>
            ) : availableParams.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                No parameters found for the selected controls.
              </p>
            ) : (
              <div className="space-y-5 max-h-[480px] overflow-y-auto pr-1">
                {Object.values(
                  availableParams
                    .filter((p) => {
                      const ids = new Set(
                        editImportRows.flatMap((r) =>
                          [...r.checkedIds].map((id) => id.toUpperCase()),
                        ),
                      );
                      return (
                        ids.size === 0 || ids.has(p.controlId.toUpperCase())
                      );
                    })
                    .reduce(
                      (acc, p) => {
                        const key = p.controlId.toUpperCase();
                        if (!acc[key])
                          acc[key] = {
                            controlId: key,
                            title: p.controlTitle ?? key,
                            params: [],
                          };
                        acc[key].params.push(p);
                        return acc;
                      },
                      {} as Record<
                        string,
                        {
                          controlId: string;
                          title: string;
                          params: typeof availableParams;
                        }
                      >,
                    ),
                ).map((group) => (
                  <div
                    key={group.controlId}
                    className="rounded-lg border border-border overflow-hidden"
                  >
                    <div className="bg-muted/40 px-4 py-3 border-b border-border flex items-center gap-2">
                      <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400">
                        {group.controlId?.toUpperCase()}
                      </span>
                      <span className="text-sm font-semibold">
                        {group.title}
                      </span>
                    </div>
                    <div className="divide-y divide-border">
                      {group.params.map((p) => {
                        const currentValue =
                          profileParamOverrides[p.paramId] ??
                          (p.values ?? []).join(", ");
                        return (
                          <div key={p.paramId} className="p-3 space-y-1.5">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-mono text-muted-foreground">
                                {p.paramId}
                              </span>
                              {p.clazz && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 font-medium">
                                  {p.clazz}
                                </span>
                              )}
                              {(p.values ?? []).length > 0 && (
                                <span className="text-[10px] text-muted-foreground">
                                  Default:{" "}
                                  <span className="font-mono text-emerald-600 dark:text-emerald-400">
                                    {(p.values ?? []).join(", ")}
                                  </span>
                                </span>
                              )}
                            </div>
                            <p className="text-xs font-medium">{p.label}</p>
                            <input
                              type="text"
                              value={currentValue}
                              onChange={(e) =>
                                setProfileParamOverrides((prev) => ({
                                  ...prev,
                                  [p.paramId]: e.target.value,
                                }))
                              }
                              placeholder="Enter value"
                              className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-foreground transition-colors"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {(!editMode || editWizardStep === 0) &&
          (editMode ? (
            /* ── Edit mode step 0: Controls tree ── */
            editCatalogControlsLoading ? (
              <div className="py-8 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                Loading catalog controls…
              </div>
            ) : (
              <ResizablePanels
                left={
                  <div className="rounded-lg border border-border bg-card flex flex-col min-h-[420px] max-h-[calc(100vh-12rem)]">
                    <div className="p-4 pb-0 shrink-0">
                      {/* Multi-source tabs for edit mode */}
                      {editImportRows.length > 1 && (
                        <div className="flex gap-1 border-b border-border mb-3">
                          {editImportRows.map((row, idx) => (
                            <button
                              key={row.key}
                              onClick={() => {
                                setEditActiveImportIdx(idx);
                                setEditCatalogControls(row.controls);
                                setEditRawControls(row.rawControls);
                                setEditCheckedIds(row.checkedIds);
                                setEditSourceCatalogId(row.sourceId);
                              }}
                              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${idx === editActiveImportIdx ? "border-indigo-600 dark:border-indigo-500 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                            >
                              {row.sourceType === "CATALOG"
                                ? "Catalog"
                                : "Profile"}
                              : {row.sourceTitle ?? `Source ${idx + 1}`}
                              <span className="ml-1.5 text-[10px] font-mono">
                                ({countLeafChecked(row.controls, row.checkedIds)})
                              </span>
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold">
                          Control Families
                        </h3>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">
                            {countLeafChecked(editCatalogControls, editCheckedIds)} selected
                          </span>
                          <button
                            onClick={() => {
                              setEditExpandAll(true);
                              setEditExpandTrigger((t) => t + 1);
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-muted-foreground hover:bg-accent transition-colors"
                          >
                            <ChevronDown className="size-3" /> Expand All
                          </button>
                          <button
                            onClick={() => {
                              setEditExpandAll(false);
                              setEditExpandTrigger((t) => t + 1);
                            }}
                            className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-muted-foreground hover:bg-accent transition-colors"
                          >
                            <ChevronRight className="size-3" /> Collapse All
                          </button>
                        </div>
                      </div>
                      <div className="relative mb-3">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                        <input
                          type="text"
                          placeholder="Search controls"
                          value={viewTreeSearch}
                          onChange={(e) => setViewTreeSearch(e.target.value)}
                          className="w-full text-sm pl-8 pr-3 py-1.5 rounded-md border border-border bg-background outline-none focus:border-indigo-500 dark:focus:border-indigo-500 transition-colors placeholder:text-muted-foreground"
                        />
                      </div>
                      {/* Filter toggles */}
                      <div className="flex items-center gap-4 mb-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch
                            checked={
                              viewTreeFilter === "selected" ||
                              viewTreeFilter === "selected-params"
                            }
                            onCheckedChange={(checked) => {
                              if (checked)
                                setViewTreeFilter(
                                  viewTreeFilter === "params"
                                    ? "selected-params"
                                    : "selected",
                                );
                              else
                                setViewTreeFilter(
                                  viewTreeFilter === "selected-params"
                                    ? "params"
                                    : "all",
                                );
                            }}
                          />
                          <span className="text-[11px] font-medium text-muted-foreground select-none">
                            Selected ({editCheckedIds.size})
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <Switch
                            checked={
                              viewTreeFilter === "params" ||
                              viewTreeFilter === "selected-params"
                            }
                            onCheckedChange={(checked) => {
                              if (checked)
                                setViewTreeFilter(
                                  viewTreeFilter === "selected"
                                    ? "selected-params"
                                    : "params",
                                );
                              else
                                setViewTreeFilter(
                                  viewTreeFilter === "selected-params"
                                    ? "selected"
                                    : "all",
                                );
                            }}
                          />
                          <span className="text-[11px] font-medium text-muted-foreground select-none">
                            Show Controls with Parameters
                          </span>
                        </label>
                      </div>
                    </div>
                    <div className="flex-1 overflow-y-auto px-4 pb-4">
                      {/* Select All */}
                      {editMode &&
                        viewTreeFilter !== "selected" &&
                        viewTreeFilter !== "selected-params" &&
                        (() => {
                          const collectLeafIds = (
                            nodes: ControlNode[],
                          ): string[] =>
                            nodes.flatMap((n) =>
                              n.children && n.children.length > 0
                                ? collectLeafIds(n.children)
                                : [n.id],
                            );
                          const filtered = searchTree(
                            filterTree(editCatalogControls, viewTreeFilter),
                            viewTreeSearch,
                          );
                          const allLeafIds = collectLeafIds(filtered);
                          const allChecked =
                            allLeafIds.length > 0 &&
                            allLeafIds.every((id) => editCheckedIds.has(id));
                          const someChecked = allLeafIds.some((id) =>
                            editCheckedIds.has(id),
                          );
                          return (
                            <label className="flex items-center gap-2 mb-2 px-2 py-1 cursor-pointer hover:bg-accent rounded text-sm">
                              <input
                                type="checkbox"
                                checked={allChecked}
                                ref={(el) => {
                                  if (el)
                                    el.indeterminate =
                                      someChecked && !allChecked;
                                }}
                                onChange={(e) => {
                                  const next = new Set(editCheckedIds);
                                  if (e.target.checked) {
                                    allLeafIds.forEach((id) => next.add(id));
                                  } else {
                                    allLeafIds.forEach((id) => next.delete(id));
                                  }
                                  setEditCheckedIds(next);
                                  // Sync to editImportRows
                                  setEditImportRows((prev) =>
                                    prev.map((r, i) =>
                                      i === editActiveImportIdx
                                        ? { ...r, checkedIds: new Set(next) }
                                        : r,
                                    ),
                                  );
                                }}
                                className="size-3.5 accent-foreground"
                              />
                              Select All ({allLeafIds.length})
                            </label>
                          );
                        })()}
                      {(() => {
                        let baseNodes = editCatalogControls;
                        // A leaf "has params" if the node flag says so (CATALOG
                        // sources) OR its raw control carries params (PROFILE /
                        // resolved sources, where the node flag isn't populated).
                        const paramLeafIds = new Set(
                          editRawControls
                            .filter((c) => c.params && c.params.length > 0)
                            .map((c) => c.controlId.toUpperCase()),
                        );
                        const leafHasParams = (n: ControlNode) =>
                          n.hasParams === true ||
                          paramLeafIds.has(n.id.toUpperCase());
                        // Prune to param-bearing branches, preserving the
                        // group -> sub-group -> control hierarchy. onlyChecked
                        // additionally requires the leaf to be selected.
                        const pruneParamTree = (
                          nodes: ControlNode[],
                          onlyChecked: boolean,
                        ): ControlNode[] =>
                          nodes.reduce<ControlNode[]>((acc, n) => {
                            if (n.children && n.children.length > 0) {
                              const kids = pruneParamTree(n.children, onlyChecked);
                              if (kids.length) acc.push({ ...n, children: kids });
                            } else if (
                              leafHasParams(n) &&
                              (!onlyChecked ||
                                editCheckedIds.has(n.id.toUpperCase()))
                            ) {
                              acc.push(n);
                            }
                            return acc;
                          }, []);
                        const renderParamTree = (
                          onlyChecked: boolean,
                          checkable: boolean,
                          emptyMsg: string,
                        ) => {
                          const tree = searchTree(
                            pruneParamTree(baseNodes, onlyChecked),
                            viewTreeSearch,
                          );
                          return tree.length > 0 ? (
                            <ControlTreeView
                              nodes={tree}
                              {...(checkable
                                ? {
                                    checkable: true,
                                    checkedIds: editCheckedIds,
                                    onCheck: handleEditCheck,
                                  }
                                : {})}
                              selectable
                              selectedId={editSelectedControl?.controlId?.toUpperCase()}
                              onSelect={(node) => {
                                const isGroup =
                                  node.children && node.children.length > 0;
                                if (!isGroup) {
                                  const ctrl = editRawControls.find(
                                    (c) =>
                                      c.controlId.toUpperCase() ===
                                      node.id.toUpperCase(),
                                  );
                                  if (ctrl) setEditSelectedControl(ctrl);
                                }
                              }}
                              expandAll={editExpandAll}
                              expandTrigger={editExpandTrigger}
                            />
                          ) : (
                            <p className="text-xs text-muted-foreground py-8 text-center">
                              {emptyMsg}
                            </p>
                          );
                        };
                        if (viewTreeFilter === "params") {
                          return renderParamTree(
                            false,
                            true,
                            "No controls with parameters.",
                          );
                        }
                        if (viewTreeFilter === "selected-params") {
                          return renderParamTree(
                            true,
                            false,
                            "No selected controls with parameters.",
                          );
                        }
                        if (viewTreeFilter === "selected") {
                          // Build flat list of selected controls
                          const selectedNodes: ControlNode[] = Array.from(
                            editCheckedIds,
                          )
                            .sort()
                            .map((id) => {
                              const ctrl = editRawControls.find(
                                (c) =>
                                  c.controlId.toUpperCase() ===
                                  id.toUpperCase(),
                              );
                              return {
                                id: id.toUpperCase(),
                                title: ctrl?.title ?? id,
                                hasParams: (ctrl?.params ?? []).length > 0,
                              };
                            });
                          const searched = viewTreeSearch
                            ? selectedNodes.filter(
                              (n) =>
                                n.id
                                  .toLowerCase()
                                  .includes(viewTreeSearch.toLowerCase()) ||
                                n.title
                                  .toLowerCase()
                                  .includes(viewTreeSearch.toLowerCase()),
                            )
                            : selectedNodes;
                          return searched.length > 0 ? (
                            <ControlTreeView
                              nodes={searched}
                              selectable
                              selectedId={editSelectedControl?.controlId?.toUpperCase()}
                              onSelect={(node) => {
                                const ctrl = editRawControls.find(
                                  (c) =>
                                    c.controlId.toUpperCase() ===
                                    node.id.toUpperCase(),
                                );
                                if (ctrl) setEditSelectedControl(ctrl);
                              }}
                              expandAll={editExpandAll}
                              expandTrigger={editExpandTrigger}
                            />
                          ) : (
                            <p className="text-xs text-muted-foreground py-8 text-center">
                              No selected controls.
                            </p>
                          );
                        }
                        const filtered = searchTree(
                          filterTree(baseNodes, viewTreeFilter),
                          viewTreeSearch,
                        );
                        return filtered.length > 0 ? (
                          <ControlTreeView
                            nodes={filtered}
                            checkable
                            checkedIds={editCheckedIds}
                            onCheck={handleEditCheck}
                            selectable
                            selectedId={editSelectedControl?.controlId?.toUpperCase()}
                            onSelect={(node) => {
                              const isGroup =
                                node.children && node.children.length > 0;
                              if (!isGroup) {
                                const ctrl = editRawControls.find(
                                  (c) =>
                                    c.controlId.toUpperCase() ===
                                    node.id.toUpperCase(),
                                );
                                if (ctrl) setEditSelectedControl(ctrl);
                              }
                            }}
                            expandAll={editExpandAll}
                            expandTrigger={editExpandTrigger}
                          />
                        ) : (
                          <p className="text-xs text-muted-foreground py-8 text-center">
                            No controls found.
                          </p>
                        );
                      })()}
                    </div>
                  </div>
                }
                right={
                  <div className="rounded-lg border border-border bg-card p-5 overflow-y-auto h-full">
                    {(() => {
                      const activeControl = editSelectedControl;
                      const rawSource = editRawControls;

                      if (activeControl)
                        return (
                          <div className="space-y-5">
                            <div className="flex items-start justify-between">
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-mono text-sm px-3 py-1 rounded-md bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 font-bold">
                                    {activeControl.controlId.toUpperCase()}
                                  </span>
                                  {editMode && (
                                    <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                                      Editing
                                    </span>
                                  )}
                                  {activeControl.clazz && !editMode && (
                                    <span className="text-xs text-muted-foreground">
                                      {activeControl.clazz}
                                    </span>
                                  )}
                                </div>
                                <h3 className="text-base font-semibold">
                                  {activeControl.title}
                                </h3>
                              </div>
                            </div>

                            {editMode ? (
                              <>
                                {/* Edit mode: Parts (read-only) */}
                                {activeControl.parts?.length > 0 && (
                                  <div className="space-y-4">
                                    {activeControl.parts
                                      .filter(
                                        (part) => part.name === "statement",
                                      )
                                      .map((part, pi) => (
                                        <div key={pi} className="space-y-2">
                                          <h4 className="text-sm font-semibold text-foreground capitalize">
                                            Statement
                                          </h4>
                                          <div className="rounded-md border border-border bg-muted/20 p-3">
                                            <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                              {part.prose || "-"}
                                            </p>
                                          </div>
                                        </div>
                                      ))}
                                    {activeControl.parts
                                      .filter(
                                        (part) => part.name === "guidance",
                                      )
                                      .map((part, pi) => (
                                        <div key={pi} className="space-y-2">
                                          <h4 className="text-sm font-semibold text-foreground capitalize">
                                            Guidance
                                          </h4>
                                          <div className="rounded-md border border-border bg-muted/20 p-3">
                                            <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                              {part.prose || "-"}
                                            </p>
                                          </div>
                                        </div>
                                      ))}
                                    {activeControl.parts
                                      .filter(
                                        (part) =>
                                          part.name !== "statement" &&
                                          part.name !== "guidance",
                                      )
                                      .map((part, pi) => (
                                        <div key={pi} className="space-y-2">
                                          <h4 className="text-sm font-semibold text-foreground capitalize">
                                            {part.name || part.partId}
                                          </h4>
                                          <div className="rounded-md border border-border bg-muted/20 p-3">
                                            <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                              {part.prose || "-"}
                                            </p>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                )}

                                {/* Edit mode: Parameter overrides */}
                                {(() => {
                                  const ctrlParams = collectParams(rawSource, [
                                    activeControl.controlId.toUpperCase(),
                                  ]);
                                  if (ctrlParams.length === 0) return null;
                                  return (
                                    <div className="space-y-2">
                                      <span className="text-xs font-bold text-muted-foreground">
                                        Parameters
                                      </span>
                                      {paramError && (
                                        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
                                          <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
                                          <p className="text-xs flex-1">
                                            {paramError}
                                          </p>
                                          <button
                                            onClick={() => {
                                              setParamError(null);
                                              setErrorParamId(null);
                                            }}
                                            className="flex-shrink-0 text-red-400 hover:text-red-600"
                                          >
                                            <X className="size-4" />
                                          </button>
                                        </div>
                                      )}
                                      {ctrlParams.map((p) => {
                                        const catalogDefault =
                                          p.catalogValues.join(", ") || "-";
                                        const currentValue =
                                          profileParamOverrides[p.paramId] ??
                                          "";
                                        const hasError =
                                          errorParamId === p.paramId;
                                        const isNumeric =
                                          p.clazz === "num" ||
                                          p.clazz === "int";
                                        const maxNumeric =
                                          isNumeric &&
                                            p.catalogValues.length > 0
                                            ? Number(p.catalogValues[0])
                                            : null;
                                        return (
                                          <div
                                            key={p.paramId}
                                            className={`rounded border p-3 space-y-2 bg-muted/20 ${hasError ? "border-red-400 dark:border-red-500 bg-red-50/50 dark:bg-red-500/5" : "border-border"}`}
                                          >
                                            {/* Read-only Param ID, Label, Class */}
                                            <div className="grid grid-cols-3 gap-3">
                                              <div className="space-y-1">
                                                <label className="text-[10px] font-medium text-muted-foreground">
                                                  Param ID
                                                </label>
                                                <p className="text-sm font-mono">
                                                  {p.paramId}
                                                </p>
                                              </div>
                                              <div className="space-y-1">
                                                <label className="text-[10px] font-medium text-muted-foreground">
                                                  Label
                                                </label>
                                                <p className="text-sm">
                                                  {p.label || "-"}
                                                </p>
                                              </div>
                                              <div className="space-y-1">
                                                <label className="text-[10px] font-medium text-muted-foreground">
                                                  Class
                                                </label>
                                                <p className="text-sm">
                                                  {p.clazz
                                                    ? `${p.clazz} (${p.clazz === "int" ? "integer" : p.clazz === "num" ? "number" : "text"})`
                                                    : "-"}
                                                </p>
                                              </div>
                                            </div>
                                            {/* Read-only Usage */}
                                            {p.usage && (
                                              <div className="space-y-1">
                                                <label className="text-[10px] font-medium text-muted-foreground">
                                                  Usage
                                                </label>
                                                <p className="text-sm text-muted-foreground">
                                                  {p.usage}
                                                </p>
                                              </div>
                                            )}
                                            {/* Editable Default Values */}
                                            <div className="space-y-1">
                                              <label className="text-[10px] font-medium text-muted-foreground">
                                                Default Values
                                              </label>
                                              <div className="flex items-center gap-1.5 flex-wrap">
                                                {p.catalogValues.map(
                                                  (v, vi) => (
                                                    <span
                                                      key={vi}
                                                      className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-mono"
                                                    >
                                                      {v}
                                                    </span>
                                                  ),
                                                )}
                                                {p.catalogValues.length ===
                                                  0 && (
                                                    <span className="text-xs text-muted-foreground">
                                                      -
                                                    </span>
                                                  )}
                                              </div>
                                            </div>
                                            {isNumeric &&
                                              maxNumeric !== null && (
                                                <p className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                                  <AlertTriangle className="size-3" />{" "}
                                                  You can only set equal or
                                                  lower than {maxNumeric}
                                                </p>
                                              )}
                                            {p.selectConfig ? (
                                              <div className="space-y-2">
                                                {/* Read-only Selection Config */}
                                                <div className="rounded border border-border p-3 space-y-3 bg-muted/10">
                                                  <div className="flex items-center justify-between">
                                                    <span className="text-xs font-bold text-foreground">
                                                      Selection Choices
                                                    </span>
                                                    <span className="text-[10px] text-muted-foreground">
                                                      {p.selectConfig[
                                                        "how-many"
                                                      ] === "one"
                                                        ? "Pick exactly one"
                                                        : "Pick one or more"}
                                                    </span>
                                                  </div>
                                                  <div className="flex flex-wrap gap-1.5">
                                                    {p.selectConfig.choice.map(
                                                      (c, ci) => (
                                                        <span
                                                          key={ci}
                                                          className="text-xs bg-muted/50 border border-border text-foreground px-2 py-0.5 rounded"
                                                        >
                                                          {c}
                                                        </span>
                                                      ),
                                                    )}
                                                  </div>
                                                  {/* Override value selection */}
                                                  <div className="space-y-1">
                                                    <span className="text-xs font-medium text-foreground">
                                                      Override Value
                                                    </span>
                                                    <div className="flex flex-wrap gap-1.5">
                                                      {p.selectConfig.choice.map(
                                                        (c) => {
                                                          const ov =
                                                            currentValue
                                                              ? currentValue
                                                                .split(",")
                                                                .map((s) =>
                                                                  s.trim(),
                                                                )
                                                                .filter(
                                                                  Boolean,
                                                                )
                                                              : [];
                                                          const sel =
                                                            ov.includes(c);
                                                          const exceedsMax =
                                                            isNumeric &&
                                                            maxNumeric !==
                                                            null &&
                                                            !isNaN(Number(c)) &&
                                                            Number(c) >
                                                            maxNumeric;
                                                          return (
                                                            <button
                                                              key={c}
                                                              type="button"
                                                              disabled={
                                                                exceedsMax
                                                              }
                                                              onClick={() => {
                                                                if (exceedsMax)
                                                                  return;
                                                                const nv =
                                                                  p
                                                                    .selectConfig![
                                                                    "how-many"
                                                                  ] === "one"
                                                                    ? sel
                                                                      ? []
                                                                      : [c]
                                                                    : sel
                                                                      ? ov.filter(
                                                                        (v) =>
                                                                          v !==
                                                                          c,
                                                                      )
                                                                      : [
                                                                        ...ov,
                                                                        c,
                                                                      ];
                                                                setProfileParamOverrides(
                                                                  (prev) => ({
                                                                    ...prev,
                                                                    [p.paramId]:
                                                                      nv.join(
                                                                        ", ",
                                                                      ),
                                                                  }),
                                                                );
                                                                if (hasError) {
                                                                  setParamError(
                                                                    null,
                                                                  );
                                                                  setErrorParamId(
                                                                    null,
                                                                  );
                                                                }
                                                              }}
                                                              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${exceedsMax ? "opacity-40 cursor-not-allowed line-through bg-muted/30 border-border text-muted-foreground" : sel ? "bg-indigo-100 dark:bg-indigo-500/20 border-indigo-400 dark:border-indigo-500 text-indigo-700 dark:text-indigo-300 font-semibold" : "bg-muted/50 border-border text-muted-foreground hover:border-indigo-300"}`}
                                                            >
                                                              {sel && (
                                                                <Check className="size-3 inline mr-0.5 -mt-0.5" />
                                                              )}
                                                              {c}
                                                            </button>
                                                          );
                                                        },
                                                      )}
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="space-y-1">
                                                <label className="text-xs font-medium text-foreground">
                                                  Override Value
                                                </label>
                                                <input
                                                  type={
                                                    isNumeric
                                                      ? "number"
                                                      : "text"
                                                  }
                                                  value={currentValue}
                                                  onKeyDown={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                  onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (
                                                      isNumeric &&
                                                      maxNumeric !== null &&
                                                      val !== "" &&
                                                      Number(val) > maxNumeric
                                                    )
                                                      return;
                                                    setProfileParamOverrides(
                                                      (prev) => ({
                                                        ...prev,
                                                        [p.paramId]: val,
                                                      }),
                                                    );
                                                    if (hasError) {
                                                      setParamError(null);
                                                      setErrorParamId(null);
                                                    }
                                                  }}
                                                  max={maxNumeric ?? undefined}
                                                  step={
                                                    p.clazz === "num"
                                                      ? "any"
                                                      : p.clazz === "int"
                                                        ? "1"
                                                        : undefined
                                                  }
                                                  placeholder={catalogDefault}
                                                  className={`w-full text-sm bg-background rounded-lg px-3 py-2 outline-none border transition-colors ${hasError ? "border-red-400 focus:border-red-500" : "border-border focus:border-indigo-500 dark:focus:border-indigo-500"}`}
                                                />
                                              </div>
                                            )}

                                            {/* Constraints (read-only) */}
                                            {p.constraints &&
                                              (p.constraints as any[]).length >
                                              0 && (
                                                <div className="space-y-2 mt-2">
                                                  <span className="text-xs font-bold text-foreground flex items-center gap-1">
                                                    <Info className="size-3" />{" "}
                                                    Constraints
                                                  </span>
                                                  {(p.constraints as any[]).map(
                                                    (con: any, ci: number) => (
                                                      <div
                                                        key={ci}
                                                        className="rounded border border-border p-3 space-y-2 bg-muted/20"
                                                      >
                                                        <div className="space-y-1">
                                                          <label className="text-xs font-medium text-muted-foreground">
                                                            Description
                                                          </label>
                                                          <p className="text-sm">
                                                            {con.description ||
                                                              "-"}
                                                          </p>
                                                        </div>
                                                        {(con.tests ?? []).map(
                                                          (
                                                            t: any,
                                                            ti: number,
                                                          ) => (
                                                            <div
                                                              key={ti}
                                                              className="grid grid-cols-2 gap-3"
                                                            >
                                                              <div className="space-y-1">
                                                                <label className="text-xs font-medium text-muted-foreground">
                                                                  Expression
                                                                </label>
                                                                <p className="text-sm font-mono">
                                                                  {t.expression ||
                                                                    "-"}
                                                                </p>
                                                              </div>
                                                              <div className="space-y-1">
                                                                <label className="text-xs font-medium text-muted-foreground">
                                                                  Remarks
                                                                </label>
                                                                <p className="text-sm">
                                                                  {t.remarks ||
                                                                    "-"}
                                                                </p>
                                                              </div>
                                                            </div>
                                                          ),
                                                        )}
                                                      </div>
                                                    ),
                                                  )}
                                                </div>
                                              )}

                                            {/* Guidelines (read-only) */}
                                            {p.guidelines &&
                                              (p.guidelines as any[]).filter(
                                                (g: any) => g.prose != null,
                                              ).length > 0 && (
                                                <div className="space-y-2 mt-2">
                                                  <span className="text-xs font-bold text-muted-foreground">
                                                    Guidelines
                                                  </span>
                                                  {(p.guidelines as any[])
                                                    .filter(
                                                      (g: any) =>
                                                        g.prose != null,
                                                    )
                                                    .map(
                                                      (g: any, gi: number) => (
                                                        <div
                                                          key={gi}
                                                          className="rounded border border-border bg-muted/20 p-2"
                                                        >
                                                          <p className="text-sm text-muted-foreground leading-relaxed">
                                                            {g.prose}
                                                          </p>
                                                        </div>
                                                      ),
                                                    )}
                                                </div>
                                              )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  );
                                })()}
                              </>
                            ) : (
                              <>
                                {/* View mode: Parts */}
                                {activeControl.parts?.length > 0 && (
                                  <div className="space-y-3">
                                    {activeControl.parts.map((part) => (
                                      <div
                                        key={part.partId}
                                        className="space-y-1"
                                      >
                                        <span className="text-[10px] font-bold text-muted-foreground ">
                                          {part.name || part.partId}
                                        </span>
                                        {part.prose && (
                                          <p className="text-sm text-muted-foreground leading-relaxed">
                                            {part.prose}
                                          </p>
                                        )}
                                        {part.parts?.map((sub) => (
                                          <div
                                            key={sub.partId}
                                            className="ml-4 space-y-1"
                                          >
                                            <span className="text-[10px] font-bold text-muted-foreground ">
                                              {sub.name || sub.partId}
                                            </span>
                                            {sub.prose && (
                                              <p className="text-sm text-muted-foreground leading-relaxed">
                                                {sub.prose}
                                              </p>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {/* View mode: Parameters */}
                                {activeControl.params?.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-semibold mb-2">
                                      Parameters
                                    </h4>
                                    <div className="space-y-2">
                                      {activeControl.params.map((p) => (
                                        <div
                                          key={p.paramId}
                                          className="rounded-lg border border-border p-3 bg-muted/30 space-y-2"
                                        >
                                          <div className="flex items-center gap-2">
                                            <span className="font-mono text-[10px] font-bold text-muted-foreground">
                                              {p.paramId}
                                            </span>
                                            {p.clazz && (
                                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 font-medium">
                                                {p.clazz}
                                              </span>
                                            )}
                                          </div>
                                          <p className="text-sm font-medium">
                                            {p.label}
                                          </p>
                                          {p.usage && (
                                            <p className="text-xs text-muted-foreground">
                                              {p.usage}
                                            </p>
                                          )}
                                          {p.values && p.values.length > 0 && (
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <span className="text-[10px] font-bold text-muted-foreground">
                                                Default:
                                              </span>
                                              {p.values.map((v, i) => (
                                                <span
                                                  key={i}
                                                  className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-mono"
                                                >
                                                  {v}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                          {p.selectConfig && (
                                            <div className="space-y-1">
                                              <span className="text-[10px] font-bold text-muted-foreground">
                                                {(
                                                  p.selectConfig as ParameterSelection
                                                )["how-many"] === "one"
                                                  ? "Pick one of:"
                                                  : "Pick one or more:"}
                                              </span>
                                              <div className="flex flex-wrap gap-1">
                                                {(
                                                  p.selectConfig as ParameterSelection
                                                ).choice.map((c, i) => (
                                                  <span
                                                    key={i}
                                                    className={`text-xs px-2 py-0.5 rounded-full border ${p.values?.includes(c) ? "bg-indigo-100 dark:bg-indigo-500/20 border-indigo-300 dark:border-indigo-500 text-indigo-700 dark:text-indigo-300 font-semibold" : "bg-muted border-border text-muted-foreground"}`}
                                                  >
                                                    {p.values?.includes(c) && (
                                                      <Check className="size-3 inline mr-0.5 -mt-0.5" />
                                                    )}
                                                    {c}
                                                  </span>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {p.constraints &&
                                            (
                                              p.constraints as ParameterConstraint[]
                                            ).length > 0 && (
                                              <div className="space-y-1">
                                                <span className="text-[10px] font-bold text-muted-foreground flex items-center gap-1">
                                                  <Info className="size-3" />{" "}
                                                  Constraints
                                                </span>
                                                {(
                                                  p.constraints as ParameterConstraint[]
                                                ).map((con, ci) => (
                                                  <div
                                                    key={ci}
                                                    className="text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1.5"
                                                  >
                                                    {con.description && (
                                                      <p>{con.description}</p>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {/* View mode: Enhancements */}
                                {activeControl.enhancements?.length > 0 && (
                                  <div>
                                    <h4 className="text-sm font-semibold mb-2">
                                      Control Enhancements
                                    </h4>
                                    <div className="space-y-1.5">
                                      {activeControl.enhancements.map((enh) => (
                                        <button
                                          key={enh.id}
                                          onClick={() => {
                                            setViewSelectedControl(enh);
                                            setViewSelectedGroup(null);
                                          }}
                                          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border border-border hover:bg-accent transition-colors text-left"
                                        >
                                          <span className="font-mono text-xs font-bold text-muted-foreground">
                                            {enh.controlId.toUpperCase()}
                                          </span>
                                          <span className="text-sm">
                                            {enh.title}
                                          </span>
                                        </button>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        );

                      // Nothing selected - show placeholder
                      return (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                          <FileText className="size-8 text-muted-foreground mb-3" />
                          <p className="text-sm font-medium text-muted-foreground">
                            Select a control to view details
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Click on a control in the tree to see its statement
                            and override parameters.
                          </p>
                        </div>
                      );
                    })()}
                  </div>
                }
              />
            )
          ) : (
            /* ── View mode: two-panel layout built from profile data ── */
            <ResizablePanels
              left={
                <div className="rounded-lg border border-border bg-card flex flex-col min-h-[420px] max-h-[calc(100vh-12rem)]">
                  <div className="p-4 pb-3 shrink-0">
                    {/* Import source tabs */}
                    {selectedProfile.imports.length > 1 && (
                      <div className="flex gap-1 border-b border-border mb-3">
                        {(selectedProfile.imports as any[]).map((imp, idx) => {
                          const ids: string[] = (
                            imp.includeControls ?? []
                          ).flatMap((ic: any) => ic["with-ids"] ?? []);
                          const uniqueIds = Array.from(
                            new Set(ids.map((cid: string) => cid.toLowerCase())),
                          );
                          // Match the list-rendering logic: count only IDs that
                          // resolve to a detail with a groupId (not "Other").
                          const namedGroupCount = uniqueIds.filter((cid) => {
                            const d = controlDetailsMap.get(cid);
                            return !!d?.groupId;
                          }).length;
                          const leafCount =
                            namedGroupCount > 0 ? namedGroupCount : uniqueIds.length;
                          return (
                            <button
                              key={idx}
                              onClick={() => {
                                setViewIncludedImportIdx(idx);
                                setViewTreeExpandAll(false);
                                setViewTreeExpandTrigger((t) => t + 1);
                                setViewSelectedControl(null);
                                setViewSelectedGroup(null);
                              }}
                              className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${idx === viewIncludedImportIdx ? "border-indigo-600 dark:border-indigo-500 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                            >
                              {(imp.sourceType ?? "CATALOG") === "CATALOG"
                                ? "Catalog"
                                : "Profile"}
                              : {imp.sourceTitle ?? `Source ${idx + 1}`}
                              <span className="ml-1.5 text-[10px] font-mono">
                                ({leafCount})
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold">
                        Included Controls
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setViewTreeExpandAll(true);
                            setViewTreeExpandTrigger((t) => t + 1);
                          }}
                          disabled={profileExpandAllDisabled}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-foreground transition-all ${profileExpandAllDisabled ? "opacity-30 cursor-not-allowed pointer-events-none" : "hover:bg-accent"}`}
                        >
                          <ChevronDown className="size-3" /> Expand All
                        </button>
                        <button
                          onClick={() => {
                            setViewTreeExpandAll(false);
                            setViewTreeExpandTrigger((t) => t + 1);
                          }}
                          disabled={profileCollapseAllDisabled}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium text-foreground transition-all ${profileCollapseAllDisabled ? "opacity-30 cursor-not-allowed pointer-events-none" : "hover:bg-accent"}`}
                        >
                          <ChevronRight className="size-3" /> Collapse All
                        </button>
                      </div>
                    </div>
                    <label className="mt-2 flex items-center gap-2 cursor-pointer">
                      <Switch
                        checked={viewParamsMode}
                        onCheckedChange={(checked) => {
                          setViewParamsMode(checked);
                          setViewSelectedGroup(null);
                          // Reset expand override when switching views (matches Catalog).
                          setViewTreeExpandAll(undefined);
                        }}
                      />
                      <span className="text-[11px] font-medium text-muted-foreground select-none">
                        Show Controls with Parameters
                      </span>
                    </label>
                  </div>
                  <div className="flex-1 overflow-y-auto px-4 pb-4">
                    {allIncludedIds.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-8 text-center">
                        No controls included.
                      </p>
                    ) : (
                      (() => {
                        // Filter controls by active tab import, skip group-level IDs (no controlDetails entry)
                        const tabIncludedIds: string[] = (() => {
                          const activeImp = (selectedProfile.imports as any[])[
                            selectedProfile.imports.length > 1
                              ? viewIncludedImportIdx
                              : 0
                          ];
                          const seen = new Set<string>();
                          const ids: string[] = (
                            activeImp?.includeControls ?? []
                          ).flatMap((ic: any) => ic["with-ids"] ?? []);
                          return ids.filter((cid: string) => {
                            const key = cid.toLowerCase();
                            if (seen.has(key)) return false;
                            seen.add(key);
                            return true;
                          });
                        })();

                        // Group controls by groupId using controlDetails, with a
                        // hierarchy-derived fallback for profiles that lack group
                        // metadata (e.g. manually created profiles).
                        let groups = buildIncludedGroups(tabIncludedIds);
                        // Filter by search
                        if (viewTreeSearch.trim()) {
                          const q = viewTreeSearch.toLowerCase();
                          groups = groups
                            .map((g) => ({
                              ...g,
                              controls: g.controls.filter(
                                (c) =>
                                  c.controlId.toLowerCase().includes(q) ||
                                  c.controlName.toLowerCase().includes(q),
                              ),
                            }))
                            .filter(
                              (g) =>
                                g.groupName.toLowerCase().includes(q) ||
                                g.groupId.toLowerCase().includes(q) ||
                                g.controls.length > 0,
                            );
                        }
                        // Filter to only controls with parameters; the grouped
                        // accordion below then renders them exactly like the
                        // "all" view (collapsible groups + Params badges).
                        if (viewParamsMode) {
                          groups = groups
                            .map((g) => ({
                              ...g,
                              controls: g.controls.filter((c) =>
                                viewParamIdSet.has(c.controlId.toUpperCase()),
                              ),
                            }))
                            .filter((g) => g.controls.length > 0);
                        }
                        // Build ControlTreeView nodes so the profile renders the
                        // same hierarchical tree as the Catalog page. The synthetic
                        // "Other" bucket has no real header, so its controls surface
                        // at the top level.
                        const treeNodes: ControlNode[] = groups.flatMap((g) => {
                          const controlNodes: ControlNode[] = g.controls.map(
                            (c) => ({
                              id: c.controlId.toUpperCase(),
                              label: c.controlId.toUpperCase(),
                              title: c.controlName,
                              hasParams: viewParamIdSet.has(
                                c.controlId.toUpperCase(),
                              ),
                            }),
                          );
                          if (g.groupId.toLowerCase() === "other")
                            return controlNodes;
                          return [
                            {
                              id: g.groupId.toUpperCase(),
                              label: g.groupId.toUpperCase(),
                              title: g.groupName,
                              children: controlNodes,
                            },
                          ];
                        });
                        return (
                          <ControlTreeView
                            nodes={treeNodes}
                            selectable
                            selectedId={
                              viewSelectedControl?.controlId?.toUpperCase() ??
                              (viewSelectedGroup?.groupId
                                ? viewSelectedGroup.groupId.toUpperCase()
                                : undefined)
                            }
                            onSelect={(node) => {
                              const isGroupNode =
                                !!node.children && node.children.length > 0;
                              // A group head can itself be a real control (CIS-style
                              // ID hierarchy) — clicking it shows that control's
                              // statement/guidance, not the generic group panel.
                              const idLower = node.id.toLowerCase();
                              const isControlGroup =
                                controlDetailsMap.has(idLower) &&
                                !viewCatalogGroupIds.has(idLower);
                              if (isGroupNode && !isControlGroup) {
                                setViewSelectedControl(null);
                                setViewSelectedGroup({
                                  groupId: node.id,
                                  groupName: node.title,
                                  controlCount: node.children!.length,
                                });
                              } else {
                                setViewSelectedGroup(null);
                                setViewSelectedControl({
                                  controlId: node.id.toUpperCase(),
                                  title: node.title,
                                } as ControlResponse);
                                fetchControlDetail(node.id);
                              }
                            }}
                            expandAll={viewTreeExpandAll}
                            expandTrigger={viewTreeExpandTrigger}
                          />
                        );
                      })()
                    )}
                  </div>
                </div>
              }
              right={
                <div className="rounded-lg border border-border bg-card p-5 overflow-y-auto h-full min-h-[420px] max-h-[calc(100vh-12rem)]">
                  {viewSelectedGroup && !viewSelectedControl ? (
                    <div className="space-y-5">
                      <h3 className="text-base font-semibold">Group Details</h3>
                      <div className="rounded-lg border border-border overflow-hidden">
                        <table className="w-full text-sm">
                          <tbody>
                            <tr className="border-b border-border">
                              <td className="px-4 py-2.5 font-medium text-muted-foreground bg-muted/50 w-40">
                                Group ID
                              </td>
                              <td className="px-4 py-2.5">
                                <span className="font-mono text-sm px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 font-bold">
                                  {viewSelectedGroup.groupId.toUpperCase()}
                                </span>
                              </td>
                            </tr>
                            <tr className="border-b border-border">
                              <td className="px-4 py-2.5 font-medium text-muted-foreground bg-muted/50 w-40">
                                Group Name
                              </td>
                              <td className="px-4 py-2.5 font-medium">
                                {viewSelectedGroup.groupName}
                              </td>
                            </tr>
                            <tr>
                              <td className="px-4 py-2.5 font-medium text-muted-foreground bg-muted/50 w-40">
                                No of Controls
                              </td>
                              <td className="px-4 py-2.5">
                                <span className="inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400">
                                  {viewSelectedGroup.controlCount}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="flex flex-col items-center justify-center flex-1 py-12 text-center">
                        <FileText className="size-8 text-muted-foreground mb-3" />
                        <p className="text-sm font-medium text-muted-foreground">
                          Select a control to view details
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Click on a control to see its statement, parameters,
                          and guidance.
                        </p>
                      </div>
                    </div>
                  ) : viewSelectedControl ? (
                    (() => {
                      const viewDetail = controlDetailsMap.get(
                        viewSelectedControl.controlId.toLowerCase(),
                      );
                      const statementParts =
                        viewControlDetail?.parts?.filter(
                          (p) => p.name === "statement",
                        ) ?? [];
                      const guidanceParts =
                        viewControlDetail?.parts?.filter(
                          (p) => p.name === "guidance",
                        ) ?? [];
                      const otherParts =
                        viewControlDetail?.parts?.filter(
                          (p) =>
                            p.name !== "statement" && p.name !== "guidance",
                        ) ?? [];
                      return (
                        <div className="space-y-5">
                          {/* Header */}
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <span className="font-mono text-sm px-3 py-1 rounded-md bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400 font-bold">
                                {viewSelectedControl.controlId.toUpperCase()}
                              </span>
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold">
                                  {viewControlDetail?.title ??
                                    viewDetail?.controlName ??
                                    viewSelectedControl.controlId.toUpperCase()}
                                </span>
                                {viewDetail && (
                                  <span className="text-xs text-muted-foreground">
                                    {viewDetail.groupName}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Loading state */}
                          {viewControlLoading && (
                            <div className="flex items-center gap-2 py-8 justify-center text-muted-foreground">
                              <Loader2 className="size-4 animate-spin" />
                              <span className="text-sm">
                                Loading control details…
                              </span>
                            </div>
                          )}

                          {/* Control details */}
                          {!viewControlLoading && viewControlDetail && (
                            <div className="space-y-4">
                              {/* Statement */}
                              {statementParts.length > 0 && (
                                <div className="space-y-2">
                                  <h4 className="text-sm font-semibold text-foreground capitalize">
                                    Statement
                                  </h4>
                                  {statementParts.map((part) => (
                                    <div
                                      key={part.id}
                                      className="rounded-md border border-border bg-muted/20 p-3"
                                    >
                                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                        {part.prose}
                                      </p>
                                      {part.parts?.length > 0 && (
                                        <div className="mt-2 space-y-1 pl-3 border-l-2 border-border">
                                          {part.parts.map((sub, si) => (
                                            <p
                                              key={si}
                                              className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap"
                                            >
                                              {sub.prose}
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Guidance */}
                              {guidanceParts.length > 0 && (
                                <div className="space-y-2">
                                  <h4 className="text-sm font-semibold text-foreground capitalize">
                                    Guidance
                                  </h4>
                                  {guidanceParts.map((part) => (
                                    <div
                                      key={part.id}
                                      className="rounded-md border border-border bg-muted/20 p-3"
                                    >
                                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                        {part.prose}
                                      </p>
                                      {part.parts?.length > 0 && (
                                        <div className="mt-2 space-y-1 pl-3 border-l-2 border-border">
                                          {part.parts.map((sub, si) => (
                                            <p
                                              key={si}
                                              className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap"
                                            >
                                              {sub.prose}
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Other parts */}
                              {otherParts.length > 0 &&
                                otherParts.map((part) => (
                                  <div key={part.id} className="space-y-2">
                                    <h4 className="text-sm font-semibold text-foreground capitalize">
                                      {part.name}
                                    </h4>
                                    <div className="rounded-md border border-border bg-muted/20 p-3">
                                      <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                        {part.prose}
                                      </p>
                                    </div>
                                  </div>
                                ))}

                              {/* Parameters */}
                              {viewControlDetail.params?.length > 0 &&
                                (() => {
                                  const savedModify =
                                    selectedProfile?.modify as {
                                      setParameters?: {
                                        "param-id": string;
                                        values: string[];
                                      }[];
                                    } | null;
                                  const overrideMap: Record<string, string> =
                                    {};
                                  for (const sp of savedModify?.setParameters ??
                                    []) {
                                    overrideMap[sp["param-id"]] =
                                      sp.values?.join(", ") ?? "";
                                  }
                                  return (
                                    <div className="space-y-2">
                                      <h4 className="text-sm font-semibold text-foreground capitalize">
                                        Parameters
                                      </h4>
                                      <div className="space-y-2">
                                        {viewControlDetail.params.map(
                                          (param) => {
                                            const overrideVal =
                                              overrideMap[param.paramId];
                                            return (
                                              <div
                                                key={param.id}
                                                className="rounded-md border border-border bg-muted/20 p-3 space-y-2"
                                              >
                                                <div className="flex items-center gap-2">
                                                  <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400">
                                                    {param.paramId}
                                                  </span>
                                                  {param.label && (
                                                    <span className="text-xs text-muted-foreground">
                                                      {param.label}
                                                    </span>
                                                  )}
                                                </div>
                                                {(() => {
                                                  const defaultVal =
                                                    param.values && param.values.length > 0
                                                      ? param.values.join(", ")
                                                      : param.selectConfig?.choice?.length
                                                        ? param.selectConfig.choice.join(", ")
                                                        : null;
                                                  if (overrideVal) {
                                                    // Only show default vs override when default exists and differs
                                                    if (defaultVal && defaultVal !== overrideVal) {
                                                      return (
                                                        <div className="space-y-1.5">
                                                          <div className="flex items-center gap-2">
                                                            <span className="text-xs font-medium text-muted-foreground">
                                                              Default Value:
                                                            </span>
                                                            <span className="text-xs text-foreground">
                                                              {defaultVal}
                                                            </span>
                                                          </div>
                                                          <div className="flex items-center gap-2">
                                                            <span className="text-xs font-medium text-muted-foreground">
                                                              Override Value:
                                                            </span>
                                                            <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                                                              {overrideVal}
                                                            </span>
                                                          </div>
                                                        </div>
                                                      );
                                                    }
                                                    // No distinct default available — just show override
                                                    return (
                                                      <div className="flex items-center gap-2">
                                                        <span className="text-xs font-medium text-muted-foreground">
                                                          Override Value:
                                                        </span>
                                                        <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                                                          {overrideVal}
                                                        </span>
                                                      </div>
                                                    );
                                                  }
                                                  if (defaultVal) {
                                                    return (
                                                      <div className="flex items-center gap-2">
                                                        <span className="text-xs font-medium text-muted-foreground">
                                                          Value:
                                                        </span>
                                                        <span className="text-xs text-foreground">
                                                          {defaultVal}
                                                        </span>
                                                      </div>
                                                    );
                                                  }
                                                  return null;
                                                })()}
                                                {param.guidelines &&
                                                  param.guidelines.length >
                                                  0 && (
                                                    <p className="text-xs text-muted-foreground">
                                                      {param.guidelines
                                                        .map(
                                                          (g: {
                                                            prose: string;
                                                          }) => g.prose,
                                                        )
                                                        .join("; ")}
                                                    </p>
                                                  )}
                                              </div>
                                            );
                                          },
                                        )}
                                      </div>
                                    </div>
                                  );
                                })()}

                              {/* Enhancements — hidden for ID-hierarchy catalogs
                                  (e.g. CIS) where the enhancements are already
                                  listed as separate controls in the accordion. */}
                              {!allowHierarchyDerive &&
                                viewControlDetail.enhancements?.length > 0 && (
                                <div className="space-y-2">
                                  <h4 className="text-sm font-semibold text-foreground capitalize">
                                    Enhancements
                                  </h4>
                                  <div className="space-y-2">
                                    {viewControlDetail.enhancements.map(
                                      (enh) => (
                                        <div
                                          key={enh.id}
                                          className="rounded-md border border-border bg-muted/20 p-3"
                                        >
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400">
                                              {enh.controlId.toUpperCase()}
                                            </span>
                                            <span className="text-sm font-medium">
                                              {enh.title}
                                            </span>
                                          </div>
                                          {enh.parts
                                            ?.filter(
                                              (p) => p.name === "statement",
                                            )
                                            .map((p) => (
                                              <p
                                                key={p.id}
                                                className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap"
                                              >
                                                {p.prose}
                                              </p>
                                            ))}
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Not yet loaded */}
                          {!viewControlLoading && !viewControlDetail && (
                            <p className="text-sm text-muted-foreground">
                              Unable to load control details.
                            </p>
                          )}
                        </div>
                      );
                    })()
                  ) : (
                    <div>
                      <div className="flex flex-col items-center justify-center py-16 text-center">
                        <FileText className="size-8 text-muted-foreground mb-3" />
                        <p className="text-sm font-medium text-muted-foreground">
                          Select a control to view details
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Click on a control to see its details, or click Edit
                          to modify the profile.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              }
            />
          ))}

        {/* ── Edit mode navigation ── */}
        {editMode && (
          <div className="flex items-center justify-between">
            {editWizardStep > 0 ? (
              <button
                onClick={() => setEditWizardStep((s) => Math.max(0, s - 1))}
                disabled={savingProfile}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors disabled:opacity-40"
              >
                Previous
              </button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={cancelEditMode}
                disabled={savingProfile}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={handleEditWizardNext}
                disabled={
                  savingProfile ||
                  (editWizardStep === 0 && editCheckedIds.size === 0)
                }
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20 disabled:opacity-50"
              >
                {savingProfile ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : editWizardStep < editWizardSteps.length - 1 ? (
                  <Save className="size-3.5" />
                ) : (
                  <Check className="size-3.5" />
                )}
                {savingProfile
                  ? "Saving…"
                  : editWizardStep < editWizardSteps.length - 1
                    ? "Next"
                    : "Save Profile"}
              </button>
            </div>
          </div>
        )}

        {/* Summary */}
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <div className="flex items-center gap-6 text-sm">
            <div>
              <span className="text-muted-foreground">Total Included: </span>
              <span className="font-mono font-bold text-emerald-600 dark:text-emerald-400">
                {editMode
                  ? editImportRows.reduce((sum, r) => sum + countLeafChecked(r.controls, r.checkedIds), 0)
                  : new Set(allIncludedIds.map((cid) => cid.toLowerCase())).size}
              </span>
            </div>
            {!editMode && (
              <div>
                <span className="text-muted-foreground">Total Excluded: </span>
                <span className="font-mono font-bold text-red-600 dark:text-red-400">
                  {allExcludedIds.length}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* ── Lifecycle Transition Confirmation Modal ── */}
        {transitionModal}
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════
     Resolved Profile View – Show resolved controls with workflow
  ════════════════════════════════════════════════════════ */
  if (selectedProfileRow?.resolved && selectedProfileRow) {
    return (
      <div className="space-y-6">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              navigate("/profiles");
              setSelectedProfile(null);
              setSelectedProfileRow(null);
              setResolvedControls([]);
            }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="size-3.5" />
            Profiles
          </button>
          <ChevronRight className="size-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">{selectedProfileRow.name}</span>
          {(() => {
            const lifecycle = selectedProfileRow.lifecycleStatus;
            const steps = [
              "draft",
              "in_review",
              "approved",
              "published",
            ] as const;
            const stepLabels: Record<string, string> = {
              draft: "Draft",
              in_review: "In Review",
              approved: "Approved",
              published: "Published",
            };
            const terminalStates: Record<
              string,
              { label: string; color: string }
            > = {
              rejected: { label: "Rejected", color: "bg-red-500" },
              superseded: { label: "Superseded", color: "bg-orange-500" },
              withdrawn: { label: "Withdrawn", color: "bg-rose-700" },
            };
            const terminal = terminalStates[lifecycle];
            const activeIdx = steps.indexOf(
              lifecycle as (typeof steps)[number],
            );
            return (
              <div className="ml-3 flex items-center gap-1">
                {terminal ? (
                  <>
                    {steps.map((s, i) => {
                      const isRejectedFromReview = lifecycle === "rejected";
                      const filledUpTo = isRejectedFromReview
                        ? 1
                        : steps.length;
                      const filled = i < filledUpTo;
                      return (
                        <React.Fragment key={s}>
                          <div
                            className={`flex items-center gap-1 ${filled ? "text-muted-foreground" : "text-muted-foreground/40"}`}
                          >
                            <div
                              className={`size-2 rounded-full ${filled ? "bg-muted-foreground/50" : "bg-muted-foreground/20"}`}
                            />
                            <span className="text-[10px] font-medium">
                              {stepLabels[s]}
                            </span>
                          </div>
                          {i < steps.length - 1 && (
                            <div
                              className={`w-4 h-px ${filled && i < filledUpTo - 1 ? "bg-muted-foreground/40" : "bg-muted-foreground/15"}`}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                    <div className="w-4 h-px bg-muted-foreground/15" />
                    <div className="flex items-center gap-1">
                      <div
                        className={`size-2 rounded-full ${terminal.color}`}
                      />
                      <span
                        className={`text-[10px] font-bold ${lifecycle === "rejected" ? "text-red-600 dark:text-red-400" : lifecycle === "superseded" ? "text-orange-600 dark:text-orange-400" : "text-rose-700 dark:text-rose-400"}`}
                      >
                        {terminal.label}
                      </span>
                    </div>
                  </>
                ) : (
                  steps.map((s, i) => {
                    const isCurrent = s === lifecycle;
                    const isPast = activeIdx >= 0 && i < activeIdx;
                    return (
                      <React.Fragment key={s}>
                        <div
                          className={`flex items-center gap-1 ${isCurrent && s === "published" ? "text-emerald-600 dark:text-emerald-400" : isCurrent ? "text-foreground" : isPast ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/60 dark:text-white"}`}
                        >
                          <div
                            className={`size-2 rounded-full ${isCurrent && s === "published" ? "bg-emerald-500 ring-2 ring-emerald-500/30" : isCurrent ? "bg-blue-500 ring-2 ring-blue-500/30" : isPast ? "bg-emerald-500" : "bg-muted-foreground/30 dark:bg-white/20"}`}
                          />
                          <span
                            className={`text-[10px] ${isCurrent ? "font-bold" : "font-medium"}`}
                          >
                            {stepLabels[s]}
                          </span>
                        </div>
                        {i < steps.length - 1 && (
                          <div
                            className={`w-4 h-px ${isPast ? "bg-emerald-500/50" : "bg-muted-foreground/20 dark:bg-white/15"}`}
                          />
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </div>
            );
          })()}
        </div>

        {/* Header with title and action buttons */}
        <div className="rounded-lg border border-border bg-card p-4 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold">
                {selectedProfileRow.name}
              </h2>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              {isEditable && (
                <button
                  onClick={() => enterEditMode()}
                  disabled={savingProfile}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border text-sm font-medium hover:bg-accent transition-colors disabled:opacity-40"
                >
                  <Pencil className="size-3.5" />
                  Edit
                </button>
              )}
              {(() => {
                const actions =
                  lifecycleActions[selectedProfileRow.lifecycleStatus] ?? [];
                return actions.map((action) => {
                  const requiresApprovePermission = ["Approve", "Reject"].includes(action.label);
                  const button = (
                    <Tip
                      key={action.target}
                      label={profileIsEmpty ? EMPTY_PROFILE_MSG : ""}
                      contentClassName="max-w-xs break-words"
                    >
                    <button
                      onClick={() =>
                        openTransitionModal(
                          action.target,
                          action.label,
                          action.requireComments,
                        )
                      }
                      disabled={savingProfile || profileIsEmpty}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-sm font-medium transition-colors shadow-md whitespace-nowrap ${action.color} disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {savingProfile ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        action.icon
                      )}
                      {action.label}
                    </button>
                    </Tip>
                  );

                  if (requiresApprovePermission) {
                    return (
                      <RBACAction key={action.target} module="profile" action="approve">
                        {button}
                      </RBACAction>
                    );
                  }
                  return button;
                });
              })()}
              {/* Resolve button - available at any stage if not already resolved */}
              {!selectedProfileRow?.resolved && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleResolveProfile}
                      disabled={resolving || profileIsEmpty}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-md text-white text-sm font-medium transition-colors shadow-md bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {resolving ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <PlayCircle className="size-4" />
                      )}
                      {resolving ? "Resolving…" : "Resolve"}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" sideOffset={6}>
                    {profileIsEmpty
                      ? EMPTY_PROFILE_MSG
                      : "You can resolve this profile at any stage"}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          {/* Profile metadata cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-2">
            <ProfStatCard
              icon={<Info className="size-4" />}
              label="OSCAL Version"
              value={selectedProfileRow.oscalVersion}
              color="sky"
            />
            <ProfStatCard
              icon={<FileText className="size-4" />}
              label="Version"
              value={formatVersion(selectedProfileRow.version)}
              color="violet"
            />
            <ProfStatCard
              icon={<Clock className="size-4" />}
              label="Created On"
              value={selectedProfileRow.created}
              color="emerald"
            />
            <ProfStatCard
              icon={<Clock className="size-4" />}
              label="Updated On"
              value={selectedProfileRow.updated}
              color="amber"
            />
            <ProfStatCard
              icon={<ShieldCheck className="size-4" />}
              label="Status"
              value={(selectedProfileRow.lifecycleStatus ?? "").replace(/_/g, " ")}
              color={profStatusColor(selectedProfileRow.lifecycleStatus)}
              valueClassName="capitalize"
            />
            <ProfStatCard
              icon={<Check className="size-4" />}
              label="Resolved"
              value={selectedProfileRow.resolved ? "Yes" : "No"}
              color={selectedProfileRow.resolved ? "emerald" : "slate"}
            />
            <ProfStatCard
              icon={<Layers className="size-4" />}
              label="Method"
              value={
                resolvedMethod
                  ? RESOLVED_METHOD_META[resolvedMethod.toLowerCase()]?.label ??
                    resolvedMethod
                  : "-"
              }
              color="indigo"
            />
          </div>
        </div>

        {/* Resolved Profile Section */}
        <div className="rounded-lg border border-border bg-card p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <ShieldCheck className="size-4 text-indigo-500" />
                Resolved Profile
                <span className="text-xs font-normal text-muted-foreground">
                  ({resolvedControls.length} controls)
                </span>
              </h3>
              <ResolvedGroupToolbar
                groupIds={getResolvedGroupIds(resolvedControls, resolvedMethod)}
                collapsedGroups={resolvedCollapsedGroups}
                onExpandAll={() => setResolvedCollapsedGroups(new Set())}
                onCollapseAll={(ids) =>
                  setResolvedCollapsedGroups(new Set(ids))
                }
              />
            </div>
            <div className="flex items-center gap-3">
              {resolvedControls.length > 0 && (
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    value={resolvedSearch}
                    onChange={(e) => setResolvedSearch(e.target.value)}
                    placeholder="Search controls…"
                    className="h-8 w-56 rounded-md border border-border bg-background pl-8 pr-7 text-xs outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-400"
                  />
                  {resolvedSearch && (
                    <button
                      type="button"
                      onClick={() => setResolvedSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="size-3.5" />
                    </button>
                  )}
                </div>
              )}
              {resolvedAt && (
                <span className="text-xs text-muted-foreground">
                  Resolved at:{" "}
                  <span className="font-medium text-foreground">
                    {resolvedAt.split("T")[0].split("-").reverse().join("/")}
                  </span>
                </span>
              )}
            </div>
          </div>

          {/* Show-controls-with-parameters toggle (matches Catalog/Profile) */}
          {resolvedParamCount > 0 && (
            <label className="flex items-center gap-2 cursor-pointer w-fit">
              <Switch
                checked={resolvedParamsOnly}
                onCheckedChange={(checked) => setResolvedParamsOnly(checked)}
              />
              <span className="text-[11px] font-medium text-muted-foreground select-none">
                Show Controls with Parameters ({resolvedParamCount})
              </span>
            </label>
          )}

          {resolvedLoading ? (
            <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              <span className="text-sm">Loading resolved catalog…</span>
            </div>
          ) : resolvedControls.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No resolved controls found.
            </p>
          ) : filteredResolvedControls.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {resolvedSearch
                ? `No controls match “${resolvedSearch}”.`
                : "No controls with parameters found."}
            </p>
          ) : (
            <ResolvedCatalogControls
              controls={filteredResolvedControls}
              method={resolvedMethod}
              collapsedGroups={resolvedCollapsedGroups}
              onToggleGroup={toggleResolvedGroup}
            />
          )}
        </div>

        {/* ── Lifecycle Transition Confirmation Modal ── */}
        {transitionModal}
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════
     Profile Builder Wizard – Full Page
  ════════════════════════════════════════════════════════ */
  if (view === "builder") {
    return (
      <>
        <div className="space-y-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                navigate("/profiles");
                setView("list");
                setBuilderStep(0);
                setProfileTitle("");
                setCreatedProfileId(null);
                setParamOverrides({});
                setAlterDirectives({});
                setAvailableControls([]);
                setShowAddForm({});
                setAddPartDraft({});
                setImportRows([]);
                setMergeAnalysis(null);
                setMergeSaved(false);
                setCombineMethod("keep");
                setStructuringDirective("as-is");
                setCustomOrder("keep");
                setAvailableParams([]);
              }}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="size-3.5" />
              Profiles
            </button>
            <ChevronRight className="size-3.5 text-muted-foreground" />
            <span className="text-sm font-medium">New Profile</span>
          </div>

          {/* Header */}
          <div className="rounded-lg border border-border bg-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold">New Profile</h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  Follow the steps to Create your OSCAL profile
                </p>
              </div>
            </div>
          </div>

          {/* Stepper */}
          <div className="rounded-lg border border-border bg-card p-5">
            <WorkflowStepper
              steps={builderSteps}
              currentStep={builderStep}
              onStepClick={(i) => setBuilderStep(i)}
            />
          </div>

          {/* Step content */}
          <div className="rounded-lg border border-border bg-card p-5">
            {builderStep === 0 && (
              <div className="space-y-4">
                <FormField
                  label="Profile Title"
                  required
                  hint="e.g. FedRAMP High Baseline, Custom Low Profile"
                >
                  <FormInput
                    placeholder="Enter profile title"
                    value={profileTitle}
                    onChange={(v) => setProfileTitle(v)}
                  />
                </FormField>
                <FormField label="Organization" required>
                  {accessibleOrgsLoading ? (
                    <div className="flex items-center gap-2 py-2">
                      <Loader2 className="size-4 animate-spin text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Loading</span>
                    </div>
                  ) : accessibleOrgs.length === 0 ? (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                      <span className="text-sm text-red-600 dark:text-red-400">No accessible organizations.</span>
                    </div>
                  ) : accessibleOrgs.length === 1 ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                      <span className="font-medium">{accessibleOrgs[0].name}</span>
                      <span className="text-muted-foreground">({accessibleOrgs[0].shortName})</span>
                    </div>
                  ) : (
                    <FormSelect
                      options={accessibleOrgs.map((o) => ({ value: o.id, label: `${o.name} (${o.shortName})` }))}
                      value={importOrgId}
                      onChange={setImportOrgId}
                      placeholder="Select organization"
                    />
                  )}
                </FormField>
                {/* Import Sources header + Add Source */}
                {(() => {
                  const allSourcesConfigured =
                    importRows.length > 0 &&
                    importRows.every((r) => r.sourceId != null);
                  const hasDuplicates = importRows.some(
                    (row, idx) =>
                      row.sourceId != null &&
                      importRows.some(
                        (other, otherIdx) =>
                          otherIdx !== idx &&
                          other.sourceType === row.sourceType &&
                          other.sourceId === row.sourceId,
                      ),
                  );
                  const canAddSource =
                    importRows.length === 0 ||
                    (allSourcesConfigured && !hasDuplicates);
                  return (
                    <>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <h3>Import Sources</h3>
                          {importRows.length > 0 && (
                            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300">
                              {importRows.length} source
                              {importRows.length > 1 ? "s" : ""}
                            </span>
                          )}
                        </div>
                        <Tip
                          label={
                            !canAddSource
                              ? "Complete the current source configuration before adding another"
                              : "Add a new import source"
                          }
                        >
                          <button
                            onClick={() =>
                              setImportRows((prev) => [
                                ...prev,
                                {
                                  key: nextImportRowKey(),
                                  sourceType: "CATALOG",
                                  sourceId: null,
                                  savedCatalogId: null,
                                  savedProfileId: null,
                                  controls: [],
                                  rawControls: [],
                                  checkedIds: new Set(),
                                  loading: false,
                                },
                              ])
                            }
                            disabled={!canAddSource}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-indigo-500/30 dark:border-indigo-500/30 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                          >
                            <Plus className="size-3.5" /> Add Source
                          </button>
                        </Tip>
                      </div>
                      {importRows.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-10 rounded-lg border-2 border-dashed border-border">
                          <Layers className="size-8 text-muted-foreground/40 mb-3" />
                          <p className="text-sm font-medium text-muted-foreground mb-1">
                            No import sources added
                          </p>
                          <p className="text-xs text-muted-foreground/70">
                            Click "+ Add Source" to begin importing from
                            catalogs or profiles.
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {importRows.map((row, idx) => {
                            const isDuplicate =
                              row.sourceId != null &&
                              importRows.findIndex(
                                (r) =>
                                  r.sourceType === row.sourceType &&
                                  r.sourceId === row.sourceId,
                              ) !== idx;
                            return (
                              <div
                                key={row.key}
                                className={`relative rounded-xl border-2 ${isDuplicate ? "border-red-500/50" : "border-border"} bg-card overflow-hidden transition-all`}
                              >
                                {/* Card header */}
                                <div className="flex items-center justify-between px-4 py-2.5 bg-muted/30 border-b border-border">
                                  <div className="flex items-center gap-2.5">
                                    <span className="flex items-center justify-center size-6 rounded-full text-[11px] font-bold text-white bg-indigo-600 dark:bg-indigo-600">
                                      {idx + 1}
                                    </span>
                                    <span className="text-xs font-semibold text-muted-foreground tracking-wide">
                                      Source {idx + 1}
                                    </span>
                                  </div>
                                  <Tip label="Remove source">
                                    <button
                                      onClick={() =>
                                        setImportRows((prev) =>
                                          prev.filter((_, i) => i !== idx),
                                        )
                                      }
                                      className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-500 transition-colors"
                                    >
                                      <Trash2 className="size-4" />
                                    </button>
                                  </Tip>
                                </div>
                                {/* Card body */}
                                <div className="px-4 py-4 space-y-4">
                                  {/* Source type toggle */}
                                  <div className="grid grid-cols-[80px_1fr] items-center gap-2">
                                    <span className="text-xs font-medium text-muted-foreground">
                                      Type
                                    </span>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() =>
                                          setImportRows((prev) =>
                                            prev.map((r, i) =>
                                              i === idx
                                                ? {
                                                  ...r,
                                                  sourceType: "CATALOG",
                                                  sourceId: r.savedCatalogId,
                                                  sourceTitle:
                                                    r.savedCatalogTitle,
                                                  savedCatalogId: null,
                                                  savedCatalogTitle:
                                                    undefined,
                                                  // only preserve profile if no catalog was ever saved
                                                  // (meaning user never committed to a catalog)
                                                  savedProfileId:
                                                    r.savedCatalogId
                                                      ? null
                                                      : r.sourceId,
                                                  savedProfileTitle:
                                                    r.savedCatalogId
                                                      ? undefined
                                                      : r.sourceTitle,
                                                  controls: [],
                                                  rawControls: [],
                                                  checkedIds: new Set(),
                                                }
                                                : r,
                                            ),
                                          )
                                        }
                                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${row.sourceType === "CATALOG" ? "bg-indigo-600 dark:bg-indigo-600 text-white shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20" : "bg-muted/60 text-muted-foreground hover:bg-muted"}`}
                                      >
                                        <Layers className="size-3" />
                                        Catalog
                                      </button>
                                      <button
                                        onClick={() => {
                                          setImportRows((prev) =>
                                            prev.map((r, i) =>
                                              i === idx
                                                ? {
                                                  ...r,
                                                  sourceType: "PROFILE",
                                                  // only preserve catalog if no profile was ever saved
                                                  // (meaning user never committed to a profile)
                                                  savedCatalogId:
                                                    r.savedProfileId
                                                      ? null
                                                      : r.sourceId,
                                                  savedCatalogTitle:
                                                    r.savedProfileId
                                                      ? undefined
                                                      : r.sourceTitle,
                                                  savedProfileId: null,
                                                  savedProfileTitle:
                                                    undefined,
                                                  sourceId: r.savedProfileId,
                                                  sourceTitle:
                                                    r.savedProfileTitle,
                                                  controls: [],
                                                  rawControls: [],
                                                  checkedIds: new Set(),
                                                }
                                                : r,
                                            ),
                                          );
                                          fetchResolvedProfilesForBuilder();
                                        }}
                                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${row.sourceType === "PROFILE" ? "bg-indigo-600 dark:bg-indigo-600 text-white shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20" : "bg-muted/60 text-muted-foreground hover:bg-muted"}`}
                                      >
                                        <ShieldCheck className="size-3" />
                                        Profile
                                      </button>
                                    </div>
                                  </div>
                                  {/* Source selector */}
                                  <div className="grid grid-cols-[80px_1fr] items-start gap-2">
                                    <span className="text-xs font-medium text-muted-foreground mt-2.5">
                                      Source
                                    </span>
                                    <div className="space-y-2">
                                      {row.sourceType === "CATALOG" ? (
                                        catalogsLoading ? (
                                          <div className="h-10 rounded-lg bg-muted animate-pulse" />
                                        ) : (
                                          <Dropdown
                                            variant="form"
                                            placeholder="Select a catalog"
                                            className={`w-full ${isDuplicate ? "border-red-500/50 bg-red-500/[0.03]" : ""}`}
                                            value={row.sourceId ?? ""}
                                            onChange={(v) => {
                                              const id = v || null;
                                              const cat = catalogs.find(
                                                (c) => c.id === id,
                                              );
                                              setImportRows((prev) =>
                                                prev.map((r, i) =>
                                                  i === idx
                                                    ? {
                                                      ...r,
                                                      sourceId: id,
                                                      sourceTitle: cat?.title,
                                                      savedProfileId: id
                                                        ? null
                                                        : r.savedProfileId,
                                                      savedProfileTitle: id
                                                        ? undefined
                                                        : r.savedProfileTitle,
                                                      controls: [],
                                                      rawControls: [],
                                                      checkedIds: new Set(),
                                                    }
                                                    : r,
                                                ),
                                              );
                                            }}
                                            options={catalogs
                                              .filter(
                                                (c) =>
                                                  c.lifecycleStatus ===
                                                  "published",
                                              )
                                              .map((cat) => ({
                                                value: cat.id,
                                                label: cat.title || cat.id,
                                              }))}
                                          />
                                        )
                                      ) : resolvedProfilesLoading ? (
                                        <div className="h-10 rounded-lg bg-muted animate-pulse" />
                                      ) : (
                                        <Dropdown
                                          variant="form"
                                          placeholder="Select a resolved profile"
                                          className={`w-full ${isDuplicate ? "border-red-500/50 bg-red-500/[0.03]" : ""}`}
                                          value={row.sourceId ?? ""}
                                          onChange={(v) => {
                                            const id = v || null;
                                            const prof =
                                              resolvedProfilesList.find(
                                                (p) => p.id === id,
                                              );
                                            setImportRows((prev) =>
                                              prev.map((r, i) =>
                                                i === idx
                                                  ? {
                                                    ...r,
                                                    sourceId: id,
                                                    sourceTitle: prof?.title,
                                                    savedCatalogId: id
                                                      ? null
                                                      : r.savedCatalogId,
                                                    savedCatalogTitle: id
                                                      ? undefined
                                                      : r.savedCatalogTitle,
                                                    controls: [],
                                                    rawControls: [],
                                                    checkedIds: new Set(),
                                                  }
                                                  : r,
                                              ),
                                            );
                                          }}
                                          options={resolvedProfilesList.map((p) => ({
                                            value: p.id,
                                            label: p.title || p.id,
                                          }))}
                                        />
                                      )}
                                      {/* Validation messages below the select */}
                                      {isDuplicate && (
                                        <div className="flex items-center gap-1.5 text-xs text-red-500">
                                          <AlertTriangle className="size-3 shrink-0" />
                                          <span>
                                            This{" "}
                                            {row.sourceType === "CATALOG"
                                              ? "catalog"
                                              : "profile"}{" "}
                                            is already added as another source.
                                            Please select a different one.
                                          </span>
                                        </div>
                                      )}
                                      {row.sourceId && !isDuplicate && (
                                        <div className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
                                          <CheckCircle className="size-3" />
                                          {row.sourceType === "CATALOG"
                                            ? "Catalog"
                                            : "Profile"}
                                          : {row.sourceTitle ?? row.sourceId}
                                        </div>
                                      )}
                                      {!row.sourceId && (
                                        <p className="text-[11px] text-muted-foreground/60 italic">
                                          Select a{" "}
                                          {row.sourceType === "CATALOG"
                                            ? "catalog"
                                            : "resolved profile"}{" "}
                                          to configure this source.
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
            {builderStep === 1 &&
              (() => {
                const activeRow = importRows[activeImportIdx];
                const totalChecked = importRows.reduce(
                  (sum, r) => sum + countLeafChecked(r.controls, r.checkedIds),
                  0,
                );
                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3>Include / Exclude Controls</h3>
                      <span className="text-sm text-muted-foreground">
                        {totalChecked} selected
                      </span>
                    </div>
                    {/* Source tabs */}
                    {importRows.length > 1 && (
                      <div className="flex gap-1 border-b border-border">
                        {importRows.map((row, idx) => (
                          <button
                            key={row.key}
                            onClick={() => setActiveImportIdx(idx)}
                            className={`px-3 py-1.5 text-xs font-medium border-b-2 transition-colors ${idx === activeImportIdx ? "border-indigo-600 dark:border-indigo-500 text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                          >
                            {row.sourceType === "CATALOG"
                              ? "Catalog"
                              : "Profile"}
                            : {row.sourceTitle ?? `Source ${idx + 1}`}
                            {row.checkedIds.size > 0 && (
                              <span className="ml-1.5 text-[10px] font-mono">
                                (
                                {countLeafChecked(row.controls, row.checkedIds)}
                                )
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                    {activeRow && (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="rounded-md border border-border overflow-hidden">
                          <div className="px-3 py-2 border-b border-border bg-muted/50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <p className="text-xs font-medium text-muted-foreground ">
                                Available Controls
                              </p>
                              <button
                                onClick={() => {
                                  setBuilderExpandAll(true);
                                  setBuilderExpandTrigger((t) => t + 1);
                                }}
                                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-muted-foreground hover:bg-accent transition-colors"
                              >
                                <ChevronDown className="size-2.5" /> Expand All
                              </button>
                              <button
                                onClick={() => {
                                  setBuilderExpandAll(false);
                                  setBuilderExpandTrigger((t) => t + 1);
                                }}
                                className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium text-muted-foreground hover:bg-accent transition-colors"
                              >
                                <ChevronRight className="size-2.5" /> Collapse
                                All
                              </button>
                            </div>
                            {activeRow.controls.length > 0 &&
                              (() => {
                                const allIds = collectAllIds(
                                  activeRow.controls,
                                );
                                const allSelected =
                                  allIds.length > 0 &&
                                  allIds.every((id) =>
                                    activeRow.checkedIds.has(id),
                                  );
                                return (
                                  <label className="flex items-center gap-1.5 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={allSelected}
                                      onChange={() => {
                                        const newChecked = allSelected
                                          ? new Set<string>()
                                          : new Set(allIds);
                                        setImportRows((prev) =>
                                          prev.map((r, i) =>
                                            i === activeImportIdx
                                              ? { ...r, checkedIds: newChecked }
                                              : r,
                                          ),
                                        );
                                      }}
                                      className="size-3.5 accent-foreground"
                                    />
                                    <span className="text-xs text-muted-foreground">
                                      Select All
                                    </span>
                                  </label>
                                );
                              })()}
                          </div>
                          <div className="p-2 max-h-72 overflow-y-auto">
                            {activeRow.loading ? (
                              <SkeletonList rows={8} className="py-2" />
                            ) : activeRow.controls.length > 0 ? (
                              <ControlTreeView
                                nodes={activeRow.controls}
                                checkable
                                checkedIds={activeRow.checkedIds}
                                onCheck={handleCheck}
                                expandAll={builderExpandAll}
                                expandTrigger={builderExpandTrigger}
                              />
                            ) : (
                              <p className="text-xs text-muted-foreground text-center py-8">
                                No controls found in this source.
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="rounded-md border border-border overflow-hidden">
                          <div className="px-3 py-2 border-b border-border bg-muted/50">
                            <p className="text-xs font-medium text-muted-foreground ">
                              Included (
                              {countLeafChecked(
                                activeRow.controls,
                                activeRow.checkedIds,
                              )}
                              )
                            </p>
                          </div>
                          <div className="p-2 max-h-72 overflow-y-auto">
                            {(() => {
                              const filteredTree: ControlNode[] = [];
                              for (const group of activeRow.controls) {
                                if (
                                  group.children &&
                                  group.children.length > 0
                                ) {
                                  const selectedChildren =
                                    group.children.filter(
                                      (child: ControlNode) =>
                                        activeRow.checkedIds.has(child.id),
                                    );
                                  if (selectedChildren.length > 0) {
                                    filteredTree.push({
                                      ...group,
                                      children: selectedChildren,
                                    });
                                  }
                                } else if (activeRow.checkedIds.has(group.id)) {
                                  filteredTree.push(group);
                                }
                              }
                              return filteredTree.length > 0 ? (
                                <ControlTreeView nodes={filteredTree} />
                              ) : (
                                <p className="text-xs text-muted-foreground text-center py-8">
                                  No controls selected.
                                </p>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            {/* ── Step 2: Merge Configuration ── */}
            {builderStep === 2 && (
              <div className="space-y-6">
                {mergeAnalysisLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="size-6 animate-spin text-indigo-500 mr-2" />
                    <span className="text-sm text-muted-foreground">
                      Analyzing merge conflicts…
                    </span>
                  </div>
                ) : (
                  <>
                    {/* Step 3a: Combine Method */}
                    {mergeAnalysis &&
                      mergeAnalysis.totalOverlappingControls > 0 ? (
                      <div className="space-y-4">
                        <h3>Combine Method</h3>
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20 text-xs text-amber-700 dark:text-amber-400">
                          <AlertTriangle className="size-3.5 shrink-0" />
                          <span>
                            <strong>
                              {mergeAnalysis.totalOverlappingControls}
                            </strong>{" "}
                            control
                            {mergeAnalysis.totalOverlappingControls > 1
                              ? "s"
                              : ""}{" "}
                            appear in multiple sources
                          </span>
                        </div>
                        {/* Overlapping controls list */}
                        <div className="rounded-md border border-border overflow-hidden">
                          <div className="px-3 py-2 bg-muted/50 border-b border-border">
                            <p className="text-xs font-medium text-muted-foreground ">
                              Overlapping Controls
                            </p>
                          </div>
                          <div className="divide-y divide-border max-h-48 overflow-y-auto">
                            {mergeAnalysis.overlappingControls.map((oc) => (
                              <div
                                key={oc.controlId}
                                className="px-3 py-2 flex items-center justify-between"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-xs font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400">
                                    {oc.controlId?.toUpperCase()}
                                  </span>
                                  <span className="text-sm">
                                    {oc.controlTitle}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  {oc.sources.map((s) => (
                                    <span
                                      key={s.importId}
                                      className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                                    >
                                      {s.sourceTitle}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Combine method selection */}
                        <div className="space-y-2">
                          <p className="text-sm font-medium">
                            How should duplicate controls be handled?
                          </p>
                          <div className="space-y-2">
                            <label
                              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${combineMethod === "use-first"
                                  ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10"
                                  : "border-border hover:border-indigo-300 dark:hover:border-indigo-500/40"
                                }`}
                            >
                              <input
                                type="radio"
                                name="combineMethod"
                                value="use-first"
                                checked={combineMethod === "use-first"}
                                onChange={() => setCombineMethod("use-first")}
                                className="mt-0.5 accent-indigo-600"
                              />
                              <div>
                                <p className="text-sm font-medium">Use First</p>
                                <p className="text-xs text-muted-foreground">
                                  Keep the first occurrence, discard later
                                  duplicates (recommended)
                                </p>
                              </div>
                            </label>
                            <label
                              className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${combineMethod === "keep"
                                  ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10"
                                  : "border-border hover:border-indigo-300 dark:hover:border-indigo-500/40"
                                }`}
                            >
                              <input
                                type="radio"
                                name="combineMethod"
                                value="keep"
                                checked={combineMethod === "keep"}
                                onChange={() => setCombineMethod("keep")}
                                className="mt-0.5 accent-indigo-600"
                              />
                              <div>
                                <p className="text-sm font-medium">Keep</p>
                                <p className="text-xs text-muted-foreground">
                                  Retain all versions (may cause clashes)
                                </p>
                              </div>
                            </label>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <h3>Combine Method</h3>
                        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-xs text-emerald-700 dark:text-emerald-400">
                          <CheckCircle className="size-3.5" />
                          No conflicting controls found across your sources
                        </div>
                      </div>
                    )}

                    {/* Divider */}
                    <div className="border-t border-border" />

                    {/* Step 3b: Structuring Directive (always shown) */}
                    <div className="space-y-4">
                      <h3>Structuring Directive</h3>
                      <p className="text-sm text-muted-foreground">
                        How should the resolved controls be organized?
                      </p>
                      <div className="space-y-2">
                        <label
                          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${structuringDirective === "flat"
                              ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10"
                              : "border-border hover:border-indigo-300 dark:hover:border-indigo-500/40"
                            }`}
                        >
                          <input
                            type="radio"
                            name="structuringDirective"
                            value="flat"
                            checked={structuringDirective === "flat"}
                            onChange={() => setStructuringDirective("flat")}
                            className="mt-0.5 accent-indigo-600"
                          />
                          <div>
                            <p className="text-sm font-medium">Flat</p>
                            <p className="text-xs text-muted-foreground">
                              All controls as a flat list, no grouping
                            </p>
                          </div>
                        </label>
                        <label
                          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${structuringDirective === "as-is"
                              ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10"
                              : "border-border hover:border-indigo-300 dark:hover:border-indigo-500/40"
                            }`}
                        >
                          <input
                            type="radio"
                            name="structuringDirective"
                            value="as-is"
                            checked={structuringDirective === "as-is"}
                            onChange={() => setStructuringDirective("as-is")}
                            className="mt-0.5 accent-indigo-600"
                          />
                          <div>
                            <p className="text-sm font-medium">As-Is</p>
                            <p className="text-xs text-muted-foreground">
                              Preserve original grouping from source catalogs
                              (recommended)
                            </p>
                          </div>
                        </label>
                        <label
                          className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${structuringDirective === "custom"
                              ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10"
                              : "border-border hover:border-indigo-300 dark:hover:border-indigo-500/40"
                            }`}
                        >
                          <input
                            type="radio"
                            name="structuringDirective"
                            value="custom"
                            checked={structuringDirective === "custom"}
                            onChange={() => setStructuringDirective("custom")}
                            className="mt-0.5 accent-indigo-600"
                          />
                          <div>
                            <p className="text-sm font-medium">Custom</p>
                            <p className="text-xs text-muted-foreground">
                              Configure how resolved controls are organized in
                              the resulting profile.
                            </p>
                          </div>
                        </label>
                      </div>
                      {structuringDirective === "custom" && (
                        <div className="space-y-3 rounded-lg border border-indigo-200 dark:border-indigo-500/30 bg-indigo-50/40 dark:bg-indigo-500/5 p-4">
                          <p className="text-sm font-medium text-foreground">
                            Control Sort Order
                          </p>
                          <p className="text-xs text-muted-foreground">
                            How should controls be ordered in the resolved
                            output?
                          </p>
                          <div className="space-y-2">
                            {(["keep", "ascending", "descending"] as const).map(
                              (opt) => (
                                <label
                                  key={opt}
                                  className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${customOrder === opt
                                      ? "border-indigo-500 bg-indigo-50/50 dark:bg-indigo-500/10"
                                      : "border-border hover:border-indigo-300 dark:hover:border-indigo-500/40"
                                    }`}
                                >
                                  <input
                                    type="radio"
                                    name="customOrder"
                                    value={opt}
                                    checked={customOrder === opt}
                                    onChange={() => setCustomOrder(opt)}
                                    className="mt-0.5 accent-indigo-600"
                                  />
                                  <div>
                                    <p className="text-sm font-medium capitalize">
                                      {opt === "keep"
                                        ? "Keep Original Order"
                                        : opt === "ascending"
                                          ? "Ascending (A → Z)"
                                          : "Descending (Z → A)"}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {opt === "keep" &&
                                        "Preserve the order controls appear in their source catalog"}
                                      {opt === "ascending" &&
                                        "Sort controls alphabetically by control ID (ac-1, ac-2, …)"}
                                      {opt === "descending" &&
                                        "Sort controls in reverse alphabetical order (z-9, …, a-1)"}
                                    </p>
                                  </div>
                                </label>
                              ),
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {builderStep === 4 &&
              (() => {
                // Build a flat lookup of all rawControls from import rows for statement/guidance
                const allRawControls = importRows.flatMap((r) => r.rawControls);
                const getRawControl = (
                  controlId: string,
                ): ControlResponse | undefined => {
                  const key = controlId.toUpperCase();
                  for (const rc of allRawControls) {
                    if (rc.controlId.toUpperCase() === key) return rc;
                    const enh = rc.enhancements?.find(
                      (e) => e.controlId.toUpperCase() === key,
                    );
                    if (enh) return enh;
                  }
                  return undefined;
                };

                // Only show parameters for controls actually selected by the user
                const allCheckedIds = new Set(
                  importRows.flatMap((r) =>
                    [...r.checkedIds].map((id) => id.toUpperCase()),
                  ),
                );
                const filteredParams =
                  allCheckedIds.size > 0
                    ? availableParams.filter((p) =>
                      allCheckedIds.has(p.controlId.toUpperCase()),
                    )
                    : availableParams;

                // Group available params by controlId (from GET /profiles/{id}/available-parameters)
                const controlParamGroups: {
                  controlId: string;
                  title: string;
                  statement: string;
                  guidance: string;
                  params: AvailableParameter[];
                }[] = [];
                const groupMap = new Map<
                  string,
                  (typeof controlParamGroups)[0]
                >();
                for (const p of filteredParams) {
                  const key = p.controlId.toUpperCase();
                  if (!groupMap.has(key)) {
                    // Prefer API-provided statement/guidance; fall back to rawControls parts
                    let statement = p.controlStatement ?? "";
                    let guidance = p.controlGuidance ?? "";
                    if (!statement || !guidance) {
                      const raw = getRawControl(key);
                      if (raw?.parts) {
                        if (!statement) {
                          statement = raw.parts
                            .filter((pt) => pt.name === "statement")
                            .map((pt) => pt.prose)
                            .filter(Boolean)
                            .join(" ");
                        }
                        if (!guidance) {
                          guidance = raw.parts
                            .filter((pt) => pt.name === "guidance")
                            .map((pt) => pt.prose)
                            .filter(Boolean)
                            .join(" ");
                        }
                      }
                    }
                    const group = {
                      controlId: key,
                      title: p.controlTitle ?? key,
                      statement,
                      guidance,
                      params: [] as AvailableParameter[],
                    };
                    groupMap.set(key, group);
                    controlParamGroups.push(group);
                  }
                  groupMap.get(key)!.params.push(p);
                }

                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3>Parameter Overrides</h3>
                      <span className="text-sm text-muted-foreground">
                        {availableParamsLoading
                          ? "Loading…"
                          : `${filteredParams.length} parameters`}
                      </span>
                    </div>
                    {availableParamsLoading ? (
                      <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
                        <Loader2 className="size-4 animate-spin" />
                        <span className="text-sm">Loading parameters…</span>
                      </div>
                    ) : filteredParams.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-8 text-center">
                        No parameters found for the selected controls.
                      </p>
                    ) : (
                      <div className="space-y-5 max-h-[480px] overflow-y-auto pr-1">
                        {controlParamGroups.map((group) => (
                          <div
                            key={group.controlId}
                            className="rounded-lg border border-border overflow-hidden"
                          >
                            {/* Control header with statement and guidance */}
                            <div className="bg-muted/40 px-4 py-3 space-y-2 border-b border-border">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400">
                                  {group.controlId?.toUpperCase()}
                                </span>
                                <span className="text-sm font-semibold">
                                  {group.title}
                                </span>
                              </div>
                              {group.statement && (
                                <div>
                                  <p className="text-[11px] font-bold text-muted-foreground tracking-wider mb-1">
                                    Statement
                                  </p>
                                  <p className="text-sm text-muted-foreground leading-relaxed">
                                    {group.statement}
                                  </p>
                                </div>
                              )}
                              {group.guidance && (
                                <div>
                                  <p className="text-[11px] font-bold text-muted-foreground tracking-wider mb-1">
                                    Guidance
                                  </p>
                                  <p className="text-sm text-muted-foreground leading-relaxed">
                                    {group.guidance}
                                  </p>
                                </div>
                              )}
                            </div>
                            {/* Parameters for this control */}
                            <div className="divide-y divide-border">
                              {group.params.map((p) => {
                                const isNumeric =
                                  p.clazz === "num" || p.clazz === "int";
                                const catalogValues = p.values ?? [];
                                const maxNumeric =
                                  isNumeric && catalogValues.length > 0
                                    ? Number(catalogValues[0])
                                    : null;
                                const currentValue =
                                  paramOverrides[p.paramId] ??
                                  catalogValues.join(", ");
                                return (
                                  <div
                                    key={p.paramId}
                                    className="rounded-md border border-border p-3 space-y-2"
                                  >
                                    {/* Header: controlId + paramId + clazz */}
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-mono text-muted-foreground">
                                        {p.controlId?.toUpperCase()}
                                      </span>
                                      <span className="text-xs text-muted-foreground">
                                        {p.paramId}
                                      </span>
                                      {p.clazz && (
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 font-medium">
                                          {p.clazz}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm font-medium">
                                      {p.label}
                                    </p>
                                    {p.usage && (
                                      <p className="text-xs text-muted-foreground">
                                        {p.usage}
                                      </p>
                                    )}

                                    {/* Catalog default */}
                                    {catalogValues.length > 0 && (
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-[10px] font-bold text-muted-foreground">
                                          Default:
                                        </span>
                                        {catalogValues.map((v, vi) => (
                                          <span
                                            key={vi}
                                            className="text-xs px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-mono"
                                          >
                                            {v}
                                          </span>
                                        ))}
                                      </div>
                                    )}

                                    {/* Tighten-only hint */}
                                    {isNumeric && maxNumeric !== null && (
                                      <p className="text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
                                        <AlertTriangle className="size-3" />
                                        You can only set equal or lower than{" "}
                                        {maxNumeric}
                                      </p>
                                    )}

                                    {/* Override input */}
                                    {p.selectConfig ? (
                                      <div className="space-y-1">
                                        <span className="text-[10px] text-muted-foreground">
                                          {p.selectConfig["how-many"] === "one"
                                            ? "Select one:"
                                            : "Select one or more:"}
                                        </span>
                                        <div className="flex flex-wrap gap-1.5">
                                          {p.selectConfig.choice.map((c) => {
                                            const overrideValues = currentValue
                                              ? currentValue
                                                .split(",")
                                                .map((s) => s.trim())
                                                .filter(Boolean)
                                              : [];
                                            const selected =
                                              overrideValues.includes(c);
                                            return (
                                              <button
                                                key={c}
                                                type="button"
                                                onClick={() => {
                                                  let newValues: string[];
                                                  if (
                                                    p.selectConfig![
                                                    "how-many"
                                                    ] === "one"
                                                  ) {
                                                    newValues = selected
                                                      ? []
                                                      : [c];
                                                  } else {
                                                    newValues = selected
                                                      ? overrideValues.filter(
                                                        (v) => v !== c,
                                                      )
                                                      : [...overrideValues, c];
                                                  }
                                                  setParamOverrides((prev) => ({
                                                    ...prev,
                                                    [p.paramId]:
                                                      newValues.join(", "),
                                                  }));
                                                }}
                                                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${selected
                                                    ? "bg-indigo-100 dark:bg-indigo-500/20 border-indigo-400 dark:border-indigo-500 text-indigo-700 dark:text-indigo-300 font-semibold"
                                                    : "bg-muted/50 border-border text-muted-foreground hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                                                  }`}
                                              >
                                                {selected && (
                                                  <Check className="size-3 inline mr-0.5 -mt-0.5" />
                                                )}
                                                {c}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    ) : (
                                      <input
                                        type={isNumeric ? "number" : "text"}
                                        value={currentValue}
                                        onChange={(e) =>
                                          setParamOverrides((prev) => ({
                                            ...prev,
                                            [p.paramId]: e.target.value,
                                          }))
                                        }
                                        max={maxNumeric ?? undefined}
                                        placeholder="Enter value"
                                        className="w-full text-sm bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-foreground transition-colors"
                                      />
                                    )}

                                    {/* Constraints */}
                                    {(p.constraints?.length ?? 0) > 0 && (
                                      <div className="space-y-1">
                                        {p.constraints!.map((con, ci) => (
                                          <div
                                            key={ci}
                                            className="flex items-start gap-1.5 text-[10px] text-muted-foreground bg-muted/40 rounded px-2 py-1"
                                          >
                                            <Info className="size-3 mt-0.5 shrink-0" />
                                            <div>
                                              {con.description && (
                                                <span>{con.description}</span>
                                              )}
                                              {con.tests?.map((t, ti) => (
                                                <span
                                                  key={ti}
                                                  className="block font-mono text-muted-foreground/70"
                                                >
                                                  {t.remarks ||
                                                    (t.expression
                                                      ? `Pattern: ${t.expression}`
                                                      : "")}
                                                </span>
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Guidelines */}
                                    {(p.guidelines?.length ?? 0) > 0 && (
                                      <div className="space-y-0.5">
                                        {p.guidelines!.map((g, gi) => (
                                          <p
                                            key={gi}
                                            className="text-xs text-muted-foreground italic"
                                          >
                                            {g.prose}
                                          </p>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            {builderStep === 3 &&
              (() => {
                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3>Alter Directives</h3>
                      <span className="text-sm text-muted-foreground">
                        {availableControlsLoading
                          ? "Loading…"
                          : `${availableControls.length} controls`}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Add new parts to controls or mark existing parts for
                      removal. Changes are applied at resolve time — the source
                      catalog is never modified.
                    </p>
                    {availableControlsLoading ? (
                      <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
                        <Loader2 className="size-4 animate-spin" />
                        <span className="text-sm">Loading controls…</span>
                      </div>
                    ) : availableControls.length === 0 ? (
                      <p className="text-sm text-muted-foreground py-8 text-center">
                        No controls found for alter configuration.
                      </p>
                    ) : (
                      <div className="space-y-4 max-h-[520px] overflow-y-auto pr-1">
                        {availableControls.map((ctrl) => {
                          const dir = alterDirectives[ctrl.controlId] ?? {
                            adds: [],
                            removes: [],
                          };
                          const draft = addPartDraft[ctrl.controlId] ?? {
                            position: "ending",
                            byId: "",
                            name: "",
                            title: "",
                            prose: "",
                            params: [],
                          };
                          const draftParamIds = extractParamIdsFromInlineProse(
                            draft.prose,
                          );
                          const definedDraftParamIds = new Set(
                            (draft.params ?? [])
                              .map((p) => p.paramId)
                              .filter(Boolean),
                          );
                          const nextDraftParamId = draftParamIds.find(
                            (id) => !definedDraftParamIds.has(id),
                          );
                          const canAddDraftParam = draftParamIds.length > 0;
                          const isShowingForm = !!showAddForm[ctrl.controlId];
                          const removedByIds = new Set(
                            dir.removes.map((r) => r.byId).filter(Boolean),
                          );
                          const removedByNames = new Set(
                            dir.removes.map((r) => r.name).filter(Boolean),
                          );
                          const hasChanges =
                            dir.adds.length > 0 || dir.removes.length > 0;

                          return (
                            <div
                              key={ctrl.controlId}
                              className="rounded-lg border border-border overflow-hidden"
                            >
                              {/* Control header */}
                              <div className="bg-muted/40 px-4 py-3 flex items-center gap-2 border-b border-border">
                                <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400">
                                  {ctrl.controlId?.toUpperCase()}
                                </span>
                                <span className="text-sm font-semibold">
                                  {ctrl.controlTitle}
                                </span>
                                {hasChanges && (
                                  <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 font-medium">
                                    {dir.adds.length > 0 &&
                                      `+${dir.adds.length}`}
                                    {dir.adds.length > 0 &&
                                      dir.removes.length > 0 &&
                                      " "}
                                    {dir.removes.length > 0 &&
                                      `-${dir.removes.length}`}
                                  </span>
                                )}
                              </div>

                              <div className="p-4 space-y-3">
                                {/* Existing parts */}
                                {ctrl.parts.length > 0 && (
                                  <div className="space-y-1">
                                    <p className="text-[10px] font-bold text-muted-foreground tracking-wider">
                                      Existing Parts
                                    </p>
                                    <div className="rounded border border-border divide-y divide-border">
                                      {ctrl.parts.map((part) => {
                                        const isRemoved =
                                          removedByIds.has(part.partId) ||
                                          removedByNames.has(part.name);
                                        return (
                                          <div
                                            key={part.partId}
                                            className={`flex items-center justify-between px-3 py-2 ${isRemoved ? "opacity-40" : ""}`}
                                          >
                                            <div className="flex items-center gap-2">
                                              <span
                                                className={`text-xs font-medium ${isRemoved ? "line-through" : ""}`}
                                              >
                                                {part.name}
                                              </span>
                                              <span className="text-[10px] text-muted-foreground font-mono">
                                                {part.partId}
                                              </span>
                                            </div>
                                            {isRemoved ? (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  setAlterDirectives(
                                                    (prev) => ({
                                                      ...prev,
                                                      [ctrl.controlId]: {
                                                        ...(prev[
                                                          ctrl.controlId
                                                        ] ?? {
                                                          adds: [],
                                                          removes: [],
                                                        }),
                                                        removes: (
                                                          prev[ctrl.controlId]
                                                            ?.removes ?? []
                                                        ).filter(
                                                          (r) =>
                                                            r.byId !==
                                                            part.partId &&
                                                            r.name !==
                                                            part.name,
                                                        ),
                                                      },
                                                    }),
                                                  )
                                                }
                                                className="text-[10px] text-muted-foreground hover:text-foreground transition-colors px-1.5 py-0.5 rounded border border-border"
                                              >
                                                Undo
                                              </button>
                                            ) : (
                                              <Tip label="Remove this part at resolve time">
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    setAlterDirectives(
                                                      (prev) => ({
                                                        ...prev,
                                                        [ctrl.controlId]: {
                                                          ...(prev[
                                                            ctrl.controlId
                                                          ] ?? {
                                                            adds: [],
                                                            removes: [],
                                                          }),
                                                          removes: [
                                                            ...(prev[
                                                              ctrl.controlId
                                                            ]?.removes ?? []),
                                                            {
                                                              id: `r-${Date.now()}-${part.partId}`,
                                                              byId: part.partId,
                                                              name: part.name,
                                                            },
                                                          ],
                                                        },
                                                      }),
                                                    )
                                                  }
                                                  className="p-1 rounded text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                                >
                                                  <Trash2 className="size-3.5" />
                                                </button>
                                              </Tip>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {/* Staged additions */}
                                {dir.adds.length > 0 && (
                                  <div className="space-y-1">
                                    <p className="text-[10px] font-bold text-muted-foreground tracking-wider">
                                      Staged Additions
                                    </p>
                                    <div className="space-y-1">
                                      {dir.adds.map((add) => (
                                        <div
                                          key={add.id}
                                          className="flex items-start justify-between rounded border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50 dark:bg-emerald-500/10 px-3 py-2 gap-2"
                                        >
                                          <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                                                {add.name}
                                              </span>
                                              {add.title && (
                                                <span className="text-xs text-emerald-600/70 dark:text-emerald-400/70">
                                                  — {add.title}
                                                </span>
                                              )}
                                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 font-mono">
                                                {add.position}
                                                {add.byId ? ` ${add.byId}` : ""}
                                              </span>
                                            </div>
                                            {add.prose && (
                                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                                {add.prose}
                                              </p>
                                            )}
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() =>
                                              setAlterDirectives((prev) => ({
                                                ...prev,
                                                [ctrl.controlId]: {
                                                  ...(prev[ctrl.controlId] ?? {
                                                    adds: [],
                                                    removes: [],
                                                  }),
                                                  adds: (
                                                    prev[ctrl.controlId]
                                                      ?.adds ?? []
                                                  ).filter(
                                                    (a) => a.id !== add.id,
                                                  ),
                                                },
                                              }))
                                            }
                                            className="p-1 rounded text-muted-foreground hover:text-red-500 transition-colors shrink-0"
                                          >
                                            <X className="size-3.5" />
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Add part form */}
                                {isShowingForm ? (
                                  <div className="rounded border border-border p-3 space-y-3 bg-muted/20">
                                    <p className="text-xs font-semibold">
                                      New Part
                                    </p>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground ">
                                          Name <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                          type="text"
                                          placeholder="e.g. implementation"
                                          value={draft.name}
                                          onChange={(e) =>
                                            setAddPartDraft((prev) => ({
                                              ...prev,
                                              [ctrl.controlId]: {
                                                ...draft,
                                                name: e.target.value,
                                              },
                                            }))
                                          }
                                          className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-foreground transition-colors"
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground ">
                                          Title
                                        </label>
                                        <input
                                          type="text"
                                          placeholder="Optional title"
                                          value={draft.title}
                                          onChange={(e) =>
                                            setAddPartDraft((prev) => ({
                                              ...prev,
                                              [ctrl.controlId]: {
                                                ...draft,
                                                title: e.target.value,
                                              },
                                            }))
                                          }
                                          className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-foreground transition-colors"
                                        />
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="space-y-1">
                                        <label className="text-[10px] font-bold text-muted-foreground ">
                                          Position
                                        </label>
                                        <Dropdown
                                          size="sm"
                                          variant="form"
                                          value={draft.position}
                                          onChange={(v) =>
                                            setAddPartDraft((prev) => ({
                                              ...prev,
                                              [ctrl.controlId]: {
                                                ...draft,
                                                position: v,
                                                byId:
                                                  v === "before" || v === "after"
                                                    ? draft.byId
                                                    : "",
                                              },
                                            }))
                                          }
                                          options={[
                                            { value: "ending", label: "Ending" },
                                            { value: "starting", label: "Starting" },
                                            { value: "before", label: "Before" },
                                            { value: "after", label: "After" },
                                          ]}
                                        />
                                      </div>
                                      {(draft.position === "before" ||
                                        draft.position === "after") && (
                                          <div className="space-y-1">
                                            <label className="text-[10px] font-bold text-muted-foreground ">
                                              Reference Part
                                            </label>
                                            <Dropdown
                                              size="sm"
                                              variant="form"
                                              placeholder="Select part…"
                                              value={draft.byId}
                                              onChange={(v) =>
                                                setAddPartDraft((prev) => ({
                                                  ...prev,
                                                  [ctrl.controlId]: {
                                                    ...draft,
                                                    byId: v,
                                                  },
                                                }))
                                              }
                                              options={ctrl.parts.map((p) => ({
                                                value: p.partId,
                                                label: `${p.name} (${p.partId})`,
                                              }))}
                                            />
                                          </div>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                      <label className="text-[10px] font-bold text-muted-foreground ">
                                        Prose <span className="text-red-500">*</span>{" "}
                                        <span className="text-[10px] font-normal normal-case text-muted-foreground italic">
                                          (Use <code className="font-mono not-italic">{"{{ insert: param, param-id }}"}</code> to add a parameter reference)
                                        </span>
                                      </label>
                                      <textarea
                                        placeholder="Part content…"
                                        value={draft.prose}
                                        onChange={(e) =>
                                          setAddPartDraft((prev) => ({
                                            ...prev,
                                            [ctrl.controlId]: {
                                              ...draft,
                                              prose: e.target.value,
                                            },
                                          }))
                                        }
                                        rows={3}
                                        className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-foreground transition-colors resize-none"
                                      />
                                    </div>
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <label className="text-[10px] font-bold text-muted-foreground ">
                                          Parameters
                                        </label>
                                        <div className="relative group">
                                          <button
                                            type="button"
                                            disabled={!canAddDraftParam}
                                            onClick={() => {
                                              if (!canAddDraftParam) return;
                                              setAddPartDraft((prev) => ({
                                                ...prev,
                                                [ctrl.controlId]: {
                                                  ...draft,
                                                  params: [
                                                    ...(draft.params ?? []),
                                                    {
                                                      id: `p-${Date.now()}`,
                                                      paramId:
                                                        nextDraftParamId ?? "",
                                                      label: "",
                                                      clazz: "",
                                                      usage: "",
                                                      values: "",
                                                      selections: [],
                                                      constraints: [],
                                                      guidelines: [],
                                                    },
                                                  ],
                                                },
                                              }));
                                            }}
                                            className={`flex items-center gap-1 text-[10px] transition-colors ${canAddDraftParam ? "text-indigo-600 dark:text-indigo-400 hover:text-indigo-700" : "text-muted-foreground cursor-not-allowed opacity-50"}`}
                                          >
                                            <Plus className="size-3" /> Add
                                            Parameter
                                          </button>
                                          {!canAddDraftParam && (
                                            <div className="absolute right-0 top-5 z-10 hidden group-hover:block w-56 px-2.5 py-1.5 rounded-md bg-popover border border-border text-[11px] text-muted-foreground shadow-md">
                                              Add a{" "}
                                              <code className="font-mono">
                                                {"{{ insert: param, param-id }}"}
                                              </code>{" "}
                                              placeholder in the prose first.
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {(draft.params ?? []).map(
                                        (param, pIdx) => {
                                          const proseRefIds = new Set(
                                            extractParamIdsFromInlineProse(
                                              draft.prose,
                                            ),
                                          );
                                          const isRef =
                                            !param.paramId ||
                                            proseRefIds.has(
                                              param.paramId.trim(),
                                            );
                                          const upd = (
                                            fields: Partial<typeof param>,
                                          ) =>
                                            setAddPartDraft((prev) => ({
                                              ...prev,
                                              [ctrl.controlId]: {
                                                ...draft,
                                                params: (
                                                  draft.params ?? []
                                                ).map((p, i) =>
                                                  i === pIdx
                                                    ? { ...p, ...fields }
                                                    : p,
                                                ),
                                              },
                                            }));
                                          return (
                                            <div
                                              key={param.id}
                                              className={`rounded border p-3 space-y-3 ${!isRef ? "border-orange-500" : "border-border"}`}
                                            >
                                              {!isRef && (
                                                <p className="flex items-center gap-1.5 text-[10px] text-orange-500">
                                                  <AlertTriangle className="size-3 shrink-0" />
                                                  Parameter {param.paramId} is
                                                  not referenced in the
                                                  statement prose.
                                                </p>
                                              )}
                                              <div className="flex items-center justify-between">
                                                <span className="text-[10px] text-muted-foreground">
                                                  Param {pIdx + 1}
                                                </span>
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    setAddPartDraft((prev) => ({
                                                      ...prev,
                                                      [ctrl.controlId]: {
                                                        ...draft,
                                                        params: (
                                                          draft.params ?? []
                                                        ).filter(
                                                          (_, i) => i !== pIdx,
                                                        ),
                                                      },
                                                    }))
                                                  }
                                                  className="text-muted-foreground hover:text-red-500 transition-colors"
                                                >
                                                  <X className="size-3" />
                                                </button>
                                              </div>
                                              <div className="grid grid-cols-3 gap-2">
                                                <div className="space-y-1">
                                                  <label className="text-[10px] font-bold text-muted-foreground ">
                                                    Param ID
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={param.paramId}
                                                    onChange={(e) =>
                                                      upd({
                                                        paramId: e.target.value,
                                                      })
                                                    }
                                                    className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-indigo-500 transition-colors"
                                                  />
                                                </div>
                                                <div className="space-y-1">
                                                  <label className="text-[10px] font-bold text-muted-foreground ">
                                                    Label
                                                  </label>
                                                  <input
                                                    type="text"
                                                    value={param.label}
                                                    onChange={(e) =>
                                                      upd({
                                                        label: e.target.value,
                                                      })
                                                    }
                                                    className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-indigo-500 transition-colors"
                                                  />
                                                </div>
                                                <div className="space-y-1">
                                                  <label className="text-[10px] font-bold text-muted-foreground ">
                                                    Class
                                                  </label>
                                                  <Dropdown
                                                    size="sm"
                                                    variant="form"
                                                    placeholder="Select class"
                                                    showPlaceholderWhenEmpty
                                                    value={param.clazz}
                                                    onChange={(v) => upd({ clazz: v })}
                                                    options={[
                                                      { value: "", label: "Select class" },
                                                      { value: "sp800-53", label: "SP 800-53" },
                                                      { value: "sp800-53a", label: "SP 800-53A" },
                                                    ]}
                                                  />
                                                </div>
                                              </div>
                                              <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-muted-foreground ">
                                                  Usage
                                                </label>
                                                <textarea
                                                  placeholder="e.g. description of how this parameter is used"
                                                  value={param.usage}
                                                  onChange={(e) =>
                                                    upd({
                                                      usage: e.target.value,
                                                    })
                                                  }
                                                  rows={2}
                                                  className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-indigo-500 transition-colors resize-none"
                                                />
                                              </div>
                                              <div className="space-y-1">
                                                <label className="text-[10px] font-bold text-muted-foreground ">
                                                  Default Values
                                                </label>
                                                <input
                                                  type="text"
                                                  placeholder="e.g. 90, 180"
                                                  value={param.values}
                                                  onChange={(e) =>
                                                    upd({
                                                      values: e.target.value,
                                                    })
                                                  }
                                                  className="w-full text-xs bg-muted/50 rounded px-2 py-1.5 outline-none border border-border focus:border-indigo-500 transition-colors"
                                                />
                                              </div>
                                              <div className="border-t border-border pt-1 space-y-1">
                                                <div className="flex items-center justify-between">
                                                  <span className="text-[10px] text-muted-foreground">
                                                    Selection Choices
                                                  </span>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      upd({
                                                        selections: [
                                                          ...param.selections,
                                                          "",
                                                        ],
                                                      })
                                                    }
                                                    className="text-[10px] text-indigo-600 dark:text-indigo-400"
                                                  >
                                                    + Add Selection
                                                  </button>
                                                </div>
                                                {param.selections.map(
                                                  (sel, sIdx) => (
                                                    <div
                                                      key={sIdx}
                                                      className="flex items-center gap-2"
                                                    >
                                                      <input
                                                        type="text"
                                                        value={sel}
                                                        onChange={(e) =>
                                                          upd({
                                                            selections:
                                                              param.selections.map(
                                                                (s, i) =>
                                                                  i === sIdx
                                                                    ? e.target
                                                                      .value
                                                                    : s,
                                                              ),
                                                          })
                                                        }
                                                        className="flex-1 text-xs bg-muted/50 rounded px-2 py-1 outline-none border border-border focus:border-indigo-500 transition-colors"
                                                      />
                                                      <button
                                                        type="button"
                                                        onClick={() =>
                                                          upd({
                                                            selections:
                                                              param.selections.filter(
                                                                (_, i) =>
                                                                  i !== sIdx,
                                                              ),
                                                          })
                                                        }
                                                        className="text-muted-foreground hover:text-red-500"
                                                      >
                                                        <X className="size-3" />
                                                      </button>
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                              <div className="border-t border-border pt-1 space-y-1">
                                                <div className="flex items-center justify-between">
                                                  <span className="text-[10px] text-muted-foreground">
                                                    Constraints
                                                  </span>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      upd({
                                                        constraints: [
                                                          ...param.constraints,
                                                          "",
                                                        ],
                                                      })
                                                    }
                                                    className="text-[10px] text-indigo-600 dark:text-indigo-400"
                                                  >
                                                    + Add Constraint
                                                  </button>
                                                </div>
                                                {param.constraints.map(
                                                  (con, cIdx) => (
                                                    <div
                                                      key={cIdx}
                                                      className="flex items-center gap-2"
                                                    >
                                                      <input
                                                        type="text"
                                                        value={con}
                                                        onChange={(e) =>
                                                          upd({
                                                            constraints:
                                                              param.constraints.map(
                                                                (c, i) =>
                                                                  i === cIdx
                                                                    ? e.target
                                                                      .value
                                                                    : c,
                                                              ),
                                                          })
                                                        }
                                                        className="flex-1 text-xs bg-muted/50 rounded px-2 py-1 outline-none border border-border focus:border-indigo-500 transition-colors"
                                                      />
                                                      <button
                                                        type="button"
                                                        onClick={() =>
                                                          upd({
                                                            constraints:
                                                              param.constraints.filter(
                                                                (_, i) =>
                                                                  i !== cIdx,
                                                              ),
                                                          })
                                                        }
                                                        className="text-muted-foreground hover:text-red-500"
                                                      >
                                                        <X className="size-3" />
                                                      </button>
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                              <div className="border-t border-border pt-1 space-y-1">
                                                <div className="flex items-center justify-between">
                                                  <span className="text-[10px] text-muted-foreground">
                                                    Guidelines
                                                  </span>
                                                  <button
                                                    type="button"
                                                    onClick={() =>
                                                      upd({
                                                        guidelines: [
                                                          ...param.guidelines,
                                                          "",
                                                        ],
                                                      })
                                                    }
                                                    className="text-[10px] text-indigo-600 dark:text-indigo-400"
                                                  >
                                                    + Add Guideline
                                                  </button>
                                                </div>
                                                {param.guidelines.map(
                                                  (gui, gIdx) => (
                                                    <div
                                                      key={gIdx}
                                                      className="flex items-center gap-2"
                                                    >
                                                      <input
                                                        type="text"
                                                        value={gui}
                                                        onChange={(e) =>
                                                          upd({
                                                            guidelines:
                                                              param.guidelines.map(
                                                                (g, i) =>
                                                                  i === gIdx
                                                                    ? e.target
                                                                      .value
                                                                    : g,
                                                              ),
                                                          })
                                                        }
                                                        className="flex-1 text-xs bg-muted/50 rounded px-2 py-1 outline-none border border-border focus:border-indigo-500 transition-colors"
                                                      />
                                                      <button
                                                        type="button"
                                                        onClick={() =>
                                                          upd({
                                                            guidelines:
                                                              param.guidelines.filter(
                                                                (_, i) =>
                                                                  i !== gIdx,
                                                              ),
                                                          })
                                                        }
                                                        className="text-muted-foreground hover:text-red-500"
                                                      >
                                                        <X className="size-3" />
                                                      </button>
                                                    </div>
                                                  ),
                                                )}
                                              </div>
                                            </div>
                                          );
                                        },
                                      )}
                                    </div>
                                    <div className="flex items-center justify-end gap-2">
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setShowAddForm((prev) => ({
                                            ...prev,
                                            [ctrl.controlId]: false,
                                          }))
                                        }
                                        className="text-xs px-3 py-1.5 rounded border border-border text-muted-foreground hover:text-foreground transition-colors"
                                      >
                                        Cancel
                                      </button>
                                      <button
                                        type="button"
                                        disabled={
                                          !draft.name.trim() ||
                                          !draft.prose.trim()
                                        }
                                        onClick={() => {
                                          const newAdd: AlterAdd = {
                                            id: `a-${Date.now()}-${ctrl.controlId}`,
                                            position: draft.position,
                                            byId:
                                              draft.position === "before" ||
                                                draft.position === "after"
                                                ? draft.byId
                                                : "",
                                            name: draft.name.trim(),
                                            title: draft.title.trim(),
                                            prose: draft.prose.trim(),
                                            params: draft.params ?? [],
                                          };
                                          setAlterDirectives((prev) => ({
                                            ...prev,
                                            [ctrl.controlId]: {
                                              ...(prev[ctrl.controlId] ?? {
                                                adds: [],
                                                removes: [],
                                              }),
                                              adds: [
                                                ...(prev[ctrl.controlId]
                                                  ?.adds ?? []),
                                                newAdd,
                                              ],
                                            },
                                          }));
                                          setAddPartDraft((prev) => ({
                                            ...prev,
                                            [ctrl.controlId]: {
                                              position: "ending",
                                              byId: "",
                                              name: "",
                                              title: "",
                                              prose: "",
                                              params: [],
                                            },
                                          }));
                                          setShowAddForm((prev) => ({
                                            ...prev,
                                            [ctrl.controlId]: false,
                                          }));
                                        }}
                                        className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-40"
                                      >
                                        Add Part
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setShowAddForm((prev) => ({
                                        ...prev,
                                        [ctrl.controlId]: true,
                                      }))
                                    }
                                    className="flex items-center gap-1.5 text-xs text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                                  >
                                    <Plus className="size-3.5" />
                                    Add Part
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
            {builderStep === 5 &&
              (() => {
                const totalChecked = importRows.reduce(
                  (sum, r) => sum + countLeafChecked(r.controls, r.checkedIds),
                  0,
                );
                const setParameters = Object.entries(paramOverrides)
                  .filter(([, v]) => v.trim())
                  .map(([paramId, v]) => ({
                    "param-id": paramId,
                    values: [v.trim()],
                  }));
                const alteredControlCount = Object.values(
                  alterDirectives,
                ).filter(
                  (d) => d.adds.length > 0 || d.removes.length > 0,
                ).length;
                const totalAdds = Object.values(alterDirectives).reduce(
                  (s, d) => s + d.adds.length,
                  0,
                );
                const totalRemoves = Object.values(alterDirectives).reduce(
                  (s, d) => s + d.removes.length,
                  0,
                );
                return (
                  <div className="space-y-4">
                    <h3>Review & Save</h3>
                    {createdProfileId && (
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-xs text-emerald-700 dark:text-emerald-400">
                        <CheckCircle className="size-3.5" />
                        Profile created - ID:{" "}
                        <code className="font-mono">{createdProfileId}</code>
                      </div>
                    )}
                    <div className="rounded-md border border-border p-4 space-y-2 text-sm">
                      <div className="flex justify-between py-1 border-b border-border">
                        <span className="text-muted-foreground">Title</span>
                        <span className="font-medium">{profileTitle}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-border">
                        <span className="text-muted-foreground">
                          Import sources
                        </span>
                        <span className="font-mono">{importRows.length}</span>
                      </div>
                      {importRows.map((row, idx) => (
                        <div
                          key={row.key}
                          className="flex justify-between py-1 border-b border-border pl-4"
                        >
                          <span className="text-muted-foreground text-xs">
                            {row.sourceType === "CATALOG"
                              ? "Catalog"
                              : "Profile"}
                            : {row.sourceTitle ?? row.sourceId}
                          </span>
                          <span className="font-mono text-xs">
                            {countLeafChecked(row.controls, row.checkedIds)}{" "}
                            controls
                          </span>
                        </div>
                      ))}
                      <div className="flex justify-between py-1 border-b border-border">
                        <span className="text-muted-foreground">
                          Total controls
                        </span>
                        <span className="font-mono">{totalChecked}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-border">
                        <span className="text-muted-foreground">
                          Merge — Combine method
                        </span>
                        <span className="font-mono">{combineMethod}</span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-border">
                        <span className="text-muted-foreground">
                          Merge — Structuring directive
                        </span>
                        <span className="font-mono">
                          {structuringDirective}
                        </span>
                      </div>
                      <div className="flex justify-between py-1 border-b border-border">
                        <span className="text-muted-foreground">
                          Parameter overrides
                        </span>
                        <span className="font-mono">
                          {setParameters.length}
                        </span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-muted-foreground">
                          Alter directives
                        </span>
                        <span className="font-mono text-xs">
                          {alteredControlCount > 0
                            ? `${alteredControlCount} control${alteredControlCount !== 1 ? "s" : ""} (+${totalAdds} / -${totalRemoves})`
                            : "none"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400">
                      <Check className="size-4" />
                      Profile ready — {totalChecked} controls,{" "}
                      {setParameters.length} parameter overrides,{" "}
                      {alteredControlCount} altered control
                      {alteredControlCount !== 1 ? "s" : ""}
                    </div>
                  </div>
                );
              })()}
          </div>

          {/* Navigation */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {builderStep > 0 ? (
              <button
                onClick={() => setBuilderStep((s) => Math.max(0, s - 1))}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
              >
                Previous
              </button>
            ) : (
              <div />
            )}
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setView("list");
                  setBuilderStep(0);
                  setProfileTitle("");
                  setCreatedProfileId(null);
                  setParamOverrides({});
                  setImportRows([]);
                  setMergeAnalysis(null);
                  setMergeSaved(false);
                  setCombineMethod("keep");
                  setStructuringDirective("as-is");
                  setAvailableParams([]);
                }}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const allSourcesSelected =
                    importRows.length > 0 &&
                    importRows.every((r) => r.sourceId);
                  if (builderStep === 0 && allSourcesSelected) {
                    // Step 0 → 1: Fetch controls for all import rows
                    for (let i = 0; i < importRows.length; i++) {
                      const row = importRows[i];
                      if (row.sourceId && row.controls.length === 0) {
                        await loadImportRowControls(
                          row.sourceId,
                          row.sourceType,
                          i,
                        );
                      }
                    }
                    setActiveImportIdx(0);
                    setBuilderStep(1);
                  } else if (builderStep === 1) {
                    // Step 1 → 2: Create profile via POST /profiles, then fetch merge analysis
                    const totalChecked = importRows.reduce(
                      (sum, r) =>
                        sum + countLeafChecked(r.controls, r.checkedIds),
                      0,
                    );
                    if (totalChecked === 0 || savingRef.current) return;

                    let profileId = createdProfileId;
                    if (!profileId) {
                      savingRef.current = true;
                      setSaving(true);
                      try {
                        const imports = importRows.map((row, idx) => ({
                          href: `#${row.sourceId}`,
                          sourceType: row.sourceType,
                          includeControls: [
                            {
                              "with-ids": [...row.checkedIds].map((id) =>
                                id.toLowerCase(),
                              ),
                            },
                          ],
                          excludeControls: [] as { "with-ids": string[] }[],
                          sortOrder: idx,
                        }));
                        const res = await createProfile({
                          title: profileTitle,
                          owningOrgUnitId: importOrgId || undefined,
                          imports,
                        });
                        profileId = res.data.id;
                        setCreatedProfileId(profileId);
                      } catch (err: unknown) {
                        const msg =
                          err instanceof Error
                            ? err.message
                            : "Failed to create profile";
                        // apiFetch surfaces the backend error envelope as
                        // err.name (title, e.g. "Duplicate Title") and
                        // err.message (detail). Use them so the real reason is
                        // shown instead of a generic message.
                        const title =
                          err instanceof Error &&
                          err.name &&
                          err.name !== "Error"
                            ? err.name
                            : "Error";
                        if (
                          msg.includes("PRF-0012") ||
                          msg.includes("resolved")
                        ) {
                          useToastStore.getState().showError("Error", "This profile must be resolved before it can be used as a source.",
                          );
                        } else if (
                          msg.includes("set-parameter") ||
                          msg.includes("param")
                        ) {
                          useToastStore.getState().showError("Error", "One or more parameter overrides are invalid. Please check your values and try again.",
                          );
                        } else if (
                          msg.includes("match") ||
                          msg.includes("imported")
                        ) {
                          useToastStore.getState().showError("Error", "Some parameters do not exist in the imported catalog. Please review your control selection.",
                          );
                        } else {
                          // Show the actual backend message (e.g. duplicate title).
                          useToastStore.getState().showError(title, msg ||
                              "Something went wrong while creating the profile. Please try again.",
                          );
                        }
                        return;
                      } finally {
                        savingRef.current = false;
                        setSaving(false);
                      }
                    }

                    // Fetch merge analysis
                    setMergeAnalysisLoading(true);
                    setBuilderStep(2);
                    try {
                      const analysisRes =
                        await getProfileMergeAnalysis(profileId);
                      setMergeAnalysis(analysisRes.data);
                      // Default combineMethod to "keep" when no overlaps
                      if (analysisRes.data.totalOverlappingControls === 0) {
                        setCombineMethod("keep");
                      } else {
                        setCombineMethod("use-first");
                      }
                    } catch {
                      useToastStore.getState().showError("Error", "Failed to fetch merge analysis. You can still configure merge settings manually.",
                      );
                      setMergeAnalysis(null);
                    } finally {
                      setMergeAnalysisLoading(false);
                    }
                  } else if (builderStep === 2 && createdProfileId) {
                    // Step 2 → 3: Save merge config via PATCH, then load available controls for Alters
                    setSaving(true);
                    try {
                      await updateProfile(createdProfileId, {
                        merge: {
                          combineMethod,
                          structuringDirective,
                          custom:
                            structuringDirective === "custom"
                              ? {
                                "insert-controls": [
                                  { order: customOrder, "include-all": {} },
                                ],
                              }
                              : null,
                        },
                      });
                      setMergeSaved(true);
                      setBuilderStep(3);
                      // Load available controls and parameters for the Alters step
                      setAvailableControlsLoading(true);
                      setAvailableControls([]);
                      setAvailableParamsLoading(true);
                      setAvailableParams([]);
                      try {
                        const [ctrlsRes, paramsRes] = await Promise.all([
                          getProfileAvailableControls(createdProfileId),
                          getProfileAvailableParameters(createdProfileId),
                        ]);
                        // use-first: only show controls from the first import source
                        let ctrlsData = ctrlsRes.data ?? [];
                        if (
                          combineMethod === "use-first" &&
                          importRows.length > 1
                        ) {
                          const firstSourceIds = new Set(
                            [...importRows[0].checkedIds].map((id) =>
                              id.toLowerCase(),
                            ),
                          );
                          ctrlsData = ctrlsData.filter((c) =>
                            firstSourceIds.has(c.controlId.toLowerCase()),
                          );
                        }
                        setAvailableControls(ctrlsData);
                        setAvailableParams(supplementParamClazz(paramsRes.data ?? [], importRows));
                      } catch {
                        setAvailableControls([]);
                        setAvailableParams([]);
                      } finally {
                        setAvailableControlsLoading(false);
                        setAvailableParamsLoading(false);
                      }
                    } catch (err: unknown) {
                      const msg =
                        err instanceof Error
                          ? err.message
                          : "Failed to save merge configuration";
                      useToastStore.getState().showError("Error", msg);
                    } finally {
                      setSaving(false);
                    }
                  } else if (builderStep === 3 && createdProfileId) {
                    // Step 3 → 4: Draft-save alters via PATCH, then load available parameters
                    const altersDraft = Object.entries(alterDirectives)
                      .filter(
                        ([, dir]) =>
                          dir.adds.length > 0 || dir.removes.length > 0,
                      )
                      .map(([controlId, dir]) => ({
                        "control-id": controlId,
                        adds: dir.adds.map((a) => ({
                          ...(a.position ? { position: a.position } : {}),
                          ...(a.byId ? { "by-id": a.byId } : {}),
                          name: a.name,
                          ...(a.title ? { title: a.title } : {}),
                          prose: a.prose,
                          ...(a.params && a.params.length > 0
                            ? {
                              params: a.params
                                .filter((p) => p.paramId.trim())
                                .map((p) => ({
                                  "param-id": p.paramId.trim(),
                                  ...(p.label ? { label: p.label } : {}),
                                  ...(p.clazz ? { class: p.clazz } : {}),
                                  ...(p.usage ? { usage: p.usage } : {}),
                                  ...(p.values
                                    ? {
                                      values: p.values
                                        .split(",")
                                        .map((s) => s.trim())
                                        .filter(Boolean),
                                    }
                                    : {}),
                                  ...(p.selections.length > 0
                                    ? {
                                      select: {
                                        choice:
                                          p.selections.filter(Boolean),
                                      },
                                    }
                                    : {}),
                                  ...(p.constraints.length > 0
                                    ? {
                                      constraints: p.constraints
                                        .filter(Boolean)
                                        .map((c) => ({ description: c })),
                                    }
                                    : {}),
                                  ...(p.guidelines.length > 0
                                    ? {
                                      guidelines: p.guidelines
                                        .filter(Boolean)
                                        .map((g) => ({ prose: g })),
                                    }
                                    : {}),
                                })),
                            }
                            : {}),
                        })),
                        removes: dir.removes.map((r) => ({
                          ...(r.byId ? { "by-id": r.byId } : {}),
                          ...(r.name ? { name: r.name } : {}),
                        })),
                      }));
                    setSaving(true);
                    try {
                      await updateProfile(createdProfileId, {
                        modify: {
                          setParameters: [],
                          alters: altersDraft,
                        },
                      });
                    } catch (err: unknown) {
                      const msg =
                        err instanceof Error
                          ? err.message
                          : "Failed to save alter directives";
                      useToastStore.getState().showError("Error", msg);
                      return;
                    } finally {
                      setSaving(false);
                    }
                    setBuilderStep(4);
                    setAvailableParamsLoading(true);
                    setAvailableParams([]);
                    try {
                      const paramsRes =
                        await getProfileAvailableParameters(createdProfileId);
                      setAvailableParams(supplementParamClazz(paramsRes.data ?? [], importRows));
                    } catch {
                      setAvailableParams([]);
                    } finally {
                      setAvailableParamsLoading(false);
                    }
                  } else if (builderStep === 4 && createdProfileId) {
                    // Step 4 → 5: Save params + alters together via PATCH
                    const setParameters = Object.entries(paramOverrides)
                      .filter(([, v]) => v.trim())
                      .map(([paramId, v]) => ({
                        "param-id": paramId,
                        values: v
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      }));
                    const alters = Object.entries(alterDirectives)
                      .filter(
                        ([, dir]) =>
                          dir.adds.length > 0 || dir.removes.length > 0,
                      )
                      .map(([controlId, dir]) => ({
                        "control-id": controlId,
                        adds: dir.adds.map((a) => ({
                          ...(a.position ? { position: a.position } : {}),
                          ...(a.byId ? { "by-id": a.byId } : {}),
                          name: a.name,
                          ...(a.title ? { title: a.title } : {}),
                          prose: a.prose,
                          ...(a.params && a.params.length > 0
                            ? {
                              params: a.params
                                .filter((p) => p.paramId.trim())
                                .map((p) => ({
                                  "param-id": p.paramId.trim(),
                                  ...(p.label ? { label: p.label } : {}),
                                  ...(p.clazz ? { class: p.clazz } : {}),
                                  ...(p.usage ? { usage: p.usage } : {}),
                                  ...(p.values
                                    ? {
                                      values: p.values
                                        .split(",")
                                        .map((s) => s.trim())
                                        .filter(Boolean),
                                    }
                                    : {}),
                                  ...(p.selections.length > 0
                                    ? {
                                      select: {
                                        choice:
                                          p.selections.filter(Boolean),
                                      },
                                    }
                                    : {}),
                                  ...(p.constraints.length > 0
                                    ? {
                                      constraints: p.constraints
                                        .filter(Boolean)
                                        .map((c) => ({ description: c })),
                                    }
                                    : {}),
                                  ...(p.guidelines.length > 0
                                    ? {
                                      guidelines: p.guidelines
                                        .filter(Boolean)
                                        .map((g) => ({ prose: g })),
                                    }
                                    : {}),
                                })),
                            }
                            : {}),
                        })),
                        removes: dir.removes.map((r) => ({
                          ...(r.byId ? { "by-id": r.byId } : {}),
                          ...(r.name ? { name: r.name } : {}),
                        })),
                      }));
                    if (setParameters.length === 0 && alters.length === 0) {
                      setBuilderStep(5);
                      return;
                    }
                    setSaving(true);
                    try {
                      await updateProfile(createdProfileId, {
                        modify: {
                          setParameters,
                          alters,
                        },
                      });
                      setBuilderStep(5);
                    } catch (err: unknown) {
                      const msg =
                        err instanceof Error
                          ? err.message
                          : "Failed to save profile";
                      useToastStore.getState().showError("Error", msg.includes("set-parameter") || msg.includes("param")
                          ? "One or more parameter overrides are invalid. Please check your values and try again."
                          : msg.includes("match") || msg.includes("imported")
                            ? "Some parameters do not exist in the imported catalog. Please review your control selection."
                            : msg.includes("restrictive") ||
                              msg.includes("tighten") ||
                              msg.includes("exceed")
                              ? "A parameter override exceeds the catalog default. Profile overrides can only tighten (reduce) values, not increase them."
                              : "Something went wrong while saving the profile. Please try again.",
                      );
                    } finally {
                      setSaving(false);
                    }
                  } else if (builderStep === 5 && createdProfileId) {
                    // Step 5 → Done: Final PATCH with complete params + alters, then go to list
                    const finalSetParameters = Object.entries(paramOverrides)
                      .filter(([, v]) => v.trim())
                      .map(([paramId, v]) => ({
                        "param-id": paramId,
                        values: v
                          .split(",")
                          .map((s) => s.trim())
                          .filter(Boolean),
                      }));
                    const finalAlters = Object.entries(alterDirectives)
                      .filter(
                        ([, dir]) =>
                          dir.adds.length > 0 || dir.removes.length > 0,
                      )
                      .map(([controlId, dir]) => ({
                        "control-id": controlId,
                        adds: dir.adds.map((a) => ({
                          ...(a.position ? { position: a.position } : {}),
                          ...(a.byId ? { "by-id": a.byId } : {}),
                          name: a.name,
                          ...(a.title ? { title: a.title } : {}),
                          prose: a.prose,
                          ...(a.params && a.params.length > 0
                            ? {
                              params: a.params
                                .filter((p) => p.paramId.trim())
                                .map((p) => ({
                                  "param-id": p.paramId.trim(),
                                  ...(p.label ? { label: p.label } : {}),
                                  ...(p.clazz ? { class: p.clazz } : {}),
                                  ...(p.usage ? { usage: p.usage } : {}),
                                  ...(p.values
                                    ? {
                                      values: p.values
                                        .split(",")
                                        .map((s) => s.trim())
                                        .filter(Boolean),
                                    }
                                    : {}),
                                  ...(p.selections.length > 0
                                    ? {
                                      select: {
                                        choice:
                                          p.selections.filter(Boolean),
                                      },
                                    }
                                    : {}),
                                  ...(p.constraints.length > 0
                                    ? {
                                      constraints: p.constraints
                                        .filter(Boolean)
                                        .map((c) => ({ description: c })),
                                    }
                                    : {}),
                                  ...(p.guidelines.length > 0
                                    ? {
                                      guidelines: p.guidelines
                                        .filter(Boolean)
                                        .map((g) => ({ prose: g })),
                                    }
                                    : {}),
                                })),
                            }
                            : {}),
                        })),
                        removes: dir.removes.map((r) => ({
                          ...(r.byId ? { "by-id": r.byId } : {}),
                          ...(r.name ? { name: r.name } : {}),
                        })),
                      }));
                    setSaving(true);
                    try {
                      await updateProfile(createdProfileId, {
                        modify: {
                          setParameters: finalSetParameters,
                          alters: finalAlters,
                        },
                      });
                    } catch (err: unknown) {
                      const msg =
                        err instanceof Error
                          ? err.message
                          : "Failed to save profile";
                      useToastStore.getState().showError("Error", msg);
                      return;
                    } finally {
                      setSaving(false);
                    }
                    setView("list");
                    setBuilderStep(0);
                    setProfileTitle("");
                    setCreatedProfileId(null);
                    setParamOverrides({});
                    setAlterDirectives({});
                    setAvailableControls([]);
                    setShowAddForm({});
                    setAddPartDraft({});
                    setImportRows([]);
                    setMergeAnalysis(null);
                    setMergeSaved(false);
                    setCombineMethod("keep");
                    setStructuringDirective("as-is");
                    fetchProfiles();
                  }
                }}
                disabled={
                  saving ||
                  mergeAnalysisLoading ||
                  (builderStep === 0 &&
                    (!profileTitle.trim() ||
                      importRows.length === 0 ||
                      !importRows.every((r) => r.sourceId) ||
                      importRows.some(
                        (row, idx) =>
                          row.sourceId != null &&
                          importRows.some(
                            (other, otherIdx) =>
                              otherIdx !== idx &&
                              other.sourceType === row.sourceType &&
                              other.sourceId === row.sourceId,
                          ),
                      ))) ||
                  (builderStep === 1 &&
                    importRows.reduce(
                      (sum, r) => sum + r.checkedIds.size,
                      0,
                    ) === 0)
                }
                className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
              >
                {saving
                  ? "Saving…"
                  : builderStep === 5
                    ? "Save Profile"
                    : "Next"}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  /* ════════════════════════════════════════════════════════
     Skip list view if navigating via URL parameter
  ════════════════════════════════════════════════════════ */
  if (profileIdFromUrl && (detailLoading || !selectedProfileRow)) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-2 animate-pulse">
          <div className="h-3.5 w-16 rounded bg-muted" />
          <div className="h-3.5 w-3 rounded bg-muted" />
          <div className="h-3.5 w-32 rounded bg-muted" />
        </div>
        <SkeletonCard lines={4} />
        <SkeletonCard lines={3} />
      </div>
    );
  }

  /* ════════════════════════════════════════════════════════
     Profile List View
  ════════════════════════════════════════════════════════ */
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1>Profile</h1>
          <p className="text-sm text-muted-foreground mt-0.5 truncate">
            Create and manage OSCAL profiles
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <RBACAction module="profile" action="create">
            <button
              onClick={() => {
                resetImport();
                setShowImport(true);
                fetchAccessibleOrgs("profile", "create");
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-indigo-500 dark:border-indigo-400 text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-all hover:-translate-y-px whitespace-nowrap"
            >
              <Import className="size-4" /> Import Profile
            </button>
            <button
              onClick={() => {
                setShowImport(false);
                resetImport();
                setView("builder");
                setBuilderStep(0);
                setImportRows([
                  {
                    key: nextImportRowKey(),
                    sourceType: "CATALOG",
                    sourceId: null,
                    savedCatalogId: null,
                    savedProfileId: null,
                    controls: [],
                    rawControls: [],
                    checkedIds: new Set(),
                    loading: false,
                  },
                ]);
                setActiveImportIdx(0);
                setProfileTitle("");
                setCreatedProfileId(null);
                setParamOverrides({});
                setMergeAnalysis(null);
                setMergeSaved(false);
                setCombineMethod("keep");
                setStructuringDirective("as-is");
                fetchCatalogs();
                fetchProfiles();
                fetchAccessibleOrgs("profile", "create");
              }}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px whitespace-nowrap"
            >
              <Plus className="size-4" />
              New Profile
            </button>
          </RBACAction>
        </div>
      </div>

      {error ? (
        <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground">
          <span>Something went wrong.</span>
          <button
            onClick={() => {
              setError(null);
              fetchProfiles();
            }}
            className="text-xs underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      ) : (
        <>
          <EntityCardGrid
            items={paginatedProfiles}
            loading={loading}
            getKey={(row) => row.id}
            getStatus={(row) => row.lifecycleStatus}
            onOpen={(row) => {
              navigate(`/profiles/${row.id}`);
              setSelectedProfileRow(row);
            }}
            page={profPage}
            pageSize={profPageSize}
            totalItems={filteredProfiles.length}
            onPageChange={setProfPage}
            onPageSizeChange={(s) => {
              setProfPageSize(s);
              setProfPage(0);
            }}
            itemLabel="profiles"
            emptyIcon={<FileText className="size-8 text-[#cbd5e1] dark:text-white/30" />}
            emptyText="No profiles found"
            toolbarLeft={
              <>
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/50 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search profiles"
                    value={profSearch}
                    onChange={(e) => {
                      setProfSearch(e.target.value);
                      setProfPage(0);
                    }}
                    className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-muted-foreground/50"
                  />
                </div>
                <Dropdown
                  size="sm"
                  variant="pill"
                  value={profStatusFilter}
                  onChange={(v) => {
                    setProfStatusFilter(v);
                    setProfPage(0);
                  }}
                  options={[
                    { value: "", label: "All Status" },
                    { value: "draft", label: "Draft", dot: "bg-slate-400" },
                    { value: "in_review", label: "In Review", dot: "bg-blue-500" },
                    { value: "approved", label: "Approved", dot: "bg-emerald-500" },
                    { value: "published", label: "Published", dot: "bg-emerald-500" },
                    { value: "rejected", label: "Rejected", dot: "bg-red-500" },
                    { value: "superseded", label: "Superseded", dot: "bg-orange-500" },
                    { value: "withdrawn", label: "Withdrawn", dot: "bg-rose-700" },
                  ]}
                />
                <Dropdown
                  size="sm"
                  variant="pill"
                  value={profResolvedFilter}
                  onChange={(v) => {
                    setProfResolvedFilter(v);
                    setProfPage(0);
                  }}
                  options={[
                    { value: "", label: "All Resolved" },
                    { value: "yes", label: "Resolved", dot: "bg-emerald-500" },
                    { value: "no", label: "Not Resolved", dot: "bg-slate-400" },
                  ]}
                />
              </>
            }
            renderTitle={(row) => (
              <TitleTooltip text={row.name}>
                <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                  {row.name}
                </h3>
              </TitleTooltip>
            )}
            renderHeaderRight={() => (
              <Globe className="size-4 text-sky-600 dark:text-sky-400" />
            )}
            renderBadges={(row) => <VersionChip version={row.version} />}
            renderStats={(row) => (
              <StatTiles>
                <StatTile
                  icon={<FileText className="size-3.5" />}
                  accent="indigo"
                  value={row.oscalVersion || "—"}
                  label="OSCAL"
                  tooltip={row.oscalVersion}
                />
                <StatTile
                  icon={
                    row.resolved ? (
                      <CheckCircle className="size-3.5" />
                    ) : (
                      <XCircle className="size-3.5" />
                    )
                  }
                  accent="emerald"
                  value={row.resolved ? "Yes" : "No"}
                  label="Resolved"
                />
              </StatTiles>
            )}
            renderDates={(row) => <MetaDates created={row.created} updated={row.updated} />}
            renderFooterActions={(row) => (
              <CardActions
                deleteModule="profile"
                exportTooltip="Export profile"
                deleteTooltip="Delete profile"
                deleteDisabledTooltip="Only draft profiles can be deleted"
                canDelete={row.lifecycleStatus === "draft"}
                exporting={exportingId === row.documentId}
                deleting={deleting === row.id}
                onExport={() => {
                  setExportModalDocId(row.documentId);
                  setExportModalName(row.name);
                }}
                onDelete={() => handleDeleteProfile(row.id)}
              />
            )}
            renderTable={(rows) => (
              <DataTable<ProfileRow>
                data={rows}
                rowKey={(row) => row.id}
                onRowClick={(row) => {
                  navigate(`/profiles/${row.id}`);
                  setSelectedProfileRow(row);
                }}
                emptyText="No profiles found"
                sortKey={profSortKey}
                sortDir={profSortDir}
                onSort={handleProfSort}
                sortIcon="arrows"
                columns={[
                  {
                    key: "_sno",
                    header: "#",
                    align: "center",
                    render: (row) => (
                      <span className="font-medium text-sm text-muted-foreground whitespace-nowrap">
                        {(profOriginalIndexMap.get(row.id) ?? 0) + 1}
                      </span>
                    ),
                  },
                  {
                    key: "name",
                    header: "Title",
                    align: "left",
                    headerAlign: "center",
                    sortable: true,
                    ellipsis: 2,
                    tooltip: (row) => row.name,
                    render: (row) => (
                      <span className="font-medium text-foreground">{row.name}</span>
                    ),
                  },
                  {
                    key: "version",
                    header: "Version",
                    sortable: true,
                    render: (row) => <VersionChip version={row.version} />,
                  },
                  {
                    key: "oscalVersion",
                    header: "OSCAL",
                    sortable: true,
                    render: (row) => (
                      <span className="font-medium">{row.oscalVersion || "—"}</span>
                    ),
                  },
                  {
                    key: "resolved",
                    header: "Resolved",
                    sortable: true,
                    render: (row) =>
                      row.resolved ? (
                        <StatusBadge status="pass" label="Yes" dot={false} />
                      ) : (
                        <StatusBadge status="draft" label="No" dot={false} />
                      ),
                  },
                  {
                    key: "created",
                    header: "Created",
                    sortable: true,
                    render: (row) => (
                      <span className="font-medium text-[#475569] dark:text-white/70">
                        {row.created}
                      </span>
                    ),
                  },
                  {
                    key: "updated",
                    header: "Updated",
                    sortable: true,
                    render: (row) => (
                      <span className="font-medium text-[#475569] dark:text-white/70">
                        {row.updated}
                      </span>
                    ),
                  },
                  {
                    key: "lifecycleStatus",
                    header: "Status",
                    sortable: true,
                    render: (row) => (
                      <StatusBadge
                        status={row.lifecycleStatus as Parameters<typeof StatusBadge>[0]["status"]}
                      />
                    ),
                  },
                  {
                    key: "actions",
                    header: "Actions",
                    render: (row) => (
                      <div
                        className="flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <CardActions
                          deleteModule="profile"
                          exportTooltip="Export profile"
                          deleteTooltip="Delete profile"
                          deleteDisabledTooltip="Only draft profiles can be deleted"
                          canDelete={row.lifecycleStatus === "draft"}
                          exporting={exportingId === row.documentId}
                          deleting={deleting === row.id}
                          onExport={() => {
                            setExportModalDocId(row.documentId);
                            setExportModalName(row.name);
                          }}
                          onDelete={() => handleDeleteProfile(row.id)}
                        />
                      </div>
                    ),
                  },
                ]}
              />
            )}
          />
        </>
      )}

      {/* ── OSCAL Import Pipeline Modal ── */}
      <Modal
        open={showImport}
        onClose={() => {
          setShowImport(false);
          resetImport();
        }}
        title="Import Profile"
        size="md"
        footer={
          importStep === "select" ? (
            <div className="flex justify-end w-full gap-2">
              <button
                onClick={() => {
                  setShowImport(false);
                  resetImport();
                }}
                className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={runImport}
                disabled={!validateResult || validating}
                className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!validateResult || validating
                    ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-indigo-500/10"
                    : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20"
                  }`}
              >
                Import
              </button>
            </div>
          ) : importStep === "url" ? (
            <div className="flex justify-end w-full gap-2">
              <button
                onClick={() => {
                  setImportStep("select");
                  setImportUrl("");
                  setImportFile(null);
                  setValidateResult(null);
                  setImportError(null);
                  setValidating(false);
                }}
                className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={runImport}
                disabled={!validateResult || validating}
                className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!validateResult || validating
                    ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-indigo-500/10"
                    : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20"
                  }`}
              >
                Import
              </button>
            </div>
          ) : importStep === "findings" ? (
            <div className="flex items-center justify-end w-full gap-3">
              <button
                onClick={() => {
                  setShowImport(false);
                  resetImport();
                }}
                className="px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  resetImport();
                }}
                className="flex items-center gap-2 px-4 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
            </div>
          ) : null
        }
      >
        {/* Step 0: Select source */}
        {importStep === "select" && (
          <div className="py-6 flex flex-col items-center gap-6">
            <input
              id="profile-file-input"
              type="file"
              accept=".json,.yaml,.yml,.xml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setImportFile(file);
                  setValidateResult(null);
                  setImportError(null);
                  setValidating(true);
                  (async () => {
                    try {
                      const res = await validateImport(file);
                      if (res.data.documentType.toLowerCase() !== "profile") {
                        setImportError(
                          `Expected a profile document but got "${res.data.documentType}"`,
                        );
                      } else {
                        setValidateResult(res.data);
                      }
                    } catch (err: unknown) {
                      setImportErrorTitle(err instanceof Error && err.name !== "Error" ? err.name : "Import Failed");
                      setImportError(err instanceof Error ? err.message : "Validation failed");
                    } finally {
                      setValidating(false);
                    }
                  })();
                }
              }}
            />

            {/* Organization selector */}
            <div className="w-full">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Organization
              </p>
              {accessibleOrgsLoading ? (
                <div className="flex items-center gap-2 py-2">
                  <Loader2 className="size-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Loading</span>
                </div>
              ) : accessibleOrgs.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                  <Ban className="size-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-600 dark:text-red-400">No accessible organizations.</span>
                </div>
              ) : accessibleOrgs.length === 1 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                  <span className="font-medium">{accessibleOrgs[0].name}</span>
                  <span className="text-muted-foreground">({accessibleOrgs[0].shortName})</span>
                </div>
              ) : (
                <FormSelect
                  options={accessibleOrgs.map((o) => ({ value: o.id, label: `${o.name} (${o.shortName})` }))}
                  value={importOrgId}
                  onChange={setImportOrgId}
                  placeholder="Select organization"
                />
              )}
            </div>

            {/* Import mode selector */}
            <div className="w-full">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Import Type
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setImportMode("full")}
                  className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${importMode === "full"
                      ? "border-indigo-500 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400"
                      : "border-border hover:bg-accent text-muted-foreground"
                    }`}
                >
                  <span className="block font-semibold">Full</span>
                  <span className="block text-[11px] mt-0.5 font-normal opacity-70">
                    Upload full profile
                  </span>
                </button>
                <Tip label="Coming soon" className="flex-1 flex">
                  <button
                    disabled
                    className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium text-muted-foreground opacity-50 cursor-not-allowed"
                  >
                    <span className="block font-semibold">Delta</span>
                    <span className="block text-[11px] mt-0.5 font-normal opacity-70">
                      Coming soon
                    </span>
                  </button>
                </Tip>
              </div>
            </div>

            <p className="text-sm font-semibold text-foreground">
              Select a source to add from
            </p>
            <div className="flex items-center justify-center gap-8">
              <button
                onClick={() =>
                  document.getElementById("profile-file-input")?.click()
                }
                className="flex flex-col items-center gap-3 group"
              >
                <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                  <Upload className="size-8 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  Upload file
                </span>
              </button>
              <button
                onClick={() => setImportStep("url")}
                className="flex flex-col items-center gap-3 group"
              >
                <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                  <Link2 className="size-8 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  Source link
                </span>
              </button>
            </div>
            {importFile && (
              <div
                className={`rounded-lg border w-full ${validateResult
                    ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                    : importError
                      ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                      : "border-border bg-muted"
                  }`}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <FileText
                    className={`size-6 flex-shrink-0 ${validateResult
                        ? "text-emerald-500"
                        : importError
                          ? "text-red-500"
                          : "text-muted-foreground"
                      }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {importFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {(importFile.size / 1024).toFixed(1)} KB
                    </p>
                    {validating && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                        <Loader2 className="size-3 animate-spin" /> Validating…
                      </p>
                    )}
                    {!validating && validateResult && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                        <CheckCircle className="size-3" /> Validated -{" "}
                        {validateResult.documentType}, OSCAL{" "}
                        {validateResult.oscalVersion}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setImportFile(null);
                      setValidateResult(null);
                      setImportError(null);
                      setValidating(false);
                    }}
                    className="p-1 rounded hover:bg-accent flex-shrink-0"
                  >
                    <X className="size-4 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {importStep === "url" && (
          <div className="space-y-4">
            <FormField
              label="Document URL"
              required
              hint="URL must end in .json, .yaml, or .xml"
            >
              <div className="flex gap-2">
                <div className="flex-1">
                  <FormInput
                    placeholder="https://example.com/oscal-profile.json"
                    value={importUrl}
                    onChange={(v) => {
                      setImportUrl(v);
                      setValidateResult(null);
                      setImportError(null);
                    }}
                    disabled={validating}
                  />
                </div>
                <button
                  onClick={fetchUrl}
                  disabled={!importUrl.trim() || validating}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${!importUrl.trim() || validating
                      ? "bg-muted text-muted-foreground cursor-not-allowed"
                      : "bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700"
                    }`}
                >
                  {validating ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="size-3.5 animate-spin" /> Fetching…
                    </span>
                  ) : (
                    "Fetch"
                  )}
                </button>
              </div>
            </FormField>

            {(validating || validateResult || importError) && (
              <div
                className={`rounded-lg border w-full ${validateResult
                    ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                    : importError
                      ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                      : "border-border bg-muted"
                  }`}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <Link2
                    className={`size-6 flex-shrink-0 ${validateResult ? "text-emerald-500" : importError ? "text-red-500" : "text-muted-foreground"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {importUrl.split("/").pop() || importUrl}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {importUrl}
                    </p>
                    {validating && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                        <Loader2 className="size-3 animate-spin" /> Fetching &
                        validating…
                      </p>
                    )}
                    {!validating && validateResult && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                        <CheckCircle className="size-3" /> Validated -{" "}
                        {validateResult.documentType}, OSCAL{" "}
                        {validateResult.oscalVersion}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setValidateResult(null);
                      setImportError(null);
                      setValidating(false);
                    }}
                    className="p-1 rounded hover:bg-accent flex-shrink-0"
                  >
                    <X className="size-4 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {importStep === "importing" && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
            <p className="text-sm font-medium">Importing document…</p>
            <p className="text-xs text-muted-foreground text-center">
              Uploading and processing your OSCAL profile. This may take a
              moment for large files.
            </p>
          </div>
        )}
      </Modal>

      {/* ── Import Session Modal ── */}
      <Modal
        open={showSession}
        onClose={() => {
          setShowSession(false);
          setSessionData(null);
        }}
        title={
          sessionData
            ? sessionData.jobStatus.toLowerCase() === "committed"
              ? "Import Complete"
              : sessionData.jobStatus.toLowerCase() === "validated"
                ? "Commit Profile"
                : sessionData.jobStatus.toLowerCase() === "failed" ||
                  sessionData.jobStatus.toLowerCase() === "aborted"
                  ? "Import Failed"
                  : "Importing Profile"
            : "Import Profile"
        }
        size="md"
        closable={false}
        footer={
          sessionData ? (
            <div className="flex justify-end w-full gap-2">
              {sessionData.jobStatus.toLowerCase() === "committed" ? (
                <button
                  onClick={() => {
                    setShowSession(false);
                    setSessionData(null);
                    resetImport();
                    fetchProfiles();
                  }}
                  className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md shadow-indigo-500/20 dark:shadow-indigo-500/20"
                >
                  Done
                </button>
              ) : sessionData.jobStatus.toLowerCase() === "failed" ||
                sessionData.jobStatus.toLowerCase() === "aborted" ? (
                <button
                  onClick={() => {
                    setShowSession(false);
                    setSessionData(null);
                    resetImport();
                  }}
                  className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={handleAbortSession}
                    disabled={aborting || committing}
                    className="px-4 py-2 rounded-lg border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40"
                  >
                    {aborting ? "Cancelling…" : "Cancel"}
                  </button>
                  <button
                    onClick={handleCommitSession}
                    disabled={
                      committing ||
                      aborting ||
                      sessionData.jobStatus.toLowerCase() !== "validated"
                    }
                    className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${committing ||
                        aborting ||
                        sessionData.jobStatus.toLowerCase() !== "validated"
                        ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed shadow-indigo-500/10 dark:shadow-indigo-500/10"
                        : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700 shadow-indigo-500/20 dark:shadow-indigo-500/20"
                      }`}
                  >
                    {committing ? "Committing…" : "Commit"}
                  </button>
                </>
              )}
            </div>
          ) : null
        }
      >
        {sessionLoading && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
            <p className="text-sm font-medium">Loading session…</p>
          </div>
        )}

        {sessionError && (
          <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400 mb-4">
            <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs">{sessionError}</p>
          </div>
        )}

        {sessionData && (
          <div className="space-y-5">
            {committing && (
              <div className="py-6 flex flex-col items-center gap-3">
                <Loader2 className="size-8 animate-spin text-indigo-500 dark:text-indigo-500" />
                <p className="text-sm font-medium">Committing import…</p>
                <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-indigo-500 dark:bg-indigo-500 animate-pulse"
                    style={{ width: "70%" }}
                  />
                </div>
              </div>
            )}

            {!committing && (
              <>
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="text-xs text-muted-foreground">
                      Status
                    </span>
                    <span
                      className={`text-sm font-semibold capitalize ${sessionData.jobStatus.toLowerCase() === "committed"
                          ? "text-emerald-600 dark:text-emerald-400"
                          : sessionData.jobStatus.toLowerCase() === "failed" ||
                            sessionData.jobStatus.toLowerCase() === "aborted"
                            ? "text-red-600 dark:text-red-400"
                            : sessionData.jobStatus.toLowerCase() ===
                              "validated"
                              ? "text-indigo-600 dark:text-indigo-400"
                              : "text-amber-600 dark:text-amber-400"
                        }`}
                    >
                      {sessionData.jobStatus}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="text-xs text-muted-foreground">
                      Doc Type
                    </span>
                    <span className="text-sm font-medium capitalize">
                      {sessionData.documentType}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-xs text-muted-foreground">Name</span>
                    <span className="text-sm font-medium">
                      {sessionData.sourceFilename}
                    </span>
                  </div>
                </div>

                {sessionData.findings.filter((f) => f.severity !== "error")
                  .length > 0 && (
                    <div className="rounded-lg border border-border p-4">
                      <h4 className="text-xs font-bold text-muted-foreground mb-2">
                        Findings (
                        {
                          sessionData.findings.filter(
                            (f) => f.severity !== "error",
                          ).length
                        }
                        )
                      </h4>
                      <div className="space-y-2">
                        {sessionData.findings
                          .filter((f) => f.severity !== "error")
                          .map((f) => (
                            <div
                              key={f.id}
                              className="flex items-start gap-2 text-xs"
                            >
                              <span
                                className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${f.severity === "info"
                                    ? "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400"
                                    : f.severity === "warning"
                                      ? "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
                                      : "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
                                  }`}
                              >
                                {f.severity}
                              </span>
                              <div className="flex-1">
                                <p className="font-medium">{f.title}</p>
                                <p className="text-muted-foreground">
                                  {f.detail}
                                </p>
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* ── Delete Confirmation Modal ── */}
      <DeleteConfirmDialog
        open={deleteModalOpen}
        type="Profile"
        name={profiles.find((p) => p.id === deleteTargetId)?.name || ""}
        deleting={!!deleting}
        onCancel={() => {
          setDeleteModalOpen(false);
          setDeleteTargetId(null);
        }}
        onConfirm={confirmDeleteProfile}
      />

      {/* ── Export Pulse Overlay ── */}
      {exportingId && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 bg-white dark:bg-zinc-900 rounded-2xl px-10 py-8 shadow-2xl">
            <div className="relative flex items-center justify-center">
              <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-indigo-500/20 animate-ping" />
              <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-indigo-500/30 animate-pulse" />
              <Upload className="relative size-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <p className="text-sm font-semibold text-foreground mt-2">
              Exporting as {exportFormat.toUpperCase()}
            </p>
            <p className="text-xs text-muted-foreground">
              Please wait while we prepare your file
            </p>
          </div>
        </div>
      )}

      {/* ── Export Format Modal ── */}
      <Modal
        open={!!exportModalDocId}
        onClose={() => setExportModalDocId(null)}
        title="Export Profile"
        description={`Choose a file format to export "${exportModalName}".`}
        size="sm"
      >
        <div className="grid grid-cols-3 gap-3">
          {(["json", "xml", "yaml"] as const).map((fmt) => (
            <button
              key={fmt}
              onClick={() => {
                if (exportModalDocId)
                  handleExportProfile(exportModalDocId, fmt);
              }}
              disabled={exportingId === exportModalDocId}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-indigo-400 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors group disabled:opacity-40"
            >
              {exportingId === exportModalDocId ? (
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              ) : (
                <Upload className="size-6 text-muted-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
              )}
              <span className="text-sm font-semibold group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                {fmt.toUpperCase()}
              </span>
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
}
